/**
 * errors.ts — словарь отказов сервера на обоих языках (WEB-1874, этап 2).
 *
 * ЧТО ЗДЕСЬ ЧИНИТСЯ (риск №2 разведки). До этой карточки `localizeError`
 * работал В ОДНУ СТОРОНУ: 174 правила «английский текст сервера → русская
 * фраза», плюс безусловный русский запасной текст. То есть человек с
 * английским кабинетом получал бы английские экраны и РУССКИЕ тосты об
 * ошибках — механизм работал против самой цели карточки. Поэтому каждая
 * фраза здесь заведена парой: `defineDictionary` не даст завести русскую без
 * английской и наоборот (тип второй половины выведен из первой).
 *
 * КАК УСТРОЕНО. Три части, и они РАЗНЫЕ:
 *  1. `errorsStrings` — сами фразы, по ключам-смыслам;
 *  2. `ERROR_CODE_KEYS` — машинный код отказа (`error.code`) → ключ фразы.
 *     Это правильная форма: код стабилен, а формулировка сервера меняется;
 *  3. `ERROR_PATTERNS` — регулярка по английскому тексту сервера → ключ
 *     фразы. Это наследство: у 44 из 47 кодов сервера кода в ответе пока нет,
 *     и текст — единственная зацепка. ПОРЯДОК ЗДЕСЬ ЗНАЧИМ: выигрывает первое
 *     совпадение, и несколько правил стоят раньше более общих намеренно
 *     (см. пометки). Этап 4 (сервер) переносит записи из части 3 в часть 2 —
 *     ключи фраз при этом не меняются, поэтому переезд не ломает переводы.
 *
 * ЧЕЙ ЭТОТ ФАЙЛ. После агента каркаса правит только агент серверных ошибок
 * (этап 4): он добавляет коды в `ERROR_CODE_KEYS` и пары фраз сюда же.
 */
import { MAX_INVITE_RECIPIENTS_PER_REQUEST } from '@/constants/inviteLimits';
import { defineDictionary, pickStrings } from '../define';
import { getUiLocale, type UiLocale } from '../locale';

export const errorsStrings = defineDictionary(
  {
    // ── Запасные фразы ──────────────────────────────────────────────────────
    somethingWentWrong: 'Something went wrong. Please try again.',

    // ── Перехватчик ответов (api/client.ts) ─────────────────────────────────
    // Отказ виден человеку всплывающим уведомлением ВНЕ React, поэтому фраза
    // берётся через pickStrings, а не хуком.
    sessionExpired: 'Your session has expired — sign in again',
    // WEB-2613: обновление пропуска не дождалось ответа (или сеть оборвалась) —
    // человек остаётся в кабинете, запрос можно повторить.
    sessionRefreshUnavailable:
      'Could not renew your sign-in: the server did not respond. You are still signed in — try again in a few seconds.',
    verifyEmailToContinue: 'Confirm your email to continue',
    // WEB-2590: запрос не дождался ответа за предел ожидания клиента.
    requestTimedOut: 'The server did not respond in time. Please try again.',
    // WEB-2590: денежное или необратимое действие — сервер мог его довести.
    requestTimedOutOutcomeUnknown:
      'The server did not answer in time, so the result is unknown. Before repeating, reload the page and check whether the action went through.',

    // ── Частота запросов, почта, пароль ─────────────────────────────────────
    tooManyRequests: 'Too many requests. Please try again later.',
    emailDeliveryFailed: 'Could not send the email. Please try again later.',
    weakPassword:
      'Password: at least 8 characters, an upper- and a lower-case Latin letter, a digit and a special character',
    invalidCredentials: 'Wrong email or password',
    emailAlreadyRegistered: 'This email is already registered',

    // ── Домены ──────────────────────────────────────────────────────────────
    domainAlreadyRegistered: 'This domain is already connected in the system',
    invalidDomainName: 'Invalid domain name',
    domainNotFoundOrNotEligible: 'Domain not found, or it cannot be renewed',
    domainNotFound: 'Domain not found',
    customDomainCannotRenew:
      'We do not renew custom domains — renew it with your own registrar',
    renewalPriceUnavailable:
      'The renewal price is temporarily unavailable. Try again later.',
    onlyOwnerOrAdminRemovesDomain:
      'Only an organization owner or admin can remove a domain',
    domainBelongsToAnotherProject: 'The domain belongs to another project',
    parentDomainNotActive: 'The parent domain is not active yet',
    customDomainNoAutoRenewal: 'Custom domains do not support auto-renewal',
    autoRenewalToggleFailed: 'Could not change auto-renewal',
    invalidSubdomainSlug: 'Invalid subdomain name',

    // ── Двухфакторная защита и коды подтверждения ───────────────────────────
    twoFactorAlreadyEnabled: 'Two-factor authentication is already on',
    twoFactorEnrollmentNotStarted:
      'Two-factor setup has not been started. Start over.',
    invalidTwoFactorCode: 'Wrong two-factor code',
    codeExpired: 'The code has expired. Request a new one.',
    tooManyAttempts: 'Too many attempts. Request a new code.',
    codeOrRecoveryCodeRequired: 'Enter the 2FA code or a recovery code',
    twoFactorNotEnabled: 'Two-factor authentication is not on',
    sendCodeNotApplicable:
      'Resending the code is not available for this method',
    resendCooldown: 'The code has already been sent. Wait before resending.',
    passwordRequired: 'Enter your password to confirm',
    invalidPassword: 'Wrong password',
    googleReauthRequired: 'Confirm your Google sign-in to continue',
    googleReauthMismatch: 'Could not confirm this is your Google account',
    invalidRecoveryCode: 'Wrong recovery code',
    invalidOrExpiredCode: 'Wrong or expired code',
    currentPasswordIncorrect: 'The current password is wrong',
    googleEmailNotVerified: 'The email of this Google account is not verified',

    // ── Объекты, которых нет ────────────────────────────────────────────────
    memberNotFound: 'Member not found',
    paywallNotFound: 'Paywall not found',
    quizNotFound: 'Quiz not found',
    funnelNotFound: 'Funnel not found',
    projectNotFound: 'Project not found',
    organizationNotFound: 'Organization not found',
    accountNotFound: 'Account not found',

    // ── Содержимое воронки не принято сервером ──────────────────────────────
    funnelContentListTooLong: (m: RegExpExecArray) =>
      `Could not save: in the "${m[1]}" block on screen "${m[2]}" the list "${m[3]}" is too long — ${m[4]} items, and no more than ${m[5]} are allowed. Remove the extra ones and try again.`,
    funnelContentBlockRejected: (m: RegExpExecArray) =>
      `Could not save: the server rejected the "${m[1]}" block on screen "${m[2]}". Check its settings or delete the block — while it is in the funnel, the rest of your edits do not save either.`,
    funnelContentScreenRejected: (m: RegExpExecArray) =>
      `Could not save: the server rejected the contents of screen "${m[1]}". Check the blocks on that screen.`,
    funnelContentRejected:
      'Could not save: the server rejected the funnel contents. Check the blocks you added last.',
    funnelContentCorrupted:
      'Could not save: the funnel contents are damaged. Reload the page and try again.',
    // WEB-2408 п.11: адрес постбэка с опечаткой в подстановке. Имя опечатки
    // берём из текста отказа сервера (код POSTBACK_URL_UNKNOWN_MACRO).
    postbackUnknownMacro: (m: RegExpExecArray) =>
      `The postback URL has an unknown macro ${m[1]}. Only {click_id}, {payout}, {status} and {txid} are allowed.`,

    // ── Правила логики ──────────────────────────────────────────────────────
    logicRulesInvalidAt: (m: RegExpExecArray) =>
      `Could not save: ${describeLogicRulePath(m[1], 'en')}.`,
    logicRulesInvalid:
      'Could not save: one of the logic rules is not filled in. Open the "Logic" panel and check the rules.',
    logicRuleAt: (number: number) => `in rule ${number}`,
    logicRuleAny: 'in a logic rule',
    logicRuleMissingData: (where: string) => `${where} some data is missing`,
    logicRuleField: (where: string, field: string) => `${where}: ${field}`,
    logicRuleUnknownField: (where: string, path: string) =>
      `${where}: "${path}" is empty`,
    logicFieldActionTarget: 'the "Target" field is empty',
    logicFieldAction: 'the action is set incorrectly',
    logicFieldConditionGroup: 'the conditions are set incorrectly',
    logicFieldTriggerScope: 'the "When it fires" field is empty',
    logicFieldScopeRef: 'the "What it applies to" field is empty',
    logicFieldId: 'the rule has no identifier',

    // ── Деньги и тарифы ─────────────────────────────────────────────────────
    billingAccessRequired:
      'Billing data is not available to you — the organization owner grants that access',
    resourceReadonly:
      'The funnel is read-only: the plan was downgraded. Renew the subscription or delete the extra funnels',
    insufficientBalance: 'Not enough money on the balance. Top it up.',
    topupRequiresPro:
      'Top-up is not available on the free plan — switch to Pro',
    bridgeRequiresPro: 'Configuring the app bridge requires the Pro plan',
    publishRequiresPaidPlan:
      'Publishing is not available on the free plan — switch to Pro to run traffic',

    // ── Публикация и копирование ────────────────────────────────────────────
    onlyPublishedPaywallCopy:
      'Only a published paywall can be copied to another project. Publish it and try again.',
    portalPaywallNotPublished:
      'The selected portal paywall is not published or was deleted — publish it, pick another one, or clear the field',

    // ── Доступ, роли, участники ─────────────────────────────────────────────
    accessDenied: 'Access denied',
    insufficientPermissions: 'Not enough rights for this action',
    authenticationRequired: 'You need to sign in',
    alreadyProjectMember: 'You are already a member of this project',
    notProjectMember: 'You are not a member of this project',
    userNotOrgMember: 'This user is not a member of this organization',
    notOrgMember: 'You are not a member of this organization',
    noUserWithEmail: 'No user with that email',
    ownerCannotLeaveOrg:
      'The owner cannot leave the organization — transfer ownership first',
    cannotDemoteOwner: 'The owner’s role cannot be lowered',
    cannotRemoveOwner: 'The owner cannot be removed',
    cannotRemoveYourself: 'You cannot remove yourself',
    onlyCurrentOwnerAction: 'Only the current owner can do this',
    inviteRevoked: 'This invite has been revoked',
    inviteMaxUsesReached: 'This invite has run out of uses',
    inviteEmailMismatch: 'This invite was issued for a different email address',
    cannotInviteAsOwner:
      'You cannot invite someone straight as an owner — ownership is transferred separately',
    adminCannotInviteAdmin: 'An admin cannot invite another admin',
    tooManyInviteRecipients: `No more than ${MAX_INVITE_RECIPIENTS_PER_REQUEST} addresses at a time — split the list`,
    onlyAdminOrOwnerInvites: 'Only a project admin or owner can invite',
    orgOwnerOrAdminOnly: 'Only an organization owner or admin can do this',
    orgOwnerOnly: 'Only the organization owner can do this',
    projectOwnerOrAdminOnly: 'Only a project owner or admin can do this',
    onlyOrgOwnerTransfers:
      'Only the current organization owner can transfer ownership',
    targetMustBeOrgMember:
      'This user has to become a member of the organization first',
    alreadyOrgOwner: 'This user is already the organization owner',
    ownerRoleImmutable: 'The owner role cannot be changed',

    // ── Оплата и сеть ───────────────────────────────────────────────────────
    stripeNotConnected: 'Stripe is not connected to this project',
    stripeCheckoutStartFailed: 'Could not start the payment. Please try again.',
    networkError: 'Network error. Check the connection and try again.',
    serverError: 'Server error. Please try again later.',
    serviceUnavailable:
      'The service is temporarily unavailable. Try again later.',
  },
  {
    somethingWentWrong: 'Что-то пошло не так. Попробуйте ещё раз.',

    sessionExpired: 'Сессия истекла — войдите снова',
    sessionRefreshUnavailable:
      'Не удалось продлить вход: сервер не ответил. Вы остаётесь в кабинете — повторите действие через несколько секунд.',
    verifyEmailToContinue: 'Подтвердите email, чтобы продолжить',
    requestTimedOut: 'Сервер не ответил вовремя. Попробуйте ещё раз.',
    requestTimedOutOutcomeUnknown:
      'Сервер не ответил вовремя, результат неизвестен. Прежде чем повторять, обновите страницу и проверьте, выполнено ли действие.',

    tooManyRequests: 'Слишком много запросов. Попробуйте позже.',
    emailDeliveryFailed: 'Не удалось отправить письмо. Попробуйте позже.',
    weakPassword:
      'Пароль: минимум 8 символов, заглавная и строчная латинские буквы, цифра и спецсимвол',
    invalidCredentials: 'Неверный email или пароль',
    emailAlreadyRegistered: 'Этот email уже зарегистрирован',

    domainAlreadyRegistered: 'Этот домен уже подключён в системе',
    invalidDomainName: 'Недопустимое имя домена',
    domainNotFoundOrNotEligible: 'Домен не найден или его нельзя продлить',
    domainNotFound: 'Домен не найден',
    customDomainCannotRenew:
      'Свои домены у нас не продлеваются — продлите домен у своего регистратора',
    renewalPriceUnavailable:
      'Цена продления временно недоступна. Попробуйте позже.',
    onlyOwnerOrAdminRemovesDomain:
      'Удалить домен может только владелец или администратор организации',
    domainBelongsToAnotherProject: 'Домен принадлежит другому проекту',
    parentDomainNotActive: 'Основной домен ещё не активен',
    customDomainNoAutoRenewal: 'Свои домены не поддерживают автопродление',
    autoRenewalToggleFailed: 'Не удалось изменить автопродление',
    invalidSubdomainSlug: 'Недопустимое имя поддомена',

    twoFactorAlreadyEnabled: 'Двухфакторная аутентификация уже включена',
    twoFactorEnrollmentNotStarted: 'Настройка 2FA не начата. Начните заново.',
    invalidTwoFactorCode: 'Неверный код двухфакторной аутентификации',
    codeExpired: 'Код истёк. Запросите новый.',
    tooManyAttempts: 'Слишком много попыток. Запросите новый код.',
    codeOrRecoveryCodeRequired: 'Введите код 2FA или резервный код',
    twoFactorNotEnabled: 'Двухфакторная аутентификация не включена',
    sendCodeNotApplicable:
      'Повторная отправка кода недоступна для этого метода',
    resendCooldown: 'Код уже отправлен. Подождите перед повторной отправкой.',
    passwordRequired: 'Введите пароль для подтверждения',
    invalidPassword: 'Неверный пароль',
    googleReauthRequired: 'Подтвердите вход через Google, чтобы продолжить',
    googleReauthMismatch: 'Не удалось подтвердить, что это ваш Google-аккаунт',
    invalidRecoveryCode: 'Неверный резервный код',
    invalidOrExpiredCode: 'Неверный или устаревший код',
    currentPasswordIncorrect: 'Текущий пароль указан неверно',
    googleEmailNotVerified: 'Почта аккаунта Google не подтверждена',

    memberNotFound: 'Участник не найден',
    paywallNotFound: 'Пейволл не найден',
    quizNotFound: 'Квиз не найден',
    funnelNotFound: 'Воронка не найдена',
    projectNotFound: 'Проект не найден',
    organizationNotFound: 'Организация не найдена',
    accountNotFound: 'Аккаунт не найден',

    funnelContentListTooLong: (m: RegExpExecArray) =>
      `Не удалось сохранить: в блоке «${m[1]}» на экране «${m[2]}» слишком длинный список «${m[3]}» — ${m[4]} шт., а можно не больше ${m[5]}. Уберите лишнее и попробуйте снова.`,
    funnelContentBlockRejected: (m: RegExpExecArray) =>
      `Не удалось сохранить: сервер не принял блок «${m[1]}» на экране «${m[2]}». Проверьте его настройки или удалите блок — пока он в воронке, не сохраняются и остальные правки.`,
    funnelContentScreenRejected: (m: RegExpExecArray) =>
      `Не удалось сохранить: сервер не принял содержимое экрана «${m[1]}». Проверьте блоки этого экрана.`,
    funnelContentRejected:
      'Не удалось сохранить: сервер не принял содержимое воронки. Проверьте блоки, добавленные последними.',
    funnelContentCorrupted:
      'Не удалось сохранить: содержимое воронки повреждено. Обновите страницу и попробуйте снова.',
    postbackUnknownMacro: (m: RegExpExecArray) =>
      `В адресе постбэка неизвестная подстановка ${m[1]}. Допустимы только {click_id}, {payout}, {status} и {txid}.`,

    logicRulesInvalidAt: (m: RegExpExecArray) =>
      `Не удалось сохранить: ${describeLogicRulePath(m[1], 'ru')}.`,
    logicRulesInvalid:
      'Не удалось сохранить: одно из правил логики заполнено не до конца. Откройте панель «Логика» и проверьте правила.',
    logicRuleAt: (number: number) => `в правиле ${number}`,
    logicRuleAny: 'в правиле логики',
    logicRuleMissingData: (where: string) => `${where} не хватает данных`,
    logicRuleField: (where: string, field: string) => `${where} ${field}`,
    logicRuleUnknownField: (where: string, path: string) =>
      `${where} не заполнено «${path}»`,
    logicFieldActionTarget: 'не заполнено поле «Цель»',
    logicFieldAction: 'неверно задано действие',
    logicFieldConditionGroup: 'неверно заданы условия',
    logicFieldTriggerScope: 'не заполнено поле «Когда срабатывает»',
    logicFieldScopeRef: 'не заполнено поле «К чему привязано»',
    logicFieldId: 'нет идентификатора правила',

    billingAccessRequired:
      'Платёжные данные вам недоступны — доступ к ним выдаёт владелец организации',
    resourceReadonly:
      'Воронка доступна только для чтения: тариф понижен. Продлите подписку или удалите лишние воронки',
    insufficientBalance: 'Недостаточно средств на балансе. Пополните счёт.',
    topupRequiresPro:
      'Пополнение баланса недоступно на бесплатном тарифе — перейдите на Pro',
    bridgeRequiresPro: 'Настройка моста в приложение доступна на тарифе Pro',
    publishRequiresPaidPlan:
      'Публикация недоступна на бесплатном тарифе — перейдите на Pro, чтобы запускать трафик',

    onlyPublishedPaywallCopy:
      'Скопировать в другой проект можно только опубликованный пейволл. Опубликуйте его и повторите.',
    portalPaywallNotPublished:
      'Выбранный пейволл-портал не опубликован или удалён — опубликуйте его, выберите другой или очистите поле',

    accessDenied: 'Доступ запрещён',
    insufficientPermissions: 'Недостаточно прав для этого действия',
    authenticationRequired: 'Требуется вход в аккаунт',
    alreadyProjectMember: 'Вы уже участник этого проекта',
    notProjectMember: 'Вы не участник этого проекта',
    userNotOrgMember: 'Пользователь не участник этой организации',
    notOrgMember: 'Вы не участник этой организации',
    noUserWithEmail: 'Пользователь с таким email не найден',
    ownerCannotLeaveOrg:
      'Владелец не может покинуть организацию — сначала передайте владение',
    cannotDemoteOwner: 'Нельзя понизить роль владельца',
    cannotRemoveOwner: 'Нельзя удалить владельца',
    cannotRemoveYourself: 'Нельзя удалить самого себя',
    onlyCurrentOwnerAction:
      'Это действие может выполнить только текущий владелец',
    inviteRevoked: 'Это приглашение отозвано',
    inviteMaxUsesReached: 'Приглашение исчерпало лимит использований',
    inviteEmailMismatch: 'Приглашение выписано на другой адрес почты',
    cannotInviteAsOwner:
      'Пригласить сразу владельцем нельзя — владение передаётся отдельно',
    adminCannotInviteAdmin:
      'Администратор не может пригласить другого администратора',
    tooManyInviteRecipients: `Не больше ${MAX_INVITE_RECIPIENTS_PER_REQUEST} адресов за один раз — разбейте список на части`,
    onlyAdminOrOwnerInvites:
      'Приглашать может только администратор или владелец проекта',
    orgOwnerOrAdminOnly:
      'Это действие может выполнить только владелец или администратор организации',
    orgOwnerOnly: 'Это действие может выполнить только владелец организации',
    projectOwnerOrAdminOnly:
      'Это действие может выполнить только владелец или администратор проекта',
    onlyOrgOwnerTransfers:
      'Передать владение может только текущий владелец организации',
    targetMustBeOrgMember:
      'Этот пользователь должен сначала стать участником организации',
    alreadyOrgOwner: 'Этот пользователь уже владелец организации',
    ownerRoleImmutable: 'Роль владельца изменить нельзя',

    stripeNotConnected: 'Stripe не подключён к этому проекту',
    stripeCheckoutStartFailed: 'Не удалось начать оплату. Попробуйте ещё раз.',
    networkError: 'Ошибка сети. Проверьте соединение и попробуйте ещё раз.',
    serverError: 'Ошибка сервера. Попробуйте позже.',
    serviceUnavailable: 'Сервис временно недоступен. Попробуйте позже.',
  },
);

/** Ключи фраз этого словаря — ими адресуются и коды, и регулярки. */
export type ErrorStringKey = keyof typeof errorsStrings.en;

/**
 * WEB-1894 п.2: путь zod-ошибки правила логики → человеческая фраза.
 *
 * Путь сервера имеет форму `<номер правила>.<поле>[.<хвост>]`
 * (`0.action.target`). Первый сегмент — индекс правила в массиве, человек
 * считает правила с единицы. Второй (и, где он однозначен, третий) сегмент
 * переводится ПО СЛОВАРЮ верхнеуровневых полей правила
 * (backend/src/ai-funnels/block-schema/rule.schema.ts:200-207 —
 * id / trigger_scope / scope_ref / condition_group / action). Всё, чего в
 * словаре нет, печатается как есть: выдуманное имя увело бы человека не туда,
 * а сырой путь хотя бы совпадает с тем, что он видит в панели.
 */
const LOGIC_RULE_FIELD_KEYS: Readonly<Record<string, ErrorStringKey>> = {
  'action.target': 'logicFieldActionTarget',
  action: 'logicFieldAction',
  condition_group: 'logicFieldConditionGroup',
  trigger_scope: 'logicFieldTriggerScope',
  scope_ref: 'logicFieldScopeRef',
  id: 'logicFieldId',
};

export function describeLogicRulePath(
  path: string,
  locale: UiLocale = getUiLocale(),
): string {
  const s = pickStrings(errorsStrings, locale);
  const segments = path.split('.');
  const index = Number(segments[0]);
  const rest = segments.slice(1);
  const where = Number.isInteger(index)
    ? s.logicRuleAt(index + 1)
    : s.logicRuleAny;
  if (rest.length === 0) return s.logicRuleMissingData(where);

  // Сначала пара сегментов (`action.target`), потом один (`action`) — от
  // подробного к общему, выигрывает первое известное.
  const twoLevel = rest.slice(0, 2).join('.');
  const key = LOGIC_RULE_FIELD_KEYS[twoLevel] ?? LOGIC_RULE_FIELD_KEYS[rest[0]];
  if (key) return s.logicRuleField(where, s[key] as string);
  return s.logicRuleUnknownField(where, rest.join('.'));
}

/**
 * WEB-1253 ревизия (пачка 2, находка 2): коды, у которых сервер кладёт
 * СТРУКТУРНОЕ поле `code` (billing-access.decision.ts:29-33), а `.message` —
 * уже человеческий текст. Интерцептор (client.ts:220-238) переносит `code` на
 * сам Error, не примешивая его к тексту. Матчить такие записи регэкспом по
 * тексту — мёртвый код. Эти записи разбираются ПО КОДУ, до перебора
 * `ERROR_PATTERNS` и до русского passthrough — см. `localizeError`.
 *
 * Ревизия фикс-волны 23.08: нетарифные 402 денежного контура. Раньше эти
 * отказы приходили БЕЗ кода, и распознаватель тарифа (utils/tierLimit.ts)
 * объявлял их «упором в тариф» — человеку показывали окно покупки PRO вместо
 * настоящей причины. Текстовые дубли этих же причин — в `ERROR_PATTERNS`
 * ниже (тело `success:false` и старые сборки сервера приходят строкой).
 */
export const ERROR_CODE_KEYS: Readonly<Record<string, ErrorStringKey>> = {
  BILLING_ACCESS_REQUIRED: 'billingAccessRequired',
  // WEB-1864: 403 при понижении тарифа (shared/guards/assert-writable.ts).
  RESOURCE_READONLY: 'resourceReadonly',
  INSUFFICIENT_BALANCE: 'insufficientBalance',
  TOPUP_REQUIRES_PRO: 'topupRequiresPro',
};

/**
 * WEB-1874 (этап 4, часть 2) — ФРАЗЫ ПО МАШИННОМУ КОДУ ОТКАЗА, все 184 кода
 * реестра сервера.
 *
 * ПРАВДА ЖИВЁТ НА СЕРВЕРЕ: `backend/src/shared/errors/human-error-codes.ts`.
 * Здесь её КОПИЯ буква в букву, потому что кабинет обязан показать отказ на
 * своём языке даже тогда, когда сервер прислал только русскую половину (он
 * шлёт её и будет слать: старые сборки фронта и те, кто ходит curl'ом, читают
 * `message`). Копия — не «вторая правда»: совпадение множеств И самих фраз в
 * ОБЕ стороны держит сторож `backend/src/shared/guards/
 * human-error-codes.guard.spec.ts` (правило двух концов WEB-1844): пропал код
 * здесь — красный, разошлась фраза — красный, появился код без перевода —
 * красный.
 *
 * ⚠ КЛЮЧ = КОД КАК ОН УХОДИТ В ТЕЛЕ ОТВЕТА, РЕГИСТР ЗНАЧИМ. Кабинетные коды
 * пишутся UPPER_SNAKE, коды машинного интерфейса для интеграторов —
 * lower_snake, и это РАЗНЫЕ коды с разными текстами: `PROJECT_FROZEN`
 * (проектная ручка кабинета) и `project_frozen` (серверный ключ интегратора)
 * живут рядом и говорят разное. Поэтому поиск здесь идёт ТОЧНЫМ кодом, без
 * `toUpperCase()` — иначе шесть пар кодов молча слипаются в одну.
 *
 * ⚠ ЭТОТ СЛОВАРЬ — ОБЩАЯ ФОРМУЛИРОВКА. Там, где сервер подставляет в
 * `message` числа и имена (сумма предела, имя домена, название организации),
 * его текст точнее нашего, и `localizeError` в русском кабинете показывает
 * ИМЕННО серверный текст, а словарь работает для английского. Разбор — в
 * `utils/localizeError.ts`.
 */
export const errorCodeStrings = defineDictionary(
  {
    BILLING_ACCESS_REQUIRED:
      'Billing data is not available to you — the organization owner grants that access.',
    CAPABILITY_REQUIRED:
      'You do not have the project permission required for this action.',
    FORBIDDEN:
      'Only an organization Owner or Admin can check domain availability for the organization.',
    NOT_RESOURCE_OWNER:
      'You can only do this with what you created yourself. Ask a project owner or admin to do it for you.',
    RESOURCE_READONLY:
      'This resource is read-only because your subscription has been downgraded.',
    PROJECT_FROZEN: 'This project is frozen. Resolve billing to continue.',
    PROJECT_READONLY: 'This project is read-only.',
    PROJECT_NOT_FOUND: 'Project not found',
    ORGANIZATION_NOT_FOUND: 'Organization not found',
    ORG_MEDIA_CURSOR_INVALID:
      'The media hub page cursor is broken — start browsing again',
    CAPI_DISPATCH_LOG_CURSOR_INVALID:
      'The delivery log page cursor is broken — open the log again',
    WEBHOOK_DELIVERY_LOG_CURSOR_INVALID:
      'The delivery log page cursor is broken — open the log again',
    EXTERNAL_ANALYTICS_DISPATCH_LOG_CURSOR_INVALID:
      'The delivery log page cursor is broken — open the log again',
    VALIDATION_WEBHOOK_URL_SCHEME:
      'The receiver address must be a full https:// URL — for example https://api.example.com/hooks/web2app',
    VALIDATION_WEBHOOK_URL_FORMAT:
      'The receiver address must be a full URL with an http:// or https:// protocol',
    VALIDATION_API_KEY_NAME_LENGTH:
      'The key name is longer than 255 characters — shorten it',
    VALIDATION_DRAFT_FIELD_NULL:
      'This field cannot be cleared — provide a value or leave the field out',
    // WEB-2668: пустое имя или имя из одних пробелов у квиза, экрана с ценами,
    // воронки, смарт-ссылки, проекта и цепочки писем.
    VALIDATION_NAME_EMPTY: 'The name cannot be empty or consist only of spaces',
    VALIDATION_DASHBOARD_LAYOUT_INVALID:
      'The dashboard layout was not saved: it must be a JSON object of at most 16 KB',
    // WEB-2383 п.23 (шов Ш3 пачки 48): потолки длины платной двери генерации
    // отвечали одной русской фразой на оба языка.
    // WEB-2375 п.Д1: автор остановил генерацию сам — это не сбой.
    GENERATION_CANCELLED:
      'You stopped the generation. Only the work the AI had finished by then was charged.',
    // WEB-2375 п.3: задача потерялась, работу никто не начал.
    GENERATION_TASK_LOST:
      'The generation task was lost and never started — your credits have been refunded. Please start the generation again.',
    VALIDATION_GENERATION_DESCRIPTION_LENGTH:
      'The description is longer than 2000 characters — keep the essentials about the product, the AI will fill in the rest',
    VALIDATION_GENERATION_NICHE_LENGTH:
      'The topic is longer than 200 characters — this is a short field for the topic ("fitness", "weight loss"), not for the brief',
    // WEB-2384 п.9: язык проверяется по списку поддерживаемых.
    VALIDATION_GENERATION_LANGUAGE_UNSUPPORTED:
      'That language is not supported. Use a code from the list — for example "ru", "en", "pt-BR".',
    VALIDATION_GENERATION_LANGUAGE_LENGTH:
      'The language code is longer than 35 characters — use a code like "ru", "en", "pt-BR"',
    VALIDATION_GENERATION_VARIANT_TASK_LENGTH:
      'The variant task is longer than 500 characters — keep the main point: what makes this variant different',
    PROJECT_BLOCKED:
      'Publishing is blocked: the organization has an unpaid balance or a disputed payment.',
    ORG_FROZEN:
      'The organization is frozen: a payment is disputed or has failed.',
    NO_ORGANIZATION: 'You have no organization yet — create one first.',
    ORG_ID_REQUIRED:
      'You belong to several organizations — choose the one to create the project in.',
    EMAIL_NOT_VERIFIED:
      'Email verification required. Please verify your email address to access this resource.',
    MEMBER_FILTER_FORBIDDEN:
      'The “Member” filter is available to the owner, admins and the retention manager',
    JOURNEY_NOT_FOUND: 'Visitor path not found',
    SDK_LOG_ACCESS_FORBIDDEN:
      'The SDK log is available to the owner, project admins and the retention manager.',
    LEDGER_ACCESS_OWNER_OR_ADMIN:
      'The transaction history is available to the organization owner or an admin only',
    ADMIN_CHANGES_OWNER_ONLY:
      'Only the owner can change the project’s admins: an admin cannot remove or demote another admin.',
    OWNER_BILLING_ACCESS_IMMUTABLE:
      'The organization owner always manages billing — that access cannot be taken away.',
    PROJECT_OWNER_ONLY_SETTING:
      'Only the project owner can change this project setting.',
    CONSENT_SHARE_UNSUPPORTED_DOMAIN:
      'Shared cookie consent is not supported yet for domains of this project: browsers will not let a shared cookie be set on their parent domain. Release these domains or keep consent separate for each address.',
    POSTBACK_URL_UNKNOWN_MACRO:
      'The postback URL has an unknown macro. Only {click_id}, {payout}, {status} and {txid} are allowed.',
    FEATURE_LOCKED: 'This feature is available on paid plans.',
    PRO_REQUIRED: 'This feature is available on Pro plans only.',
    PUBLIC_FUNNEL_UNAVAILABLE: 'This page is currently unavailable.',
    TIER_LIMIT: 'You have reached your plan’s limit.',
    TOPUP_REQUIRES_PRO: 'Wallet top-up is available on Pro and higher plans.',
    INSUFFICIENT_BALANCE: 'Not enough funds in the wallet.',
    // WEB-3288: причина отказа платного действия ИИ при долге по кредитам.
    CREDIT_DEBT_BLOCKED:
      'Your organization has a credit debt — paid AI actions are closed until it is settled. Top up the wallet: the debt is settled right away and AI works again. Your published funnels keep working in the meantime.',
    // WEB-1908: причина денежного отказа 402 (поле `reasonCode` тела). Общий
    // код тела (TIER_LIMIT/FEATURE_LOCKED) выбирает окно, эти — говорят, что
    // именно упёрлось. Зеркало backend/src/shared/errors/human-error-codes.ts.
    PAYMENT_REQUIRED_TIER_LIMIT_PROJECTS:
      'Your plan does not allow any more projects in this organization.',
    PAYMENT_REQUIRED_TIER_LIMIT_QUIZZES:
      'Your plan does not allow any more active quizzes.',
    PAYMENT_REQUIRED_TIER_LIMIT_PAYWALLS:
      'Your plan does not allow any more active paywalls.',
    PAYMENT_REQUIRED_TIER_LIMIT_FUNNELS:
      'Your plan does not allow any more funnels.',
    PAYMENT_REQUIRED_TIER_LIMIT_FUNNELS_DETACH:
      'Your plan does not allow detaching a piece of the funnel: it would become a funnel of its own and the limit is already used up. Upgrade your plan to detach it.',
    PAYMENT_REQUIRED_TIER_LIMIT_DOMAINS:
      'Your plan does not allow any more custom domains.',
    PAYMENT_REQUIRED_PUBLISH_ON_FREE:
      'Publishing is not available on the Free plan.',
    PAYMENT_REQUIRED_LIMIT_CHECK_INCOMPLETE:
      'The plan limit could not be checked: the request is missing the data needed for the check.',
    // WEB-2502: причины отказа в состоянии «не оплачено» (тарифа Free нет).
    UNPAID_PUBLISH_CLOSED:
      'Publishing is not available without a paid plan: pay for the plan to launch traffic.',
    UNPAID_DOMAINS_CLOSED:
      'Custom domains are not available without a paid plan: pay for the plan to connect a domain.',
    UNPAID_PROJECT_LIMIT:
      'Without a paid plan you can have one project. Pay for the plan to create more.',
    UNPAID_QUIZ_LIMIT:
      'Without a paid plan you can create up to 50 quizzes. Pay for the plan to create more.',
    UNPAID_PAYWALL_LIMIT:
      'Without a paid plan you can create up to 50 paywalls. Pay for the plan to create more.',
    UNPAID_FUNNEL_LIMIT:
      'Without a paid plan you can create up to 50 funnels. Pay for the plan to create more.',
    UNPAID_SMART_LINKS_CLOSED:
      'A/B tests are not available without a paid plan: pay for the plan to run them.',
    UNPAID_AI_CLOSED:
      'AI is not available without a paid plan: pay for the plan to use AI.',
    UNPAID_BRIDGE_CLOSED:
      'App connection is not available without a paid plan: pay for the plan to set it up.',
    UNPAID_TOPUP_CLOSED:
      'Wallet top-up is not available without a paid plan: pay for the plan first.',
    TRIAL_TOPUP_CAP_EXCEEDED:
      'During the trial you can buy credit packages for up to {cap} in total. {remaining} left — pick a smaller package or pay for the plan: there is no limit after payment.',
    TRIAL_TOPUP_CAP_UNAVAILABLE:
      'Buying credits during the trial is unavailable right now: we could not check the trial limit. Nothing was charged — try again later or contact support.',
    USAGE_DEBT_FORBIDDEN:
      'Only an organization owner or an admin with billing access can pay the usage debt.',
    USAGE_DEBT_ORG_NOT_FOUND:
      'The project is not linked to an organization — no usage debt was found.',
    UNFREEZE_REASON_REQUIRED:
      'The organization still has open disputes — unfreezing requires an explicit reason.',
    UNFREEZE_WRITE_OFF_DECISION_REQUIRED:
      'Credits were granted for disputed payments of the organization, or the amount could not be determined. Mark "write-off done" or "write-off not required" — the freeze is not lifted without the mark.',
    CREDIT_DAILY_CAP_EXCEEDED: 'The daily top-up cap has been exceeded.',
    STARTING_BALANCE_NEEDS_ADMIN:
      'A starting balance cannot be granted without an initiating admin.',
    RESUBMIT_OTHER_ORG:
      'Resubmitted with a different organization: this key already carries an operation for another organization. Nothing was changed.',
    RESUBMIT_OTHER_CLIENT:
      'Resubmitted with a different client: this key already carries an operation for another client. Nothing was changed.',
    RESUBMIT_CHANGED_FIELDS:
      'Resubmitted with changed fields: this key already carries a different operation. Nothing was changed.',
    ADJUSTMENT_AMOUNT_INVALID:
      'The adjustment amount must be a whole, non-zero number of cents',
    ADJUSTMENT_AMOUNT_ABOVE_LIMIT:
      'The adjustment amount exceeds the per-operation limit.',
    ORG_NOT_OWNED_BY_CLIENT:
      'The organization does not belong to the client being adjusted. Nothing was changed.',
    TIER_ORG_NOT_OWNED_BY_CLIENT:
      'The organization does not belong to the client being granted the plan. Nothing was changed.',
    DEBIT_ABOVE_BALANCE:
      'The debit exceeds the balance — the balance cannot go negative.',
    ACTIVE_STRIPE_SUBSCRIPTION_EXISTS:
      'The client has an active Stripe subscription — cancel it first.',
    MONEY_REVIEW_KIND_UNKNOWN: 'Unknown money review type.',
    STRIPE_NOT_CONNECTED: 'Stripe is not connected for this project',
    STRIPE_KEYS_ENV_MISMATCH:
      'The keys come from different Stripe environments. Take both keys from the same environment (Live or Test) and try again.',
    STRIPE_CONNECT_FAILED:
      'Could not connect Stripe. Check the secret key and try again.',
    stripe_connect_conflict:
      'This mode is already being connected — reload the page to see which keys were saved.',
    STRIPE_PRICE_NOT_FOUND: 'Price not found in this project',
    STRIPE_PRICE_UPDATE_FAILED: 'Could not update the price in Stripe.',
    SUBSCRIPTION_TARGET_PRICE_NOT_RECURRING:
      'A one-time price cannot be the target of a subscription plan change',
    NO_RECURRING_PRICES:
      'The project has no active recurring price — there is nothing to switch to in the portal',
    NOTHING_TO_UPDATE:
      'Nothing to change: provide active and/or accessDurationDays',
    KEEP_SUBSCRIPTIONS_UNAVAILABLE:
      'Disconnecting while keeping subscriptions is unavailable, and the decision must be recorded. Choose “cancel now” or “cancel at period end”.',
    STORE_CUSTOMER_NOT_FOUND: 'Customer not found',
    PROVIDER_NOT_SUPPORTED: 'This payment provider is not supported yet.',
    project_frozen:
      'The organization is frozen — server-key money operations are stopped; settle the debt or resolve the dispute to resume.',
    project_readonly:
      'The project is read-only — server-key money operations and customer subscription management are stopped; platform support lifts this restriction.',
    test_key_not_allowed:
      'This is a test key (sk_test_…) — money operations and customer subscription management are not available with it; issue a live key (sk_live_…).',
    CHARGES_EXPORT_NOT_JOURNALED:
      'The export was not performed: it could not be recorded in the team activity log (“Audit → Project team”). The file contains buyers’ personal data, so it is not handed out without a record of who exported it. Try again later.',
    PERSONAL_DATA_EXPORT_NOT_JOURNALED:
      'The export was not performed: it could not be recorded in the team activity log (the “Audit” section). The file contains email addresses of visitors and leads, so it is not handed out without a record of who exported it. Try again later.',
    REPORT_EXPORT_NOT_FOUND: 'There is no such export — request it again.',
    REPORT_EXPORT_EXPIRED:
      'The export link has expired — request the export again.',
    REPORT_EXPORT_NOT_READY:
      'The export is still being prepared — the file will be available once it is built.',
    REPORT_EXPORT_FAILED:
      'The export could not be built — request it again. If it happens again, contact support.',
    REPORT_EXPORT_UNAVAILABLE:
      'Could not queue the export right now — please try again in a minute.',
    REPORT_EXPORT_DISABLED:
      'Background exports are not set up on this server — contact support.',
    REPORT_EXPORT_SCOPE_CHANGED:
      'Your access in this project has changed and this file was built wider than you may now see — request the export again.',
    charge_disabled:
      'Server-side charges are disabled for this project — enable them in the project settings.',
    refund_disabled:
      'Server-side refunds are disabled for this project — enable them in the project settings.',
    subscription_manage_disabled:
      'Server-key management of customer subscriptions is disabled for this project — enable it in the project settings.',
    bridge_temporarily_unavailable:
      'The machine bridge is temporarily unavailable — this is a temporary failure on our side, not a permission problem: your plan and access keys are fine. Retry the request later.',
    // WEB-2156: текст — про ТАРИФ, без слова «мост» (дверью пользуется и
    // клиент без моста; метка показа доступ не закрывает).
    bridge_tier_expired:
      'The grace period for paid machine channels has ended — return to a paid plan to resume them. Access keys are not revoked; there is no need to reissue them.',
    // Пачка 49: приём событий с сервера клиента (POST /public/event).
    event_time_in_milliseconds:
      'The event time arrived in milliseconds — the eventTimeSec field expects unix seconds (10 digits). Divide the value by 1000 and retry the request.',
    invalid_currency:
      'The currency code is not in the ISO-4217 list — send the three-letter code of a real currency (for example, usd or eur).',
    invalid_client_ip:
      'The clientIp field is not an IP address. Send the buyer address as IPv4 or IPv6, or omit the field.',
    scopes_empty_after_billing_flags:
      'The key’s scope list came out empty after the project’s toggles were applied — add a scope other than billing:* and subscription:manage, or turn on the matching project toggle.',
    subscription_action_in_progress:
      'Another operation is already running on this subscription — retry later.',
    subscription_not_paused:
      'The entitlement has expired and the Stripe subscription is not paused — there is nothing to resume.',
    no_active_subscription:
      'The customer has no active subscription — pass subscriptionId explicitly.',
    charge_outcome_unknown:
      'The outcome of this charge is unknown — the money may have moved; check the payment on the provider’s side.',
    refund_state_unknown:
      'The refund state for this subscription is unknown — check the refund on the provider’s side.',
    charge_refunded_needs_new_key:
      'The previous charge for this price today was refunded — pass your own idempotencyKey for a new charge.',
    price_not_one_time:
      'A charge does not create a subscription — use a one-time price.',
    price_has_no_amount: 'The price has no amount.',
    charge_amount_above_limit:
      'The amount is above the project’s per-operation limit.',
    no_saved_payment_method: 'The customer has no saved payment method.',
    charge_project_daily_limit_exceeded:
      'The project’s daily charge cap is exhausted — try later or raise the limit in the project settings.',
    charge_daily_limit_exceeded:
      'The customer’s daily charge cap is exhausted.',
    charge_in_progress:
      'An operation with this idempotency key is already running — repeat the same request later.',
    refund_in_progress:
      'A refund is already running in another request — retry later.',
    idempotency_key_reused:
      'This idempotency key was already used with different parameters — pass a new key or repeat with the original price.',
    discount_current_price_unknown:
      'The subscription’s current price is not in the project catalog — the interval is unknown, so the discount is not applied blindly.',
    discount_interval_mismatch:
      'The discount’s target price must have the same interval as the current subscription.',
    guid_user_mismatch:
      'Parameters guid and user were both given and do not match.',
    guid_required: 'Provide guid (or its alias user for web2wave migrations).',
    SUBSCRIPTION_ALREADY_REFUNDED:
      'This subscription has already been refunded',
    NO_PAYMENT_TO_REFUND: 'There was no charge — nothing to refund',
    CHARGE_OUTCOME_UNKNOWN:
      'The outcome is unknown — the money may have been charged. Check the payment on the provider’s side.',
    SUBSCRIPTION_ACTION_IN_PROGRESS:
      'Another operation is running on this subscription — the refund has not started, retry later',
    REFUND_STATE_UNKNOWN:
      'The refund state for this subscription is unknown — retry later.',
    // WEB-2005 — фильтры журнала списаний.
    CHARGE_FILTER_STATUS_UNKNOWN: 'Unknown charge outcome in the filter',
    CHARGE_FILTER_DATE_INVALID: 'The period date is not a valid date',
    CHARGE_FILTER_PERIOD_INVALID:
      'The start of the period is later than its end',
    CHARGE_FILTER_AMOUNT_INVALID:
      'The amount filter is invalid: use a whole number in the currency’s minor units, with the minimum not above the maximum',
    CHARGE_FILTER_SORT_UNKNOWN: 'Unknown sort order for the charge log',
    CHARGE_FILTER_TIMEZONE_UNKNOWN: 'Unknown time zone in the period filter',
    CHARGE_FILTER_VALUE_INVALID:
      'A filter value is malformed: each filter takes a single text value',
    // П3-3 (свежий взгляд пачки 37): пять отказов двухфакторной защиты. Сервер
    // теперь называет их машинным кодом, а не машинной строкой в тексте.
    '2FA_NOT_ENABLED': 'Two-factor authentication is not on.',
    SEND_CODE_NOT_APPLICABLE:
      'Resending the code is not available for this sign-in method.',
    LOGIN_TOO_MANY_ATTEMPTS:
      'Too many sign-in attempts. Try again in {minutes} min.',
    RATE_LIMITED_TRY_AGAIN_IN: 'Too many attempts. Try again in {minutes} min.',
    RESEND_COOLDOWN:
      'The code has already been sent — wait a moment and request a new one.',
    PASSWORD_REQUIRED: 'Enter your password to confirm.',
    INVALID_PASSWORD: 'Wrong password.',
    // WEB-3970: повторное подтверждение передачи и удаления организации.
    ORG_ACTION_PASSWORD_INVALID: 'Wrong password — enter your password again.',
    ORG_ACTION_GOOGLE_CONFIRM_REQUIRED:
      'Confirm this action by signing in with Google.',
    ORG_ACTION_GOOGLE_CONFIRM_MISMATCH:
      'Confirm with the same Google account you signed in with.',
    ORG_ACTION_CONFIRM_UNAVAILABLE:
      'Could not check the confirmation. Try again in a minute.',
    VERIFICATION_CODE_INVALID: 'Invalid verification code',
    VERIFICATION_CODE_EXPIRED: 'The code has expired',
    VERIFICATION_CODE_ATTEMPTS_EXCEEDED: 'Too many code attempts',
    RESET_CODE_INVALID: 'Invalid reset code',
    RESET_CODE_EXPIRED: 'The code has expired',
    RESET_CODE_ATTEMPTS_EXCEEDED: 'Too many code attempts',
    EMAIL_TAKEN: 'This email is taken by an active account.',
    EMAIL_TAKEN_DEACTIVATED:
      'This email belongs to a deactivated account. Deactivation does not free the address: restore that account or use a different email.',
    CLIENT_EMAIL_REQUIRED: 'Enter the client’s email',
    CLIENT_EMAIL_NOT_FOUND: 'No client with that email was found',
    PROJECT_OWNER_ROLE_NOT_ASSIGNABLE:
      'The Owner role cannot be assigned from the admin panel: a project has exactly one owner. Ownership is transferred by the project or organization owner.',
    PROJECT_OWNER_PROMOTION_FORBIDDEN:
      'Promotion to Owner from the admin panel is not possible: a project has exactly one owner.',
    PROJECT_SOLE_OWNER_DEMOTE_FORBIDDEN:
      'A project has exactly one owner — they cannot be demoted. Changing the owner is a separate ownership transfer.',
    PROJECT_SOLE_OWNER_REMOVE_FORBIDDEN:
      'A project has exactly one owner — they cannot be removed. Changing the owner is a separate ownership transfer.',
    ALREADY_PROJECT_OWNER:
      'This member is already the project owner — there is nothing to transfer.',
    OWNERSHIP_TARGET_NOT_MEMBER:
      'Only a project member can be made owner: invite the person to the project first, then transfer ownership.',
    // WEB-1947 — отказы, жившие русской строкой в константе на сервере.
    TRANSFER_OWNERSHIP_FORBIDDEN:
      'Only the project owner or the organization owner can transfer a project.',
    PROJECT_OWNER_CANNOT_LEAVE:
      'The owner cannot leave the project: hand ownership to someone else first — use “Make owner” in the project members list.',
    ADMIN_CANNOT_CREATE_ADMIN:
      'An admin cannot appoint another admin: only the owner decides who administers the project.',
    REMOVED_MEMBER_LINK_INVITE:
      'You were removed from this project, so the shared invite link will not let you back in. Ask the project owner to send you a personal invitation by email.',
    PROJECT_HAS_PAYMENT_HISTORY:
      'This project cannot be deleted: it has taken payments, and we are required to keep payment history.',
    PROJECT_HAS_ENTITLEMENT_GRANTS:
      'This project cannot be deleted: it has active customer entitlements.',
    // WEB-1947 — три помехи удаления собственного аккаунта.
    ACCOUNT_DELETION_ORG_TRANSFER_REQUIRED:
      'Hand your organization over to someone else, or use the admin panel, before deleting your account',
    ACCOUNT_DELETION_DOMAIN_RELEASE_REQUIRED:
      'Release the domains you bought, or hand them over, before deleting your account',
    ACCOUNT_DELETION_SUBSCRIPTION_ACTIVE:
      'Cancel your paid subscription under Balance / Plan first, then delete the account. A paused or unfinished subscription holds the deletion just the same: Stripe can still charge you for it, and once the account is gone you can no longer come back and cancel — cancel it in Stripe or wait until it ends on its own.',
    // Ревизия 31 — тот же гейт, но след без подписки: отменять нечего.
    ACCOUNT_DELETION_PAYMENT_PROFILE_ACTIVE:
      'Your Stripe payment profile is still there, and we have no confirmation that it can no longer be charged — so the account is not deleted yet. There is no subscription for you to cancel yourself: write to support, we will check the profile in Stripe and close it, after which the deletion will go through.',
    // WEB-1956 — две помехи на той же двери, приходившие по-английски без кода.
    ACCOUNT_DELETION_PASSWORD_REQUIRED:
      'Deleting the account requires a password: set one in settings first, then delete.',
    ACCOUNT_DELETION_PASSWORD_INVALID:
      'Wrong password — the account was not deleted. Enter your current password again.',
    // WEB-1945 — деактивация клиента админом при живой подписке Stripe.
    CLIENT_DEACTIVATION_SUBSCRIPTION_ACTIVE:
      'A client with a live Stripe subscription cannot be deactivated: their sign-in will be closed, they will no longer be able to cancel, and Stripe will keep charging the card. Cancel the subscription in the platform’s Stripe dashboard first (Customers → the client’s card → Subscriptions → Cancel subscription), then deactivate. A subscription the client has already cancelled at the end of the paid period does not block deactivation.',
    INVITE_ALREADY_ACCEPTED:
      'The invite has already been accepted — it cannot be revoked. To close access, remove the person from the members list.',
    ORG_COUNT_FAILED:
      'Could not count the organizations — creation was cancelled. Please try again.',
    ORG_NAME_CONFIRMATION_MISMATCH:
      'Type the organization name exactly. That is how we make sure the right one is being deleted.',
    ORG_DELETE_BLOCKED: 'The organization cannot be deleted yet.',
    PURCHASED_DOMAINS:
      'The organization holds domains bought through us — they cannot be detached by hand.',
    CUSTOM_DOMAINS:
      'The organization has custom domains attached — detach them in the Domains section.',
    PROJECT_PAYMENT_HISTORY:
      'The organization’s projects have taken customer payments whose retention period has not expired yet.',
    PAYMENT_RECORDS_RETAINED:
      'Customer payment records cannot be deleted — they are retained under our money-data rule.',
    ACTIVE_ENTITLEMENT_GRANTS:
      'Customers hold active entitlements. People paid for them, so we do not revoke them together with the organization.',
    ACTIVE_PLATFORM_SUBSCRIPTION:
      'The organization has an active plan subscription. Cancel the plan first — otherwise charges continue.',
    WALLET_BALANCE:
      'The organization’s wallet still holds money. Deleting would burn it, so contact support first.',
    OPEN_CHARGEBACK_DISPUTE:
      'A chargeback dispute is open for the organization. Until it closes we cannot delete — the data is needed for the case.',
    UNSYNCED_BALANCE_PAYMENT:
      'The organization’s plan has an unsettled payment: an invoice was paid from the wallet balance and the confirmation has not gone through yet. We do not delete the organization until it is sorted out — please contact support.',
    DOMAIN_NOT_FOUND: 'Domain not found',
    DOMAIN_RENEWAL_PAYMENT_ORG_PAID:
      'Your organization has a paid plan — the domain is renewed from the wallet, a separate renewal payment is not needed.',
    DOMAIN_RENEWAL_PAYMENT_NOT_ELIGIBLE:
      'This domain cannot be renewed by payment right now: it was not bought through us, its purchase is not confirmed yet, it is frozen by a payment dispute, or it is waiting for a check with the registrar.',
    DOMAIN_RENEWAL_PAYMENT_NO_STRIPE_CUSTOMER:
      'Your organization has no billing profile yet, so there is no one to issue a renewal invoice to. Please contact support.',
    DOMAIN_RENEWAL_PAYMENT_STRIPE_REFUSED:
      'The payment system did not accept the domain renewal invoice. Please try again later.',
    DOMAIN_RENEWAL_PAYMENT_PRICE_ROSE:
      'The registrar renewal price rose above the paid amount — the domain was not renewed, the money will be returned to your card.',
    DOMAIN_RENEWAL_PAYMENT_CYCLE_BUSY:
      'This registration period is already being renewed or has been renewed — the domain is not renewed twice, the money will be returned to your card.',
    DOMAIN_TAKEN: 'This domain is already taken.',
    INVALID_DOMAIN: 'Invalid domain name.',
    PLATFORM_DOMAIN_NOT_ALLOWED:
      'This name belongs to our platform and cannot be connected as your own domain.',
    UNSUPPORTED_TLD:
      'We cannot register this domain yet: its zone requires registrant data we do not collect. Please pick another zone.',
    TRIAL_DOMAIN_BLOCKED:
      'Domains are not available during the trial. Wait for the first subscription payment to buy domains.',
    DOMAIN_PRICE_UNAVAILABLE:
      'The domain price is unavailable right now — the registrar is not responding. Try again later.',
    DOMAIN_PURCHASE_IN_PROGRESS:
      'This domain is already being purchased — wait for it to finish.',
    DOMAIN_PURCHASE_PROCESSING:
      'The domain purchase is being processed — there is no need to pay again. As soon as the registrar confirms the purchase, the domain will appear in your list; if the purchase did not go through, the money will be returned to your balance.',
    DOMAIN_PURCHASE_NOT_MOVABLE:
      'The domain is still being purchased — you can move it to another project or to the organization pool after the purchase is complete.',
    DOMAIN_PURCHASE_RETRY_LIMIT:
      'Too many purchase attempts for this domain — try again later.',
    DOMAIN_CONNECT_RATE_LIMITED:
      'Too many domain connection attempts for this organization — try again later.',
    PUBLIC_EVENT_PROJECT_RATE_LIMITED:
      'Too many events for this project: at most 300 per minute — retry later.',
    PUBLIC_EVENT_FAILED_AUTH_RATE_LIMITED:
      'Too many requests without a valid signature from this address — retry later.',
    PUBLIC_EVENT_AUTH_UNAVAILABLE:
      'The signature check is temporarily unavailable on our side — retry the request later (for an event, with the same eventId).',
    PUBLIC_EVENT_IP_RATE_LIMITED:
      'Too many requests from this address: at most 3000 per minute for all projects together — wait as many seconds as the Retry-After header says and retry the event with the same eventId.',
    // WEB-2431 п.4: `{tests}` — имена A/B-тестов из `details.tests` отказа.
    DOMAIN_HAS_SMART_LINKS:
      'First deal with this domain’s A/B tests: {tests}. Move them to the target project or delete them — then the domain can be transferred.',
    DOMAIN_NOT_SERVABLE:
      'The domain is not set up yet — wait for the DNS check.',
    NO_DOMAIN_BOUND:
      'Attach a domain and subdomain to the funnel before publishing.',
    CROSS_ORG_REASSIGN_FORBIDDEN:
      'A domain can only be reassigned to a project within the same organization.',
    CUSTOM_HOSTNAME_TAKEN: 'This hostname is already taken at the provider.',
    CUSTOM_SUBDOMAINS_UNAVAILABLE:
      'Subdomains on custom domains are temporarily unavailable — the Cloudflare for SaaS integration is not configured.',
    PARENT_IS_SUBDOMAIN:
      'A subdomain cannot be created from another subdomain — pick a root domain.',
    PARENT_NOT_SERVABLE:
      'The parent domain has not passed its DNS check yet — wait for it to go live.',
    ROUTE_PATH_RESERVED_PREFIX:
      'The "/public" and "/__w2a" addresses are reserved for edge service channels — a funnel bound there would never open. Choose a different path.',
    REGISTRAR_NOT_CONFIGURED:
      'The domain registrar is not configured — the operation is unavailable. No charge was made.',
    CUSTOM_DOMAINS_NOT_CONFIGURED:
      'Custom domain connection is not configured on the server — the operation is unavailable. Please contact support.',
    // WEB-2489: перезапуск подключения своего домена.
    PUBLIC_RESOLVE_HOST_REQUIRED:
      'The site address (host parameter) is missing — without it we cannot tell which funnel to show.',
    // WEB-3673: подтверждение показа прогретой страницы с негодным телом.
    PUBLIC_SHOW_CONFIRM_INVALID:
      'The show confirmation was not accepted: it needs a project id (UUID) and a page load id (16 to 64 Latin letters, digits, "-" or "_").',
    // WEB-3286: сохранение версии ценового плана организации (админка).
    PRICING_PLAN_WRITE_FORBIDDEN:
      'Only an administrator with the "pricing_plan.write" permission can change pricing plans.',
    PRICING_PLAN_ORG_NOT_FOUND:
      'Organization not found — the pricing plan version was not saved.',
    PRICING_PLAN_FIELD_INVALID:
      'The pricing plan version was not saved: the "{field}" value is invalid.',
    PRICING_PLAN_EFFECTIVE_FROM_IN_PAST:
      'The version start date is already in the past — past events are never recalculated. Choose the current moment or later.',
    PRICING_PLAN_TARIFF_CHANGE_TOO_SOON:
      'The plan price can change no earlier than {earliest} (in 30 days). An immediate change is allowed only for a decrease (the "this is a decrease" mark) or for an organization that has never paid.',
    PRICING_PLAN_AI_MARKUP_BELOW_FLOOR:
      'The resulting AI markup for the "{modelClass}" class is below the ×{floor} minimum — this plan version cannot be saved.',
    // WEB-3848: ручки админки плана и цена тарифа в Stripe.
    PRICING_PLAN_READ_ORG_NOT_FOUND:
      'Organization not found — the pricing plan cannot be shown.',
    PRICING_PLAN_CALCULATOR_INPUT_INVALID:
      'The calculation was not run: the "{field}" value is invalid.',
    PRICING_PLAN_STRIPE_PRODUCT_NOT_CONFIGURED:
      'The new plan price cannot be saved: the platform Stripe product is not configured (PLATFORM_STRIPE_PRODUCT_ID). Rates and other plan fields can be saved without changing the plan price.',
    PRICING_PLAN_STRIPE_PRICE_CONFLICT:
      'The plan price cannot be saved: in Stripe the "{lookupKey}" key is held by a different price. Resolve that price in the Stripe dashboard and try again.',
    // WEB-3292: разовый счёт допродажи Enterprise (админка).
    ENTERPRISE_ADDON_NO_STRIPE_CUSTOMER:
      'The organization has no Stripe customer — the add-on invoice was not issued. A Stripe customer appears after the first plan payment.',
    ENTERPRISE_ADDON_AMOUNT_INVALID:
      'The add-on invoice amount must be a whole number of cents from 1 to {max}. The invoice was not issued.',
    ENTERPRISE_ADDON_STRIPE_REFUSED:
      'Stripe refused at the "{step}" step — nothing was charged. Retry with the same request key: the retry continues from the same place.',
    ENTERPRISE_ADDON_IDEMPOTENCY_CONFLICT:
      'This request key was already used for a different add-on invoice (another amount or description). Reopen the form and try again.',
    // WEB-2503: оформление тарифа по периоду (вторая оплата, цена не настроена).
    CHECKOUT_SUBSCRIPTION_EXISTS:
      'The organization already has a subscription — a second payment is not needed. Change the card or period, or cancel, in subscription management.',
    CHECKOUT_TARIFF_PRICE_UNAVAILABLE:
      'Plan payment is unavailable right now: the plan price is not configured. Nothing was charged — contact support.',
    // WEB-3892: второе оформление тарифа, пока открыто первое.
    CHECKOUT_IN_PROGRESS:
      'Plan payment is already open in another tab or window — finish it there or try again in 35 minutes. Nothing was charged a second time.',
    // WEB-4198: повтор с ключом уже закрытого резерва.
    BALANCE_HOLD_KEY_CLOSED:
      'This request has already finished and cannot be repeated with the same number. Start the action again.',
    CUSTOM_HOSTNAME_NOT_FOUND:
      'Cloudflare no longer has this domain connection — restart the connection.',
    DOMAIN_CONNECTION_RESTART_NOT_NEEDED:
      'This domain connection does not need a restart: it is not stopped or has already been restarted.',
    DOMAIN_CONNECTION_RESTART_FROZEN:
      'The domain is suspended because of billing — restarting the connection will not bring it back. Restore the payment or plan first.',
    DOMAIN_CONNECTION_RESTART_PROVIDER_FAILED:
      'The connection has been restarted, but Cloudflare did not accept the domain right now. We will retry the registration automatically within a few minutes — the domain stays "Pending".',
    // WEB-2132 (атом 1): подтверждение владения доменом через TXT.
    OWNERSHIP_TXT_MISSING:
      'The ownership TXT record was not found, or its value does not match. Check the record with your domain registrar and try again.',
    // WEB-2255 (п.4): три исхода проверки TXT вместо одного — у каждого своя
    // подсказка «когда имеет смысл нажать в следующий раз».
    OWNERSHIP_TXT_NOT_FOUND:
      'There is no ownership TXT record in the domain zone. Add it with your registrar, then re-check in 5-30 minutes — that is how long a new record usually takes to spread.',
    OWNERSHIP_TXT_VALUE_MISMATCH:
      'The TXT record is there, but its value is not the one we expect. Fix the value with your registrar and re-check right after saving — there is no need to wait for it to spread.',
    OWNERSHIP_DOMAIN_NOT_RESOLVING:
      'The domain does not answer DNS yet, so there is nothing we can say about the TXT record. This is normal for a freshly bought domain and right after a nameserver change: spreading can take a few hours. Try again later.',
    OWNERSHIP_CHECK_FAILED:
      'The TXT record could not be checked — a temporary failure on our side or with the DNS service. Try again.',
    OWNERSHIP_CLAIMED_BY_ANOTHER_ACCOUNT:
      'Ownership of this domain has already been confirmed by another account.',
    OWNERSHIP_ALREADY_VERIFIED:
      'Ownership of this domain is already confirmed — no re-check is needed.',
    OWNERSHIP_VERIFICATION_NOT_APPLICABLE:
      'TXT ownership verification does not apply to this domain.',
    OWNERSHIP_PENDING_IN_ANOTHER_PROJECT:
      'This domain is already awaiting TXT confirmation in another project of your organization.',
    // Гейты публикации воронки (assert-paywall-publishable.ts) — сообщение
    // сервера динамическое (называет экран/блок/цену), здесь общая фраза.
    LEGAL_URLS_REQUIRED:
      'Cannot publish: the terms block is missing links to the terms of use and the privacy policy.',
    PAYMENTS_NOT_CONNECTED:
      'Cannot publish: the funnel has a payment block but the project has no payment provider connected.',
    PAYWALL_PRICE_MISSING:
      'Cannot publish: some payment blocks have no price set.',
    PAYWALL_PRICE_STALE:
      'Cannot publish: the price no longer exists in the connected Stripe account — pick the prices again.',
    // WEB-3336: `{screen}` — имя экрана после оплаты из `details` отказа.
    PAYWALL_PLAN_SELECTOR_POST_PURCHASE:
      'Cannot publish: the plan selector is only on the post-purchase screen "{screen}" — buyers do not see it before paying, so the payment button has no price. Move the plan selector to a regular screen, or delete it and set a price on the payment button.',
    PAYWALL_PRICE_CONFLICT:
      'Cannot publish: the screen has an ambiguous price source.',
    APP_BRIDGE_NOT_CONFIGURED:
      'Cannot publish: the screen has a button that opens the app, but the project has no app bridge configured — set it up in project settings → App connection.',
    // WEB-2355: квиз чувствительной ниши без блока явного согласия перед
    // первым чувствительным вопросом сервер не публикует.
    SENSITIVE_CONSENT_REQUIRED:
      'Cannot publish: a visible explicit consent block must come before the first sensitive question on every visitor path — check the branching and the block display rules.',
    EMPTY_QUIZ:
      'Add at least one screen with content before publishing or sending for review.',
    EMPTY_PAYWALL:
      'An empty paywall cannot be published or sent for review: add at least one screen with blocks — for example, a screen with prices. An already published paywall keeps working as is — this only blocks a new publication, sending for review and a rollback to an empty version.',
    STALE_DRAFT:
      'The page was changed in another window or by another member — refresh the editor.',
    DRAFT_BASE_REQUIRED:
      'Could not tell which draft version you are looking at — refresh the editor and try again.',
    // WEB-2997: отказы ручки раскладки графа.
    GRAPH_LAYOUT_INVALID:
      'The graph node positions were not saved: the data came in the wrong shape. Refresh the page and try again.',
    GRAPH_LAYOUT_TOO_LARGE:
      'The graph node positions were not saved: there are more than 1000 screens. Remove extra screens or click “Auto-arrange”.',
    // WEB-3319 (Р-2): «окном поверх квиза» сохранено как переход — пометка
    // в ответе сохранения и публикации (`openModeDowngrades`), не отказ.
    OVERLAY_FRAME_FORBIDDEN:
      'This page does not allow being shown inside other pages, so it cannot open in a window over the quiz — the paywall will open as a redirect. Your other changes are saved.',
    OVERLAY_CHECK_FAILED:
      'We could not check whether this page can be shown in a window over the quiz (it did not answer in time or answered with an error) — for now the paywall will open as a redirect. Save again later to re-check. Your other changes are saved.',
    OVERLAY_NOT_HTTPS:
      'Only addresses starting with https:// can open in a window over the quiz — the paywall will open as a redirect. Your other changes are saved.',
    OVERLAY_TOO_MANY:
      "At most 10 different window addresses are checked in one save — this window's address is over the limit, so the paywall will open as a redirect. Remove extra windows or switch some paywalls to a redirect. Your other changes are saved.",
    // WEB-3819: «перейти к пейволлу» — только в логике квиза.
    LOGIC_PAYWALL_JUMP_IN_PAYWALL:
      'The “Go to paywall” rule only works in a quiz — it cannot be saved in a paywall. Remove the rule or choose another action.',
    // WEB-3929: воронка «с нуля» — выбор частей «новый / из библиотеки / без».
    VALIDATION_FUNNEL_PIECE_SOURCE:
      'For the quiz and the paywall you can only choose “new”, “from library” or “none”.',
    VALIDATION_FUNNEL_PIECE_ID_REQUIRED:
      'You chose “from library” — pick which quiz or paywall to take from the library.',
    VALIDATION_FUNNEL_PIECE_ID_UNEXPECTED:
      'A quiz or paywall from the library is only accepted together with the “from library” choice for that part.',
    VALIDATION_FUNNEL_PIECE_ID_FORMAT:
      'The selected quiz or paywall could not be recognized — reload the page and pick it from the library again.',
    FUNNEL_NEEDS_PIECE: 'A funnel needs at least a quiz or a paywall.',
    FUNNEL_LIBRARY_PIECE_DRAFT:
      'A draft can’t go into a funnel — publish it first.',
    FUNNEL_LIBRARY_QUIZ_NOT_FOUND:
      'The selected quiz was not found in this project — it may have been deleted. Choose another one.',
    FUNNEL_LIBRARY_PAYWALL_NOT_FOUND:
      'The selected paywall was not found in this project — it may have been deleted. Choose another one.',
    FUNNEL_NOT_FOUND:
      'The funnel was not found in this project — it may have been deleted.',
    // WEB-3985 п.1: без привязки к воронке.
    QUIZ_NOT_FOUND: 'The quiz was not found — it may have been deleted.',
    PAYWALL_NOT_FOUND: 'The paywall was not found — it may have been deleted.',
    // WEB-3399: свои стили и палитры организации.
    ORG_STYLE_LIMIT_REACHED:
      'The organization already has 50 own styles. Delete one you no longer need to save a new one.',
    ORG_PALETTE_LIMIT_REACHED:
      'The organization already has 50 own palettes. Delete one you no longer need to save a new one.',
    ORG_DESIGN_INVALID:
      'Not saved: the style or palette data did not pass the check. Reload the page and try again.',
    ORG_DESIGN_MANAGE_FORBIDDEN:
      'Only the author, the organization owner or an admin can rename or delete this.',
    ORG_DESIGN_SAVE_FORBIDDEN:
      'Only people who can edit funnels can save styles',
    ORG_DESIGN_NOT_FOUND:
      'Style or palette not found — it may have been deleted already.',
    CUSTOM_STYLE_PACK_INVALID:
      "The funnel's own style was not saved: the data did not pass the check. Reload the page and try again.",
    /**
     * П4-1 (свежий взгляд пачки 37): у гонки ПУБЛИКАЦИИ свой совет. Сосед
     * выше — отказ ЗАПИСИ: правка не принята, редактор надо обновить. Здесь
     * наоборот: правка уже в черновике, достаточно нажать «Опубликовать» ещё
     * раз. Пока сервер отвечал чужим кодом, английский кабинет печатал по
     * нему неверный совет.
     */
    DRAFT_CHANGED_DURING_PUBLISH:
      'The page changed while publishing — your changes are saved, press Publish again',
    DRAFT_CONTENT_NOT_ACCEPTED:
      'Saving accepts the content field — that is what goes into the draft.',
    // WEB-2557: общая фраза кода (особая «не список экранов» — funnelContentCorrupted).
    INVALID_FUNNEL_CONTENT:
      'The server did not accept the funnel content — check the screens and blocks',
    PIECE_IN_USE: 'This piece is used by other funnels.',
    PIECE_SHARED:
      'One or more pieces are shared with other funnels in this project.',
    SOURCE_LANGUAGE_HAS_TRANSLATION:
      'A translation into this language is already ordered — it cannot be made the source language.',
    TRANSLATION_UNAVAILABLE:
      'Translation is unavailable right now: the AI service is not configured on the server.',
    PRICE_LEVEL_MAP_INCOMPLETE:
      'Some paywall prices have no level set — fill them in before publishing.',
    RETURN_URL_INVALID: 'Invalid return URL.',
    PRICE_NOT_ON_PAYWALL:
      'This price is not available for payment on this page.',
    PRICE_GONE:
      'This price is no longer available. Refresh the page or contact the seller.',
    CHECKOUT_START_FAILED:
      'Could not start the payment. Try again or contact the seller.',
    INSTRUCTION_TOO_LONG:
      'The instruction for the AI is too long. The maximum is 8000 characters. Shorten the text or split the edit into several turns.',
    REFERENCE_REJECTED: 'The attached reference was rejected.',
    // WEB-2461 (слита WEB-2418): причины отказа нарезки образца. Места `{имя}`
    // заполняются подробностями отказа (`details`) — `utils/localizeError.ts`.
    REFERENCE_REJECTED_SERVER_BUSY:
      'The server is busy processing other files — "{fileName}" could not be taken on. Send it again in a minute.',
    REFERENCE_REJECTED_FILE_TOO_LARGE:
      'The file "{fileName}" is larger than {maxMb} MB — attach a smaller file or split the PDF into parts.',
    REFERENCE_REJECTED_IMAGE_PROCESSING_UNAVAILABLE:
      'Image processing is unavailable on the server — please contact support.',
    REFERENCE_REJECTED_IMAGE_UNREADABLE:
      'Could not read "{fileName}" as an image — is the file damaged?',
    REFERENCE_REJECTED_PDF_UNREADABLE:
      'Could not read "{fileName}" as a PDF — is the file damaged?',
    REFERENCE_REJECTED_TIMEOUT:
      'Could not process "{fileName}" in the time allowed (page {page}) — the file is too heavy. Send the pages you need as a separate file, or the screens as images.',
    REFERENCE_REJECTED_NO_PAGES: 'No pages were found in "{fileName}".',
    REFERENCE_REJECTED_TOO_MANY_PAGES:
      '"{fileName}" has {pages} pages — the limit is {maxPages}. Attach the pages you need as a separate file.',
    REFERENCE_REJECTED_PAGE_RENDER_FAILED:
      'Could not render page {page} of "{fileName}" — re-save the PDF or send the screens as images.',
    REFERENCE_REJECTED_NO_SCREEN_FRAMES:
      'No screen frames were found on the board in "{fileName}" — send screenshots of the screens as images.',
    REFERENCE_REJECTED_UNSUPPORTED_FORMAT:
      'The format "{mimeType}" is not supported — attach a PNG, JPG, WebP or PDF.',
    REFERENCE_REJECTED_SLICE_BUDGET_EXHAUSTED:
      'There are already {count} slices with a limit of {maxSlices} — "{fileName}" will not fit; remove it or send a smaller board.',
    REFERENCE_REJECTED_TOO_MANY_SLICES:
      'The attached files produced {count} slices with a limit of {maxSlices} per turn — remove one of the files.',
    // WEB-2861: картинка-образец больше предела пикселей нарезки.
    REFERENCE_REJECTED_TOO_MANY_PIXELS:
      'The image "{fileName}" is too large: more than {maxMegapixels} megapixels. Downscale it or cut it into parts and attach it again.',
    // WEB-2880: длинная картинка-образец — частей высотой в экран больше потолка.
    REFERENCE_REJECTED_IMAGE_TOO_LONG:
      'The image "{fileName}" is too long: it would make {count} screen-height parts with a limit of {maxSlices} per turn. Crop it or split it across several turns.',
    REFERENCE_SLICING_FAILED:
      'Could not process the attached reference — try again or send a different file.',
    TURN_IN_FLIGHT:
      'A turn is already running in this chat — wait until it finishes.',
    // WEB-3744: ход в другом разговоре той же части воронки.
    FUNNEL_TURN_IN_FLIGHT:
      'A turn is already running in another chat of this funnel — wait until it finishes.',
    // WEB-3744, юнит «предложения»: «Принять всё», «Отменить» и откат
    // хода-предложения.
    PROPOSAL_STALE: 'The draft changed after this proposal — ask the AI again.',
    PROPOSAL_NOT_PENDING:
      'This proposal has already been accepted or discarded.',
    PROPOSAL_NOT_APPLIED:
      'This proposal was never applied — there is nothing to undo.',
    // Возврат 1 (ответ 52): ход-предложение без номера команды кабинета.
    PROPOSAL_REQUEST_ID_REQUIRED:
      'The proposal was not sent: the request has no command id. Reload the page and try again.',
    // WEB-3744, юнит «поэкранно»: «принять» с выбором экранов.
    PROPOSAL_SCREEN_UNKNOWN: 'This proposal has no such screen.',
    PROPOSAL_NOTHING_SELECTED:
      'No screens are selected — there is nothing to accept.',
    PROPOSAL_LANGUAGE_WHOLE_ONLY:
      'This proposal changes the funnel language — it can only be accepted as a whole.',
    TURN_FAILED_INTERNAL: 'Internal server error while running the turn',
    TURN_QUEUE_UNAVAILABLE:
      'The AI turn queue is unavailable — try again in a minute.',
    TURN_STREAM_INTERRUPTED:
      'The live connection to this turn dropped — the turn itself keeps running. Reload the page to see the result.',
    STREAM_TICKET_UNAVAILABLE:
      'The live connection dropped — generation keeps running on the server, and the result will appear here on its own.',
    TURN_PROCESS_DIED:
      'The turn could not be completed — the server restarted. The draft is unchanged and the turn itself was not billed.',
    TURN_PROCESS_DIED_IMAGE_REFUNDED:
      'The turn could not be completed — the server restarted. The charge for this turn’s picture has been refunded.',
    TURN_PROCESS_DIED_IMAGE_REFUND_PENDING:
      'The turn could not be completed — the server restarted. The charge for this turn’s picture could not be refunded automatically; we are on it.',
    REVERT_NO_CONTENT_SNAPSHOT:
      'This turn cannot be undone: the thread’s saved states hold no content snapshot.',
    REVERT_NO_LOGIC_SNAPSHOT:
      'This turn cannot be undone: the thread’s saved states hold no logic snapshot.',
    PREVIEW_UNPARSEABLE: 'Could not parse the app page at the given URL.',
    REPORT_TOO_SLOW:
      'This report takes too long to calculate for such a period — narrow the period and try again.',
    // WEB-2636: оборванный пределом GET, который не отчёт (загрузка редактора,
    // список воронок, настройки) — периода нет, «сузьте период» не к месту.
    READ_REQUEST_TOO_SLOW:
      'The server did not respond in time — please try again.',
    DATABASE_QUERY_TOO_SLOW:
      'The database did not finish this action in time — please try again in a minute.',
    // WEB-2374 п.13 / WEB-2380 Д1: брошенная перерисовка всё равно
    // дорисовывается и ложится в медиа-хаб подарком — человек должен это
    // знать, иначе заплатит за ту же картинку второй раз.
    IMAGE_GENERATION_TIMEOUT:
      'The image was not generated in time — the request timed out. Please try again: if the image is finished anyway, it will show up in your media hub.',
    // WEB-2881: перерисовка без картинки по причине, отличной от срока.
    IMAGE_GENERATION_FAILED:
      'The image could not be generated — the image service returned no result. No credits were charged for the image; please try again.',
    IMAGE_GENERATION_UNAVAILABLE:
      'Could not start the redraw — the service is temporarily unavailable. Please try again.',
    FILE_NOT_PROVIDED: 'No file was provided',
    FILE_EMPTY: 'The file is empty',
    FILE_TOO_LARGE: 'The file is too large',
    IMAGE_FORMAT_UNSUPPORTED:
      'Unsupported image format. JPEG, PNG, WebP, GIF and AVIF are allowed',
    FONT_FORMAT_UNSUPPORTED:
      'This is not a font file. WOFF2, WOFF, TTF and OTF are allowed',
    IMAGE_PROCESSING_BUSY:
      'The server is busy processing other large images. Try again in a minute',
    IMAGE_TOO_MANY_PIXELS:
      'The image is too large: more than 268 megapixels. Downscale it and upload again',
    IMAGE_HIGH_BIT_DEPTH_TOO_LARGE:
      'The image has more than 8 bits per channel and is too large: more than 100 megapixels. Save it with 8 bits per channel (a regular PNG or JPEG) or downscale it and upload again',
    IMAGE_ALPHA_TOO_LARGE:
      'The image with transparency is too large: more than 100 megapixels. Downscale it or save it without transparency (for example, as JPEG) and upload again',
    SKELETON_DESCRIPTIONS_DISABLED:
      'Reference content descriptions are disabled on the server.',
    CAPI_ACCESS_TOKEN_REQUIRED:
      'Connecting a network for the first time needs an access token — without it there is nothing to send events with.',
    CAPI_TEST_EVENT_NO_CONNECTION:
      'The network is not connected — there is nowhere to send a test event.',
    CAPI_PIXEL_ID_INVALID_META:
      'This does not look like a Meta pixel id: in Events Manager → Data sources copy the dataset ID — digits only.',
    CAPI_PIXEL_ID_INVALID_TIKTOK:
      'This does not look like a TikTok pixel id: in Events Manager copy the pixel ID — Latin letters and digits only, no spaces or punctuation.',
    CAPI_EVENT_MAPPING_UNKNOWN_EVENT:
      'The mapping contains an event the service does not know. Pick one of our events from the list.',
    CAPI_EVENT_NAME_PURCHASE_FORBIDDEN:
      'A purchase name (Purchase, CompletePayment) can only be given to a payment event. Otherwise the network counts a purchase that never happened, and the "Only a person’s first payment" setting suppresses the real one.',
    CAPI_EVENT_NAME_SHARED_WITH_PURCHASE:
      'This name is already used by a payment event — the network would count an event without a payment under the same name as a purchase, and purchase numbers in the ad account would be wrong. Give the events different names.',
    CAPI_EVENT_NAME_CUSTOM_INVALID:
      'A custom event name is up to 50 characters: Latin letters, digits, "_" and "-" (Meta also allows spaces). For TikTok the name must start with a letter.',
    SEQUENCE_TRIGGER_UNSUPPORTED:
      'This trigger is not supported by the server yet — the sequence would never run. Pick another trigger.',
    SEQUENCE_HAS_NO_STEPS:
      'The sequence has no steps — add one before sending a test email.',
    SEQUENCE_TEST_SEND_SUPPRESSED:
      'This address has unsubscribed — a test email will not be sent to it, just as a real one would not. Use a different address.',
    UNSUBSCRIBE_FAILED:
      "We couldn't process the unsubscribe — please try again later or contact support.",
    SEQUENCE_LOG_NOT_FOUND:
      'That entry in the email log was not found — it may already have been dropped by the retention policy.',
    SEQUENCE_RESEND_SUPPRESSED:
      'This address has unsubscribed — the email cannot be sent again.',
    SEQUENCE_NOT_FOUND:
      'Sequence not found — it may have been deleted. Refresh the page.',
    SEQUENCE_STEP_NOT_FOUND:
      'Step not found — it may have been deleted. Refresh the page.',
    SEQUENCE_WRONG_PROJECT: 'This sequence does not belong to your project',
    ANALYTICS_EVENT_FILTER_REQUIRED:
      'An analytics-event subscription needs a non-empty list of event types — otherwise it would never fire.',
    FUNNEL_SLUG_RESERVED:
      'This address is taken by a service page — a funnel at that address would never open. Rename the funnel.',
    DUPLICATE_NAME: 'Something with this name already exists in this project.',
    QUIZ_WRAPPER_FUNNEL_FAILED:
      'Could not create the internal funnel for this quiz. Try renaming the quiz and saving again.',
    SMART_LINK_QUIZ_ONLY_SHAPE:
      'A quiz direction must have exactly one quiz selected — no paywall and no funnel.',
    SMART_LINK_QUIZ_NOT_IN_PROJECT:
      'This quiz does not belong to the project — pick a quiz from the project list.',
    SMART_LINK_TOO_MANY_DIRECTIONS:
      'An A/B test can have at most 8 directions. Remove the extra ones and save again.',
    // WEB-3742: «Отдать весь трафик победителю»
    SMART_LINK_FINISH_DRAFT:
      'A draft cannot be finished: the test has not split any traffic yet. Publish it or delete it.',
    SMART_LINK_WINNER_NOT_IN_TEST:
      'This route is not part of this A/B test. Refresh the page and pick the winner again.',
    SMART_LINK_ALREADY_FINISHED:
      'This A/B test is already finished with a different winner.',
    // WEB-3742: запреты завершённому тесту
    SMART_LINK_FINISHED_DIRECTIONS_LOCKED:
      'The test is finished: routes and weights can no longer change. To try another split, run the test again (Run again).',
    SMART_LINK_FINISHED_MOVE_FORBIDDEN:
      'A finished test cannot be moved to another project: all traffic already goes to the winner.',
    ORG_ROLE_REQUIRED:
      'You are not allowed to create projects in this organization',
    ORG_LIMIT_REACHED:
      'You have reached the per-person limit on organizations. To create a new one, close an unneeded organization in its settings, “Danger zone”.',
    ORG_LIMIT_REACHED_ON_TRANSFER:
      'Transfer is not possible: the recipient is already at the organization limit. It will go through once they close an unneeded one.',
    AI_PROVIDER_UNAVAILABLE:
      'The AI service is temporarily unavailable — try again in a few minutes.',
    AI_REQUEST_REJECTED: 'The AI service rejected the request.',
    AI_REQUEST_REFUSED:
      'The model declined this request under the provider usage rules. Retrying will not help — rephrase the topic or the brief.',
    AI_REQUEST_TOO_LARGE:
      'The request is too large for AI generation. Send a shorter text.',
    AI_GENERATION_BASE_NOT_FOUND:
      'The generation base was not found in this project. It may have been deleted — pick another one.',
    AI_GENERATION_BASE_KIND_MISMATCH:
      'This base does not fit the selected generation type: a quiz is built from a quiz or a funnel, a paywall from a paywall or a funnel that has one.',
    // WEB-4229: у проекта со своими платежами студия создаёт только квиз.
    AI_GENERATION_OWN_PAYMENTS_QUIZ_ONLY:
      'This project takes payments on its own side, so the AI studio creates quizzes only. Choose “Quiz” and start the generation again.',
    AI_GENERATION_BASE_FUNNEL_NO_QUIZ:
      'This funnel has no quiz, so there is nothing to build from. Add a quiz to the funnel or pick another base.',
    AI_GENERATION_BASE_FUNNEL_NO_PAYWALL:
      'This funnel has no paywall, so there is nothing to build from. Add a paywall to the funnel or pick another base.',
    AI_GENERATION_BASE_UNSUPPORTED:
      'This blueprint from a link is not ready yet or could not be parsed — pick a ready one.',
    AI_GENERATION_BASE_AMBIGUOUS:
      'The request names two different bases. Pick one.',
    AI_GENERATION_BASE_OWN_NOT_SUPPORTED:
      "Generating from your own quiz, paywall or funnel isn't available yet — pick a template.",
    // WEB-4044: своя основа больше предела (до резерва кредитов).
    AI_GENERATION_BASE_TOO_LARGE:
      'This base is too large: up to 40 quiz screens, up to 15 paywall screens and up to 60,000 characters of text. Pick a smaller base.',
    // WEB-4044 (возврат 2): основа изменилась, пока задание ждало очереди.
    AI_GENERATION_BASE_CHANGED:
      'The base changed while the generation was waiting in line — please run it again.',
    // WEB-2401: образец хода ИИ-чата (своя сущность проекта или каркас по ссылке).
    EDIT_REFERENCE_NOT_FOUND:
      'The sample was not found in this project. It may have been deleted — pick another sample.',
    EDIT_REFERENCE_BLUEPRINT_NOT_READY:
      'This blueprint from a link is not ready yet or could not be parsed — pick a ready blueprint as the sample.',
    EDIT_REFERENCE_NO_QUIZ:
      'This sample has no quiz, so it cannot be used as a sample. Pick another one.',
    EDIT_REFERENCE_TOO_LONG:
      'The sample is too large for the chat: more than {max} characters. Pick a smaller sample.',
    EDIT_REFERENCE_INVALID:
      'The sample is specified incorrectly: both its type and id are required.',
    // WEB-2397: пачка вариантов генерации.
    AI_GENERATION_BATCH_EMPTY: 'The batch has no variants. Add at least one.',
    AI_GENERATION_BATCH_TOO_MANY_VARIANTS:
      'That many variants cannot be started at once. Reduce the number of variants in the batch.',
    AI_GENERATION_BATCH_THREADS_OUT_OF_RANGE:
      'That number of parallel threads is not available: choose from one up to your organization limit.',
    AI_GENERATION_BATCH_REQUEST_ID_TAKEN:
      'This launch is already registered to another project member. Press "Generate" again.',
    AI_GENERATION_BATCH_LAUNCH_FAILED:
      'This variant batch failed to launch. Press "Generate" again.',
    AI_GENERATION_BATCH_NOT_FOUND:
      'The generation batch was not found in this project.',
    AI_GENERATION_BATCH_VARIANT_WITHDRAWN:
      'The variant was withdrawn: the batch was stopped before it started. No credits were charged for it.',
    // WEB-2806: лимит трат организации на ИИ / подтверждение крупного запуска.
    AI_SPEND_LIMIT_DAILY_EXCEEDED:
      "This launch would take the organization's AI spending over its daily limit. No credits were charged. Wait for the next day or ask the organization owner to raise the limit.",
    AI_SPEND_LIMIT_MONTHLY_EXCEEDED:
      "This launch would take the organization's AI spending over its monthly limit. No credits were charged. Wait for the next month or ask the organization owner to raise the limit.",
    AI_LARGE_LAUNCH_CONFIRMATION_REQUIRED:
      'This is a large launch — confirm the amount before launching. No credits were charged.',
    AI_SPEND_LIMIT_BILLING_ACCESS_REQUIRED:
      'Only someone with billing access to the organization can change the AI spending limit. The organization owner grants that access.',
    // WEB-2803: ответ на вопрос генерации.
    AI_AUTHOR_QUESTION_NOT_FOUND: 'The generation question was not found.',
    AI_AUTHOR_QUESTION_CLOSED:
      'This question can no longer be answered: the time ran out or it was already answered. The generation continues with the chosen option.',
    AI_AUTHOR_QUESTION_GENERATION_STOPPED:
      'This question can no longer be answered: the generation was stopped.',
    AI_AUTHOR_QUESTION_UNKNOWN_OPTION:
      'This question has no such answer option. Choose one of the options offered.',
    AI_MODEL_UNAVAILABLE:
      'This AI model is not available right now. Choose another model.',
    AI_MODEL_NOT_ALLOWED_FOR_ACTION:
      'This AI model cannot be used for this action. Choose another model.',
    TRANSLATION_CONTENT_CHANGED:
      'The translation changed while this editor was open: the system filled in the missing strings. Refresh the window so the newer translation is not lost — your edits are kept.',
    LINK_PARSE_LINK_INVALID:
      'This is not a link to a web page. Paste an address that starts with http:// or https://. Credits were not charged.',
    LINK_PARSE_LINK_UNAVAILABLE:
      'Could not open the page at this link: it does not respond, is closed, or does not lead to a funnel. Check the link in a browser. Credits were not charged.',
    LINK_PARSE_BOT_PROTECTION:
      'The page at this link is protected against bots (for example, with a captcha) — our reader cannot get past it. Try another link to this funnel. Credits were not charged.',
    LINK_PARSE_LOGIN_REQUIRED:
      'The funnel at this link asks you to sign in — our reader cannot get past a sign-in. Give a link to a part of the funnel that opens without signing in. Credits were not charged.',
    LINK_PARSE_EMAIL_CODE_TIMEOUT:
      'The funnel at this link asked for a code from an email, but the email with the code did not arrive in time. Try again later or give another link to this funnel. Credits were not charged.',
    LINK_PARSE_UNKNOWN_PLATFORM:
      'We cannot read funnels built on this platform yet. Credits were not charged.',
    LINK_PARSE_PARSE_FAILED:
      'Could not read the funnel on this page. Credits were not charged.',
    LINK_PARSE_PAGE_TOO_LARGE:
      'The page at this link is too large to read. Credits were not charged.',
    LINK_PARSE_UNAVAILABLE:
      'Reading funnels by link is temporarily unavailable. Try again in a few minutes. Credits were not charged.',
    LINK_PARSE_PAID_ACCESS_REQUIRED:
      'Link parsing is available on paid plans and during the trial.',
    LINK_PARSE_TOO_FREQUENT:
      'This site is being read too often right now. Try again in a minute.',
    LINK_PARSE_NOT_FOUND: 'This link reading was not found in the project.',
    PARSED_FUNNEL_NOT_READY:
      'This link reading has not finished or has failed — a funnel cannot be created from it. Wait until the reading finishes or read the link again.',
    PARSED_FUNNEL_OLD_PATH:
      'This link reading was made the old way: it has no ready funnel. Read the link again to get a ready funnel.',
    PARSED_FUNNEL_FORM_UNSUPPORTED:
      'The ready funnel in this link reading is stored in an outdated form. Read the link again.',
    AI_ASK_IN_FLIGHT:
      'The answer to this question is already on its way — give it a few seconds.',
    AI_ASK_TIMEOUT:
      'The AI ran out of time to answer. No credits were charged — ask again or keep it shorter.',
    AI_ASK_NO_ANSWER:
      "The AI couldn't answer from your data. No credits were charged — try rephrasing the question.",
    AI_ASK_REQUEST_CLOSED:
      'This request is already closed. Ask the question again — it will go as a new request.',
    // WEB-3820 юнит 2: план «Спросить ИИ» (без сумм).
    AI_ASK_NO_EDIT_RIGHT:
      'You can’t edit this funnel. Project members edit their own funnels; the owner and admins can edit any. Anyone can ask about the data.',
    AI_ASK_PLAN_DAILY_CAP:
      'Your organization has used today’s free AI plans ({limit} a day). Try again tomorrow — days are counted in UTC.',
    AI_ASK_PLAN_UNAVAILABLE:
      'AI plans are temporarily unavailable. Try again in a minute.',
    AI_ASK_PLAN_FAILED:
      'The AI couldn’t put a plan together. Try rephrasing the task.',
    AI_ASK_TARGET_NOT_FOUND: 'This funnel was not found in the project.',
    FUNNEL_IMAGES_NOTHING_TO_DO:
      'There is nothing to draw — no screen has an image slot with a description.',
    FUNNEL_IMAGES_RUN_NOT_FOUND: 'This image run was not found in the funnel.',
    FUNNEL_IMAGES_SLOT_FAILED:
      'The image couldn’t be drawn — nothing was charged for it. Redraw it on the screen.',
    FUNNEL_IMAGES_MODEL_GONE:
      'The image model this run was started with is no longer available. This spot was not drawn and you were not charged — start the images again.',
    FUNNEL_IMAGES_RUN_STARTING:
      'Images for this funnel are already starting. Wait a few seconds and refresh.',
    TRANSLATION_NOTHING_TO_TRANSLATE:
      'There is nothing to translate — there are no screens with text yet. Add screens with text and order the translation again.',
    TRANSLATION_BATCH_INSUFFICIENT_BALANCE:
      'There are not enough credits to translate all the selected languages. Top up the wallet or select fewer languages — nothing was started or reserved.',
    TRANSLATION_BATCH_TOO_MANY_LANGUAGES:
      'More languages are selected than there are in the list. Remove the extra ones and send again.',
    TRANSLATION_BATCH_INVALID_LANGUAGES:
      'The language list was not accepted: languages repeat, or it includes the original language or a language that is not in the list. Fix the selection and send again.',
    TRANSLATION_BATCH_FAILED:
      'The translation into this language failed. No credits were charged for it — try again.',
    TRANSLATION_BATCH_TIMED_OUT:
      'The translation into this language waited in the queue for too long and was cancelled. No credits were charged for it — try again.',
    TRANSLATION_BATCH_LANGUAGE_DELETED:
      'The translation into this language was cancelled: the language was deleted while it was being translated. No credits were charged for it.',
    TRANSLATION_LANGUAGE_DELETED:
      'The translation was cancelled: the language was deleted while it was being translated. No credits were charged for it.',
    REGISTRAR_UNAVAILABLE:
      'The domain registrar is unavailable right now. No money was taken; try again later.',
    DOMAIN_AVAILABILITY_UNAVAILABLE:
      'Domain lookup is temporarily unavailable; please try again later.',
    DOMAIN_PURCHASE_FAILED: 'The domain purchase failed; no money was taken.',
    DOMAIN_RENEWAL_FAILED: 'The domain renewal failed; no money was taken.',
    // WEB-2957: отказы продления домена (итог одиночного и строка пачки).
    DOMAIN_RENEW_DUPLICATE:
      'This domain is already being renewed by the same request — no second charge was made.',
    DOMAIN_RENEW_RETRY_LIMIT:
      'Too many renewal attempts for this domain in a row — try again later or contact support.',
    DOMAIN_RENEW_FORCE_TOO_SOON:
      'The previous renewal attempt was made just now and may still be in progress. Charging again now could pay for a second year that cannot be refunded — wait about 15 minutes and try again.',
    DOMAIN_RENEWED_NOT_PERSISTED:
      'The domain has been renewed at the registrar; a manual reconciliation is required.',
    DOMAIN_RENEW_OUTCOME_UNKNOWN:
      'The registrar did not answer in time, so it is not yet known whether the domain was renewed. We will check this ourselves — there is no need to pay again: if the renewal did not go through, the money will be returned automatically.',
    // WEB-4070: массовое продление — остатка организации меньше суммы пачки.
    DOMAIN_RENEW_INSUFFICIENT_BALANCE:
      'There are not enough credits in the wallet to renew the selected domains — nothing was charged. Top up the wallet and try again.',
    stripe_connect_mode_key_mismatch:
      'The keys come from a different Stripe environment than the row you are connecting. Take the keys from the matching environment, or connect them in the other row.',
    STRIPE_LIVE_KEYS_NOT_CONNECTED:
      'Live mode cannot be turned on: the project has no live Stripe keys connected.',
    STRIPE_TEST_KEYS_NOT_CONNECTED:
      'Test mode cannot be turned on: the project has no test Stripe keys connected.',
    STRIPE_LIVE_MODE_WITHOUT_KEYS:
      'The project is switched to Stripe live mode, but no live keys are connected.',
    STRIPE_TEST_MODE_WITHOUT_KEYS:
      'The project is switched to Stripe test mode, but no test keys are connected.',
    STRIPE_SUBSCRIPTION_MODE_KEYS_MISSING:
      'The subscription lives in its own Stripe environment, and those keys are not connected to the project — it cannot be canceled or changed.',
    STRIPE_SUBSCRIPTION_MODE_UNKNOWN:
      'This subscription has no recorded Stripe environment (live or test) — it cannot be canceled or changed until this is resolved manually.',
    // Ревизия пачки 46, Н2 (деньги): предупреждения подключения Stripe. Пара
    // с реестром `backend/src/shared/errors/human-error-codes.ts` — сверяется
    // дословно сторожем `human-error-codes.guard.spec.ts` в обе стороны.
    STRIPE_ACCOUNT_SHARED_WARNING:
      'This Stripe account is already connected to another project. Subscriptions, emails and customer revenue may get mixed up between projects.',
    STRIPE_ACCOUNT_NOT_READ_LABEL_DROPPED:
      'The Stripe account could not be read with the new keys (the key is restricted, or Stripe did not respond), so we did not keep the previous account on this project: otherwise the price catalog would hide the new account’s prices. The catalog will be re-synced.',
    STRIPE_KEY_SET_NOT_CONNECTED:
      'This Stripe key set is not connected to the project — there is nothing to disconnect.',
    STRIPE_KEY_SET_WEBHOOK_REMOVE_FAILED:
      'The webhook of the environment being disconnected could not be removed in Stripe — the keys were left in place, please try again.',
    PAYMENTS_UNAVAILABLE:
      'Payment is unavailable right now. Try again later or contact the seller.',
    VISITOR_ID_REQUIRED:
      'Could not start the payment. Nothing was charged — contact the app’s support.',
    VISITOR_ID_FOREIGN_PROJECT:
      'Could not start the payment. No money was taken — contact the app’s support.',
    consent_denied:
      'The visitor has explicitly declined tracking — the property was not stored.',
    reserved_property:
      'This property is reserved and can only be set by the platform.',
    no_payment_to_refund:
      'No successful charge was found for the given payment to refund.',
    not_found: 'Resource not found.',
    QA_EVENT_STATUS_REQUIRED:
      'The status field is required for a customer.subscription.updated event',
    // Сообщения проверок формы (WEB-1904): их отдаёт class-validator/zod, код
    // правила ставит рядом с текстом серверный CodedValidationPipe.
    VALIDATION_ADJUSTMENT_AMOUNT_INT:
      'The adjustment amount must be a whole number of cents',
    VALIDATION_ADJUSTMENT_AMOUNT_NOT_ZERO:
      'The adjustment amount cannot be zero',
    VALIDATION_ADJUSTMENT_AMOUNT_MIN:
      'This debit is larger than the maximum allowed for a single adjustment',
    VALIDATION_ADJUSTMENT_AMOUNT_MAX:
      'This credit is larger than the maximum allowed for a single adjustment',
    VALIDATION_UNFREEZE_REASON_LENGTH:
      'The reason must be 10 to 500 characters — the organization owner will read it',
    VALIDATION_UNFREEZE_WRITE_OFF_DECISION:
      'The write-off mark must be write_off_done (debit done) or write_off_not_required (no debit needed)',
    VALIDATION_FORCE_RENEW_CONFIRM_REQUIRED:
      'A deliberate renewal retry needs an explicit confirmation: confirmNewCharge must be true',
    VALIDATION_BUNDLE_ID_FORMAT:
      'bundleId: letters, digits, dot, hyphen and underscore only — for example com.my-app.ios',
    VALIDATION_RETURN_TARGET_KIND:
      'Return target: only “app or app store”, “custom address” and “stay on the last screen” are allowed',
    VALIDATION_RETURN_TARGET_URL_REQUIRED:
      'A custom return address is selected — fill the address in or choose another target',
    VALIDATION_RETURN_TARGET_URL_SCHEME:
      'The return address must start with http:// or https:// — other schemes (javascript:, data:, file:) and relative paths are not allowed',
    VALIDATION_RETURN_TARGET_URL_MACRO:
      'Label substitution such as {utm_source} is not supported in the return address — use a plain address',
    VALIDATION_RETURN_TARGET_URL_LENGTH:
      'The return address is longer than 1000 characters',
    VALIDATION_STORE_URL_SCHEME:
      'The store link must start with http:// or https:// — other schemes (javascript:, data:, file:) are not allowed',
    VALIDATION_MMP_BASE_URL_SCHEME:
      'The tracker link must start with http:// or https:// — other schemes (javascript:, data:, file:) are not allowed',
    VALIDATION_IOS_APP_ID_FORMAT:
      'iOS App ID: TeamID.BundleID format (for example ABCDE12345.com.example.app), no spaces and no "://"',
    VALIDATION_ANDROID_PACKAGE_FORMAT:
      'Android package: reverse-DNS (for example com.example.app)',
    VALIDATION_SHA256_FINGERPRINT_FORMAT:
      'SHA-256: 32 hex bytes separated by colons (AA:BB:…) or 64 hex characters',
    VALIDATION_EVENT_SESSION_ID_LENGTH: 'sessionId: 8 to 64 characters',
    VALIDATION_EVENT_SCREEN_ID_LENGTH: 'screenId: at most 128 characters',
    VALIDATION_MMP_BRANCH_UNSUPPORTED:
      'Branch is not supported yet — choose AppsFlyer or Adjust',
    VALIDATION_MMP_PAIR_INCOMPLETE:
      'Fill in the other half of the pair: the MMP link has no tracker selected — the install link cannot be built',
    VALIDATION_MMP_BASE_URL_HOST_MISMATCH:
      'The link does not match the selected tracker',
    // WEB-2854: отказ по длине идентификатора посетителя у публичных ручек.
    VALIDATION_PUBLIC_GUID_LENGTH:
      'The visitor identifier (guid) must be 8 to 64 characters long. The app sent a value of a different length — use the same identifier both at checkout and when linking the buyer to the platform profile.',
    REDIRECT_ENDING_NO_STORE_URL:
      'The redirect ending sends the visitor to an app store, but the project settings have no link at all — neither App Store nor Google Play. Fill in at least one in the project settings and start the generation again.',
    // WEB-1992 — отказы ручки приёма замеров скорости `POST /public/vitals`.
    VALIDATION_VITALS_METRIC_UNKNOWN:
      'Unknown speed metric: only LCP, INP, CLS, TTFB and FCP are accepted',
    VALIDATION_VITALS_BATCH_TOO_LARGE:
      'Too many metrics in a single measurement batch',
    // WEB-2114 — ссылки предпросмотра (кабинет).
    PREVIEW_LINKS_DISABLED:
      'Preview links are turned off for now. Try again later or contact support.',
    PREVIEW_TTL_OUT_OF_RANGE:
      'A link can live from 1 to 48 hours. Enter a value within that range.',
    PREVIEW_TARGET_NOT_FOUND:
      'The funnel, quiz or paywall to preview was not found in this project.',
    FUNNEL_BROKEN:
      'The funnel is broken: its quiz or paywall was deleted or is unavailable. Fix the funnel in the editor and try again.',
    EMPTY_FUNNEL:
      'There is nothing to show in this funnel: both the quiz and the paywall are empty. Add at least one screen with content.',
    PREVIEW_LINK_NOT_FOUND: 'Preview link not found.',
    PREVIEW_PAYWALL_NOT_REVIEWED:
      "First send this funnel's payment screen (paywall) for review — the owner or anyone with access to this paywall can do it.",
    PREVIEW_QUIZ_NOT_REVIEWED:
      "First send this funnel's quiz for review — the owner or anyone with access to this quiz can do it.",
    // WEB-2791: кусок заперт понижением тарифа — совет про тариф.
    PREVIEW_PAYWALL_READONLY_NOT_REVIEWED:
      "This funnel's payment screen (paywall) is read-only after the plan downgrade and has never been sent for review. You can send it for review after upgrading the plan.",
    PREVIEW_QUIZ_READONLY_NOT_REVIEWED:
      "This funnel's quiz is read-only after the plan downgrade and has never been sent for review. You can send it for review after upgrading the plan.",
    // WEB-2114 — публичная дверь предпросмотра (получатель ссылки).
    PREVIEW_UNAVAILABLE: 'This link is unavailable.',
    PREVIEW_CODE_INVALID: 'The code is incorrect.',
    PREVIEW_ATTEMPTS_EXHAUSTED:
      'No code attempts left. Ask the author for a new link.',
    // WEB-2119 — внешняя аналитика клиента (Amplitude и её будущие соседи).
    EXTERNAL_ANALYTICS_CONNECTION_NOT_FOUND:
      'External analytics connection not found.',
    EXTERNAL_ANALYTICS_API_KEY_REQUIRED:
      'Paste the project API key — the analytics connection cannot be created without it.',
    EXTERNAL_ANALYTICS_KEY_OR_REGION_INVALID:
      'The analytics tool rejected the key. Check both fields at once: the project API key and the region (US or EU) — the response is identical for both cases, so they cannot be told apart.',
    EXTERNAL_ANALYTICS_PROVIDER_PAUSED:
      'Delivery to this analytics tool is currently paused by us for every project. The test event was not sent: there is nothing to fix on your side. Live events are kept for up to 24 hours and will go out once delivery resumes.',
    EXTERNAL_ANALYTICS_EVENT_RENAME_FORBIDDEN:
      'Event names are fixed when you connect and cannot be renamed: renaming would break your history and saved charts in the analytics tool. Adding new events is fine.',
    OUTBOUND_URL_MACRO_FORBIDDEN:
      'The address contains a label with personal data \u2014 email, phone, name, date of birth, address and free-text answers are never passed out. Allowed labels: campaign ({utm_source}, {utm_medium}, {utm_campaign}, {utm_content}, {utm_term}), ad click ({click_id}), visitor id ({visitor_id}) and a question answer ({answer.<question id>}) \u2014 the last one only for questions with the \u201cpass the answer to the outgoing address\u201d checkbox on.',
    BLOCK_MIN_ABOVE_MAX:
      'A block has its minimum value above its maximum — a visitor would not be able to answer that question. Swap the numbers or fix one of them.',
    FUNNEL_TOO_MANY_SCREENS:
      'The funnel has more than 150 screens — that many cannot be saved. Remove extra screens or split the funnel in two.',
    SMARTLINK_EXTERNAL_URL_OWN_HOST:
      'A direction’s external address cannot point at one of our own domains — that would build a redirect chain, and ad networks penalise the domain it starts from. Use the address of your own page.',
    HANDOFF_BASE_DOMAIN_NOT_ALLOWED:
      'That handoff base domain is not allowed: only the main handoff base domain or one of the legacy ones will work — on any other domain the server does not serve the Universal Links / App Links files, so the link would not open the app.',
    VITALS_PAYLOAD_TOO_LARGE: 'The speed measurement payload is too large',
    REQUEST_BODY_TOO_LARGE:
      'The request body is larger than this address accepts',
    ORIGIN_NOT_VERIFIED:
      'The request did not come through the protected service entry point. Use your project or dashboard address',
    ANALYTICS_TIMEZONE_INVALID:
      'Unknown report time zone — use an IANA time zone, for example Europe/Moscow',
    ANALYTICS_ANSWER_FILTER_INVALID:
      'The answer filter is malformed: expected “question:option”.',
    ANALYTICS_HOURLY_RANGE_INVALID:
      'The hourly breakdown covers one day only — pick a period of one day or less',
    ANALYTICS_TOO_FEW_VISITORS:
      'Too few visitors in this selection to show numbers: at least 5 are needed. Remove some filters or choose a longer period.',
    ANALYTICS_UTM_VALUES_TOO_MANY:
      'Too many UTM values in one filter: you can pick up to 50.',
  },
  {
    BILLING_ACCESS_REQUIRED:
      'Платёжные данные вам недоступны — доступ к ним выдаёт владелец организации.',
    CAPABILITY_REQUIRED: 'Для этого действия не хватает прав в проекте.',
    FORBIDDEN:
      'Проверять доступность домена для организации может только её владелец или администратор.',
    NOT_RESOURCE_OWNER:
      'Это можно сделать только с тем, что вы создали сами. Попросите владельца или администратора проекта сделать это за вас.',
    RESOURCE_READONLY: 'Этот объект доступен только для чтения: тариф понижен.',
    PROJECT_FROZEN:
      'Проект заморожен. Разберитесь с оплатой, чтобы продолжить.',
    PROJECT_READONLY: 'Проект переведён в режим «только чтение».',
    PROJECT_NOT_FOUND: 'Проект не найден',
    ORGANIZATION_NOT_FOUND: 'Организация не найдена',
    ORG_MEDIA_CURSOR_INVALID:
      'Курсор страницы медиа-хаба испорчен — начните просмотр заново',
    CAPI_DISPATCH_LOG_CURSOR_INVALID:
      'Курсор страницы журнала отправок испорчен — откройте журнал заново',
    WEBHOOK_DELIVERY_LOG_CURSOR_INVALID:
      'Курсор страницы журнала доставок испорчен — откройте журнал заново',
    EXTERNAL_ANALYTICS_DISPATCH_LOG_CURSOR_INVALID:
      'Курсор страницы журнала отправок испорчен — откройте журнал заново',
    VALIDATION_WEBHOOK_URL_SCHEME:
      'Адрес получателя должен быть полным адресом на https:// — например https://api.example.com/hooks/web2app',
    VALIDATION_WEBHOOK_URL_FORMAT:
      'Адрес получателя должен быть полным адресом с протоколом http:// или https://',
    VALIDATION_API_KEY_NAME_LENGTH:
      'Имя ключа длиннее 255 символов — сократите его',
    VALIDATION_DRAFT_FIELD_NULL:
      'Это поле нельзя очистить — укажите значение или не передавайте поле',
    // WEB-2668 — пара к английской фразе выше.
    VALIDATION_NAME_EMPTY:
      'Имя не может быть пустым или состоять из одних пробелов',
    VALIDATION_DASHBOARD_LAYOUT_INVALID:
      'Раскладка дашборда не сохранена: нужен объект JSON не больше 16 КБ',
    // WEB-2383 п.23 (шов Ш3 пачки 48) — пара к английским фразам выше.
    // WEB-2375 п.Д1 — пара к английской фразе выше.
    GENERATION_CANCELLED:
      'Генерация остановлена вами. Списано только за работу, которую ИИ успел сделать до остановки.',
    // WEB-2375 п.3 — пара к английской фразе выше.
    GENERATION_TASK_LOST:
      'Задача генерации потерялась и не была взята в работу — кредиты возвращены. Запустите генерацию заново.',
    VALIDATION_GENERATION_DESCRIPTION_LENGTH:
      'Описание длиннее 2000 символов — оставьте главное о продукте, детали ИИ додумает сам',
    VALIDATION_GENERATION_NICHE_LENGTH:
      'Тематика длиннее 200 символов — это короткое поле для темы («фитнес», «похудение»), а не для задания',
    // WEB-2384 п.9 — пара к английской фразе выше.
    VALIDATION_GENERATION_LANGUAGE_UNSUPPORTED:
      'Такой язык мы не поддерживаем. Укажите код из списка — например «ru», «en», «pt-BR».',
    VALIDATION_GENERATION_LANGUAGE_LENGTH:
      'Код языка длиннее 35 символов — укажите код вида «ru», «en», «pt-BR»',
    VALIDATION_GENERATION_VARIANT_TASK_LENGTH:
      'Задание варианта длиннее 500 символов — оставьте главное: чем этот вариант отличается',
    PROJECT_BLOCKED:
      'Публикация недоступна: по организации есть долг или спорный платёж.',
    ORG_FROZEN:
      'Организация заморожена: по платежу идёт спор либо он не прошёл.',
    NO_ORGANIZATION: 'У вас пока нет организации — сначала создайте её.',
    ORG_ID_REQUIRED:
      'У вас несколько организаций — укажите, в какой создать проект.',
    EMAIL_NOT_VERIFIED:
      'Подтвердите адрес почты, чтобы пользоваться этим разделом.',
    MEMBER_FILTER_FORBIDDEN:
      'Фильтр «Участник» доступен владельцу, администратору и менеджеру по удержанию',
    JOURNEY_NOT_FOUND: 'Путь посетителя не найден',
    SDK_LOG_ACCESS_FORBIDDEN:
      'Журнал SDK доступен владельцу, администратору проекта и менеджеру по удержанию.',
    LEDGER_ACCESS_OWNER_OR_ADMIN:
      'История операций доступна только владельцу или администратору организации',
    ADMIN_CHANGES_OWNER_ONLY:
      'Состав администраторов проекта меняет только владелец: администратор не может исключить или понизить другого администратора.',
    OWNER_BILLING_ACCESS_IMMUTABLE:
      'Владелец организации всегда управляет платежами — этот доступ у него не отнять.',
    PROJECT_OWNER_ONLY_SETTING:
      'Эту настройку проекта меняет только его владелец.',
    CONSENT_SHARE_UNSUPPORTED_DOMAIN:
      'Для доменов этого проекта единое согласие на cookie пока не поддерживается: браузер не даст записать общую cookie на их родительский домен. Освободите эти домены или оставьте согласие отдельным для каждого адреса.',
    POSTBACK_URL_UNKNOWN_MACRO:
      'В адресе постбэка неизвестная подстановка. Допустимы только {click_id}, {payout}, {status} и {txid}.',
    FEATURE_LOCKED: 'Эта возможность доступна на платных тарифах.',
    PRO_REQUIRED: 'Эта возможность доступна только на тарифе Pro.',
    PUBLIC_FUNNEL_UNAVAILABLE: 'Эта страница сейчас недоступна.',
    TIER_LIMIT: 'Достигнут предел вашего тарифа.',
    TOPUP_REQUIRES_PRO: 'Пополнение кошелька доступно на тарифе Pro и выше.',
    INSUFFICIENT_BALANCE: 'На кошельке недостаточно средств.',
    CREDIT_DEBT_BLOCKED:
      'У организации долг по кредитам — пока он не погашен, платные действия ИИ закрыты. Пополните кошелёк: долг погасится сразу, и ИИ снова заработает. Опубликованные воронки при этом продолжают работать.',
    PAYMENT_REQUIRED_TIER_LIMIT_PROJECTS:
      'На вашем тарифе больше проектов в организации создать нельзя.',
    PAYMENT_REQUIRED_TIER_LIMIT_QUIZZES:
      'На вашем тарифе больше действующих квизов создать нельзя.',
    PAYMENT_REQUIRED_TIER_LIMIT_PAYWALLS:
      'На вашем тарифе больше действующих пейволлов создать нельзя.',
    PAYMENT_REQUIRED_TIER_LIMIT_FUNNELS:
      'На вашем тарифе больше воронок создать нельзя.',
    PAYMENT_REQUIRED_TIER_LIMIT_FUNNELS_DETACH:
      'На вашем тарифе нельзя отцепить кусок воронки: он станет отдельной воронкой, а лимит уже исчерпан. Перейдите на тариф выше, чтобы отцепить.',
    PAYMENT_REQUIRED_TIER_LIMIT_DOMAINS:
      'На вашем тарифе больше своих доменов подключить нельзя.',
    PAYMENT_REQUIRED_PUBLISH_ON_FREE:
      'Публикация недоступна на бесплатном тарифе.',
    PAYMENT_REQUIRED_LIMIT_CHECK_INCOMPLETE:
      'Не удалось проверить лимит тарифа: в запросе не хватает данных для проверки.',
    UNPAID_PUBLISH_CLOSED:
      'Публикация недоступна без оплаты тарифа: оплатите тариф, чтобы запустить трафик.',
    UNPAID_DOMAINS_CLOSED:
      'Свои домены недоступны без оплаты тарифа: оплатите тариф, чтобы подключить домен.',
    UNPAID_PROJECT_LIMIT:
      'Без оплаты тарифа доступен один проект. Оплатите тариф, чтобы создать ещё.',
    UNPAID_QUIZ_LIMIT:
      'Без оплаты тарифа можно создать до 50 квизов. Оплатите тариф, чтобы создать ещё.',
    UNPAID_PAYWALL_LIMIT:
      'Без оплаты тарифа можно создать до 50 пейволлов. Оплатите тариф, чтобы создать ещё.',
    UNPAID_FUNNEL_LIMIT:
      'Без оплаты тарифа можно создать до 50 воронок. Оплатите тариф, чтобы создать ещё.',
    UNPAID_SMART_LINKS_CLOSED:
      'A/B-тесты недоступны без оплаты тарифа: оплатите тариф, чтобы их запускать.',
    UNPAID_AI_CLOSED:
      'ИИ недоступен без оплаты тарифа: оплатите тариф, чтобы пользоваться ИИ.',
    UNPAID_BRIDGE_CLOSED:
      'Подключение приложения недоступно без оплаты тарифа: оплатите тариф, чтобы его настроить.',
    UNPAID_TOPUP_CLOSED:
      'Пополнение кошелька недоступно без оплаты тарифа: сначала оплатите тариф.',
    TRIAL_TOPUP_CAP_EXCEEDED:
      'На пробном периоде можно купить пакеты кредитов всего на {cap}. Осталось {remaining} — выберите пакет поменьше или оплатите тариф: после оплаты предела нет.',
    TRIAL_TOPUP_CAP_UNAVAILABLE:
      'Докупка кредитов на пробном периоде сейчас недоступна: не удалось проверить предел пробного. Деньги не списаны — попробуйте позже или напишите в поддержку.',
    USAGE_DEBT_FORBIDDEN:
      'Долг по плате за объём может оплатить только владелец или администратор организации с доступом к деньгам.',
    USAGE_DEBT_ORG_NOT_FOUND:
      'Проект не привязан к организации — долг по плате за объём не найден.',
    UNFREEZE_REASON_REQUIRED:
      'По организации остались незакрытые споры — разморозка требует явной причины.',
    UNFREEZE_WRITE_OFF_DECISION_REQUIRED:
      'За оспоренные платежи организации выданы кредиты или сумму выяснить не удалось. Отметьте «списание сделано» или «списание не требуется» — без отметки заморозка не снимается.',
    CREDIT_DAILY_CAP_EXCEEDED: 'Суточный потолок начислений превышен.',
    STARTING_BALANCE_NEEDS_ADMIN:
      'Начисление стартового баланса невозможно без администратора-инициатора.',
    RESUBMIT_OTHER_ORG:
      'Повтор отправки с другой организацией: по этому ключу уже записана операция для другой организации. Ничего не изменено.',
    RESUBMIT_OTHER_CLIENT:
      'Повтор отправки с другим клиентом: по этому ключу уже записана операция для другого клиента. Ничего не изменено.',
    RESUBMIT_CHANGED_FIELDS:
      'Повтор отправки с изменёнными полями: по этому ключу уже записана другая операция. Ничего не изменено.',
    ADJUSTMENT_AMOUNT_INVALID:
      'Сумма корректировки должна быть целым ненулевым числом центов',
    ADJUSTMENT_AMOUNT_ABOVE_LIMIT:
      'Сумма корректировки за одну операцию превышает разрешённый предел.',
    ORG_NOT_OWNED_BY_CLIENT:
      'Организация не принадлежит клиенту, которому делается корректировка. Ничего не изменено.',
    TIER_ORG_NOT_OWNED_BY_CLIENT:
      'Организация не принадлежит клиенту, которому выдаётся тариф. Ничего не изменено.',
    DEBIT_ABOVE_BALANCE:
      'Списание больше баланса — баланс не может уйти в минус.',
    ACTIVE_STRIPE_SUBSCRIPTION_EXISTS:
      'У клиента активная Stripe-подписка — сначала отмените её.',
    MONEY_REVIEW_KIND_UNKNOWN: 'Неизвестный вид денежного разбора.',
    STRIPE_NOT_CONNECTED: 'Stripe не подключён для этого проекта',
    STRIPE_KEYS_ENV_MISMATCH:
      'Ключи из разных сред Stripe. Возьмите оба ключа из одной среды (Live или Test) и повторите попытку.',
    STRIPE_CONNECT_FAILED:
      'Не удалось подключить Stripe. Проверьте корректность секретного ключа и повторите попытку.',
    stripe_connect_conflict:
      'Подключение этого режима уже идёт, обновите страницу и посмотрите, какие ключи сохранились.',
    STRIPE_PRICE_NOT_FOUND: 'Цена не найдена в этом проекте',
    STRIPE_PRICE_UPDATE_FAILED: 'Не удалось изменить цену в Stripe.',
    SUBSCRIPTION_TARGET_PRICE_NOT_RECURRING:
      'Разовая цена не может быть целью смены тарифа подписки',
    NO_RECURRING_PRICES:
      'У проекта нет ни одной действующей регулярной цены — переключать в портале нечего',
    NOTHING_TO_UPDATE: 'Нечего менять: укажите active и/или accessDurationDays',
    KEEP_SUBSCRIPTIONS_UNAVAILABLE:
      'Отвязка с сохранением подписок недоступна, а решение обязано попасть в журнал. Выберите «отменить сейчас» или «отменить в конце оплаченного периода».',
    STORE_CUSTOMER_NOT_FOUND: 'Покупатель не найден',
    PROVIDER_NOT_SUPPORTED: 'Эта платёжная система пока не поддерживается.',
    project_frozen:
      'Организация заморожена — денежные операции по серверному ключу остановлены; погасите задолженность или разрешите спор, чтобы возобновить.',
    project_readonly:
      'Проект переведён в режим «только чтение» — денежные операции и управление подписками покупателей по серверному ключу остановлены; ограничение снимает поддержка платформы.',
    test_key_not_allowed:
      'Это тестовый ключ (sk_test_…) — денежные операции и управление подписками покупателей им недоступны; выпустите боевой ключ (sk_live_…).',
    CHARGES_EXPORT_NOT_JOURNALED:
      'Выгрузка не выполнена: не удалось записать её в журнал действий команды (раздел «Аудит» → «Команда проекта»). Выгрузка содержит персональные данные покупателей, поэтому без записи о том, кто её сделал, файл не отдаётся. Повторите позже.',
    PERSONAL_DATA_EXPORT_NOT_JOURNALED:
      'Выгрузка не выполнена: не удалось записать её в журнал действий команды (раздел «Аудит»). Файл содержит почты посетителей и лидов, поэтому без записи о том, кто его выгрузил, он не отдаётся. Повторите позже.',
    REPORT_EXPORT_NOT_FOUND: 'Такой выгрузки нет — запросите её заново.',
    REPORT_EXPORT_EXPIRED:
      'Срок ссылки на выгрузку вышел — запросите выгрузку заново.',
    REPORT_EXPORT_NOT_READY:
      'Выгрузка ещё готовится — файл появится, когда она будет собрана.',
    REPORT_EXPORT_FAILED:
      'Выгрузку не удалось собрать — запросите её заново. Если повторится, напишите в поддержку.',
    REPORT_EXPORT_UNAVAILABLE:
      'Сейчас не получилось поставить выгрузку в очередь — попробуйте ещё раз через минуту.',
    REPORT_EXPORT_DISABLED:
      'Фоновые выгрузки на этом сервере не настроены — обратитесь в поддержку.',
    REPORT_EXPORT_SCOPE_CHANGED:
      'Ваши права в проекте изменились, и этот файл собран шире, чем вам теперь доступно — запросите выгрузку заново.',
    charge_disabled:
      'Серверные списания для проекта выключены — включите их в настройках проекта.',
    refund_disabled:
      'Серверные возвраты для проекта выключены — включите их в настройках проекта.',
    subscription_manage_disabled:
      'Управление подписками покупателей по серверному ключу для проекта выключено — включите его в настройках проекта.',
    bridge_temporarily_unavailable:
      'Машинный мост временно недоступен — это временный сбой на нашей стороне, а не отказ в праве: тариф и ключи доступа в порядке. Повторите запрос позже.',
    // WEB-2156: текст — про ТАРИФ, без слова «мост» (дверью пользуется и
    // клиент без моста; метка показа доступ не закрывает).
    bridge_tier_expired:
      'Льготный период тарифного доступа к платным машинным каналам истёк — вернитесь на платный тариф, чтобы возобновить их работу. Ключи доступа не отозваны, перевыпускать их не нужно.',
    // Пачка 49: приём событий с сервера клиента (POST /public/event).
    event_time_in_milliseconds:
      'Время события пришло в миллисекундах — поле eventTimeSec ждёт unix-секунды (10 цифр). Разделите значение на 1000 и повторите запрос.',
    invalid_currency:
      'Код валюты не из списка ISO-4217 — пришлите трёхбуквенный код настоящей валюты (например, usd или eur).',
    invalid_client_ip:
      'Поле clientIp — не IP-адрес. Пришлите адрес покупателя в виде IPv4 или IPv6 либо не присылайте поле.',
    scopes_empty_after_billing_flags:
      'Сужение прав ключа осталось пустым после ограничений по переключателям проекта — добавьте право без billing:* и без subscription:manage либо включите нужный переключатель проекта.',
    subscription_action_in_progress:
      'Над этой подпиской прямо сейчас выполняется другая операция — повторите запрос позже.',
    subscription_not_paused:
      'Право доступа истекло, а подписка Stripe не находится на паузе — снимать нечего.',
    no_active_subscription:
      'У покупателя нет действующей подписки — укажите subscriptionId явно.',
    charge_outcome_unknown:
      'Исход этого списания неизвестен — деньги могли уйти; сверьте платёж на стороне провайдера.',
    refund_state_unknown:
      'Состояние возврата по этой подписке неизвестно — сверьте возврат на стороне провайдера.',
    charge_refunded_needs_new_key:
      'Предыдущее списание по этой цене в текущих сутках возвращено — для нового списания передайте свой idempotencyKey.',
    price_not_one_time:
      'Списание не создаёт подписку — используйте разовую цену.',
    price_has_no_amount: 'У цены нет суммы.',
    charge_amount_above_limit: 'Сумма выше потолка одной операции для проекта.',
    no_saved_payment_method: 'У покупателя нет сохранённого способа оплаты.',
    charge_project_daily_limit_exceeded:
      'Суточный потолок списаний для проекта исчерпан — попробуйте позже либо поднимите лимит в настройках проекта.',
    charge_daily_limit_exceeded:
      'Суточный потолок списаний для покупателя исчерпан.',
    charge_in_progress:
      'Операция по этому ключу идемпотентности уже выполняется — повторите тот же запрос позже.',
    refund_in_progress:
      'Возврат уже выполняется другим запросом — повторите позже.',
    idempotency_key_reused:
      'Этот ключ идемпотентности уже использован с другими параметрами — передайте новый ключ либо повторите с прежней ценой.',
    discount_current_price_unknown:
      'Действующая цена подписки не найдена в каталоге проекта — периодичность неизвестна, скидка не применяется вслепую.',
    discount_interval_mismatch:
      'Целевая цена скидки обязана быть той же периодичности, что и текущая подписка.',
    guid_user_mismatch:
      'Параметры guid и user заданы одновременно и не совпадают.',
    guid_required: 'Укажите guid (либо его алиас user для web2wave-мигрантов).',
    SUBSCRIPTION_ALREADY_REFUNDED: 'Возврат по этой подписке уже сделан',
    NO_PAYMENT_TO_REFUND: 'Списания не было — возвращать нечего',
    CHARGE_OUTCOME_UNKNOWN:
      'Исход операции неизвестен — деньги могли быть списаны. Проверьте платёж на стороне платёжного провайдера.',
    SUBSCRIPTION_ACTION_IN_PROGRESS:
      'По этой подписке прямо сейчас идёт другая операция — возврат не начат, повторите запрос позже',
    REFUND_STATE_UNKNOWN:
      'Состояние возврата по этой подписке неизвестно — повторите позже.',
    // WEB-2005 — фильтры журнала списаний.
    CHARGE_FILTER_STATUS_UNKNOWN: 'Неизвестный исход списания в фильтре',
    CHARGE_FILTER_DATE_INVALID: 'Дата периода указана неверно',
    CHARGE_FILTER_PERIOD_INVALID: 'Начало периода позже его конца',
    CHARGE_FILTER_AMOUNT_INVALID:
      'Сумма в фильтре указана неверно: нужно целое число в минимальных единицах валюты, и минимум не больше максимума',
    CHARGE_FILTER_SORT_UNKNOWN:
      'Неизвестный порядок сортировки журнала списаний',
    CHARGE_FILTER_TIMEZONE_UNKNOWN:
      'Неизвестный часовой пояс в фильтре периода',
    CHARGE_FILTER_VALUE_INVALID:
      'Значение фильтра передано неверно: у каждого фильтра должно быть одно текстовое значение',
    // П3-3 (свежий взгляд пачки 37): пять отказов двухфакторной защиты.
    '2FA_NOT_ENABLED': 'Двухфакторная аутентификация не включена.',
    SEND_CODE_NOT_APPLICABLE:
      'Повторная отправка кода недоступна для этого способа входа.',
    LOGIN_TOO_MANY_ATTEMPTS:
      'Слишком много попыток входа. Попробуйте снова через {minutes} мин.',
    RATE_LIMITED_TRY_AGAIN_IN:
      'Слишком много попыток. Попробуйте снова через {minutes} мин.',
    RESEND_COOLDOWN: 'Код уже отправлен — подождите немного и запросите новый.',
    PASSWORD_REQUIRED: 'Введите пароль для подтверждения.',
    INVALID_PASSWORD: 'Неверный пароль.',
    // WEB-3970: повторное подтверждение передачи и удаления организации.
    ORG_ACTION_PASSWORD_INVALID: 'Пароль не подошёл — введите снова пароль.',
    ORG_ACTION_GOOGLE_CONFIRM_REQUIRED:
      'Подтвердите действие входом через Google.',
    ORG_ACTION_GOOGLE_CONFIRM_MISMATCH:
      'Подтвердите вход тем же аккаунтом Google, через который вы вошли.',
    ORG_ACTION_CONFIRM_UNAVAILABLE:
      'Не удалось проверить подтверждение. Попробуйте ещё раз через минуту.',
    VERIFICATION_CODE_INVALID: 'Неверный код подтверждения',
    VERIFICATION_CODE_EXPIRED: 'Срок действия кода истёк',
    VERIFICATION_CODE_ATTEMPTS_EXCEEDED: 'Слишком много попыток ввода кода',
    RESET_CODE_INVALID: 'Неверный код сброса',
    RESET_CODE_EXPIRED: 'Срок действия кода истёк',
    RESET_CODE_ATTEMPTS_EXCEEDED: 'Слишком много попыток ввода кода',
    EMAIL_TAKEN: 'Эта почта занята действующей учётной записью.',
    EMAIL_TAKEN_DEACTIVATED:
      'Эта почта принадлежит деактивированной учётной записи. Почта при деактивации не освобождается: восстановите ту запись либо используйте другую почту.',
    CLIENT_EMAIL_REQUIRED: 'Укажите почту клиента',
    CLIENT_EMAIL_NOT_FOUND: 'Клиент с такой почтой не найден',
    PROJECT_OWNER_ROLE_NOT_ASSIGNABLE:
      'Роль «Владелец» из админ-панели не назначается: у проекта ровно один владелец. Владение передаёт владелец проекта или организации сам.',
    PROJECT_OWNER_PROMOTION_FORBIDDEN:
      'Повысить до роли «Владелец» из админ-панели нельзя: у проекта ровно один владелец.',
    PROJECT_SOLE_OWNER_DEMOTE_FORBIDDEN:
      'У проекта ровно один владелец — понизить его нельзя. Смена владельца — отдельная операция передачи владения.',
    PROJECT_SOLE_OWNER_REMOVE_FORBIDDEN:
      'У проекта ровно один владелец — удалить его из проекта нельзя. Смена владельца — отдельная операция передачи владения.',
    ALREADY_PROJECT_OWNER:
      'Этот участник уже владелец проекта — передавать нечего.',
    OWNERSHIP_TARGET_NOT_MEMBER:
      'Владельцем можно сделать только участника проекта: сначала пригласите человека в проект, потом передавайте владение.',
    TRANSFER_OWNERSHIP_FORBIDDEN:
      'Передать владение проектом может только его владелец или владелец организации.',
    PROJECT_OWNER_CANNOT_LEAVE:
      'Владелец не может покинуть проект: сначала передайте владение другому участнику — пункт «Сделать владельцем» в списке участников проекта.',
    ADMIN_CANNOT_CREATE_ADMIN:
      'Администратор не может назначить другого администратора: состав администраторов проекта меняет владелец.',
    REMOVED_MEMBER_LINK_INVITE:
      'Вас удалили из этого проекта, поэтому вернуться по общей ссылке-приглашению нельзя. Попросите владельца проекта прислать персональное приглашение на вашу почту.',
    PROJECT_HAS_PAYMENT_HISTORY:
      'Этот проект нельзя удалить: по нему проходили платежи, а историю платежей мы обязаны сохранять.',
    PROJECT_HAS_ENTITLEMENT_GRANTS:
      'Этот проект нельзя удалить: по нему выданы права доступа покупателям.',
    ACCOUNT_DELETION_ORG_TRANSFER_REQUIRED:
      'Передайте владение организацией или используйте админ-панель, прежде чем удалить аккаунт',
    ACCOUNT_DELETION_DOMAIN_RELEASE_REQUIRED:
      'Отвяжите или передайте купленные домены, прежде чем удалить аккаунт',
    ACCOUNT_DELETION_SUBSCRIPTION_ACTIVE:
      'Сначала отмените платную подписку в разделе «Баланс/Тариф», затем удаляйте аккаунт. Подписка на паузе или незавершённая держит удаление так же: Stripe по ней ещё может списать деньги, а вернуться и отменить её после удаления аккаунта будет уже нельзя — отмените подписку в Stripe или дождитесь, пока она завершится.',
    ACCOUNT_DELETION_PAYMENT_PROFILE_ACTIVE:
      'В Stripe остался ваш платёжный профиль, и подтверждения, что списаний по нему больше не будет, у нас нет — поэтому аккаунт пока не удаляется. Подписки, которую вы могли бы отменить сами, в нём нет: напишите в поддержку, мы проверим профиль в Stripe и закроем его, после этого удаление пройдёт.',
    ACCOUNT_DELETION_PASSWORD_REQUIRED:
      'Удалить аккаунт можно только с паролем: сначала задайте пароль в настройках, потом удаляйте.',
    ACCOUNT_DELETION_PASSWORD_INVALID:
      'Неверный пароль — аккаунт не удалён. Введите текущий пароль ещё раз.',
    CLIENT_DEACTIVATION_SUBSCRIPTION_ACTIVE:
      'Клиента с живой подпиской Stripe деактивировать нельзя: вход ему закроется, отменить подписку он уже не сможет, а Stripe продолжит списывать с карты. Сначала отмените подписку в панели Stripe платформы (Customers → карточка клиента → Subscriptions → Cancel subscription), затем деактивируйте. Подписка, которую клиент уже отменил «в конце оплаченного периода», деактивации не мешает.',
    INVITE_ALREADY_ACCEPTED:
      'Приглашение уже принято — отозвать его нельзя. Чтобы закрыть доступ вошедшему, удалите его из участников.',
    ORG_COUNT_FAILED:
      'Не удалось посчитать организации — создание отменено. Повторите попытку.',
    ORG_NAME_CONFIRMATION_MISMATCH:
      'Введите название организации слово в слово. Так мы убеждаемся, что удаляют именно её.',
    ORG_DELETE_BLOCKED: 'Организацию пока нельзя удалить.',
    PURCHASED_DOMAINS:
      'В организации есть купленные нами домены — отключить их вручную нельзя.',
    CUSTOM_DOMAINS:
      'К организации подключены свои домены — отключите их в разделе «Домены».',
    PROJECT_PAYMENT_HISTORY:
      'По проектам организации проходили платежи покупателей, и срок их хранения ещё не истёк.',
    PAYMENT_RECORDS_RETAINED:
      'Записи о платежах покупателей удалить нельзя — они хранятся по правилу денежных данных.',
    ACTIVE_ENTITLEMENT_GRANTS:
      'У покупателей есть действующие права доступа. Люди за них заплатили, поэтому вместе с организацией мы их не гасим.',
    ACTIVE_PLATFORM_SUBSCRIPTION:
      'У организации живая подписка на тариф. Сначала отмените тариф — иначе списания продолжатся.',
    WALLET_BALANCE:
      'На кошельке организации остались деньги. Удаление их сожжёт, поэтому сначала напишите в поддержку.',
    OPEN_CHARGEBACK_DISPUTE:
      'По организации идёт спор о списании. Пока он не закрыт, удалять нечего — данные нужны для разбора.',
    UNSYNCED_BALANCE_PAYMENT:
      'По тарифу организации остался незакрытый расчёт: счёт оплачен с баланса, а отметка об этом ещё не подтверждена. Пока он не разобран, организацию не удаляем — напишите в поддержку.',
    DOMAIN_NOT_FOUND: 'Домен не найден',
    DOMAIN_RENEWAL_PAYMENT_ORG_PAID:
      'У организации оплачен тариф — домен продлевается из кошелька, отдельная оплата продления не нужна.',
    DOMAIN_RENEWAL_PAYMENT_NOT_ELIGIBLE:
      'Этот домен сейчас нельзя продлить оплатой: он не куплен через нас, покупка ещё не подтверждена, домен заморожен по спору о платеже или ждёт сверки с регистратором.',
    DOMAIN_RENEWAL_PAYMENT_NO_STRIPE_CUSTOMER:
      'У организации ещё нет платёжного профиля — выставить счёт за продление не на кого. Напишите в поддержку.',
    DOMAIN_RENEWAL_PAYMENT_STRIPE_REFUSED:
      'Платёжная система не приняла счёт за продление домена. Попробуйте позже.',
    DOMAIN_RENEWAL_PAYMENT_PRICE_ROSE:
      'Цена продления у регистратора выросла выше оплаченной суммы — домен не продлён, деньги вернутся на карту.',
    DOMAIN_RENEWAL_PAYMENT_CYCLE_BUSY:
      'Этот срок регистрации уже продлевается или продлён — второй раз домен не продлевается, деньги вернутся на карту.',
    DOMAIN_TAKEN: 'Этот домен уже занят.',
    INVALID_DOMAIN: 'Некорректное имя домена.',
    PLATFORM_DOMAIN_NOT_ALLOWED:
      'Это имя принадлежит нашей платформе, его нельзя подключить как свой домен.',
    UNSUPPORTED_TLD:
      'Этот домен пока нельзя купить через нас: его зона требует данных регистранта, которые мы не собираем. Пожалуйста, выберите другую зону.',
    TRIAL_DOMAIN_BLOCKED:
      'Домены недоступны во время пробного периода. Дождитесь первого платежа по подписке, чтобы покупать домены.',
    DOMAIN_PRICE_UNAVAILABLE:
      'Цена домена сейчас недоступна — регистратор не отвечает. Попробуйте позже.',
    DOMAIN_PURCHASE_IN_PROGRESS:
      'Покупка этого домена уже идёт — дождитесь её завершения.',
    DOMAIN_PURCHASE_PROCESSING:
      'Покупка домена обрабатывается — повторно платить не нужно. Как только регистратор подтвердит покупку, домен появится в списке; если покупка не прошла, деньги вернутся на баланс.',
    DOMAIN_PURCHASE_NOT_MOVABLE:
      'Домен ещё покупается — перенести его в другой проект или в пул организации можно после того, как покупка завершится.',
    DOMAIN_PURCHASE_RETRY_LIMIT:
      'Слишком много попыток покупки этого домена — попробуйте позже.',
    DOMAIN_CONNECT_RATE_LIMITED:
      'Слишком много подключений домена для этой организации — попробуйте позже.',
    PUBLIC_EVENT_PROJECT_RATE_LIMITED:
      'Слишком много событий этого проекта: не больше 300 в минуту — повторите позже.',
    PUBLIC_EVENT_FAILED_AUTH_RATE_LIMITED:
      'Слишком много запросов без действительной подписи с этого адреса — повторите позже.',
    PUBLIC_EVENT_AUTH_UNAVAILABLE:
      'Проверка подписи временно недоступна на нашей стороне — повторите запрос позже (событие — с тем же eventId).',
    PUBLIC_EVENT_IP_RATE_LIMITED:
      'Слишком много запросов с этого адреса: не больше 3000 в минуту на все проекты вместе — подождите столько секунд, сколько указано в заголовке Retry-After, и повторите событие с тем же eventId.',
    DOMAIN_HAS_SMART_LINKS:
      'Сначала разберитесь с A/B-тестами этого домена: {tests}. Перенесите их в целевой проект или удалите — после этого домен можно передать.',
    DOMAIN_NOT_SERVABLE: 'Домен ещё не настроен — дождитесь проверки DNS.',
    NO_DOMAIN_BOUND: 'Привяжите домен и субдомен к воронке перед публикацией.',
    CROSS_ORG_REASSIGN_FORBIDDEN:
      'Домен можно передать только проекту той же организации.',
    CUSTOM_HOSTNAME_TAKEN: 'Это имя хоста уже занято у поставщика.',
    CUSTOM_SUBDOMAINS_UNAVAILABLE:
      'Поддомены на подключённых доменах временно недоступны — не настроена интеграция Cloudflare for SaaS.',
    PARENT_IS_SUBDOMAIN:
      'Нельзя создать поддомен от другого поддомена — выберите корневой домен.',
    PARENT_NOT_SERVABLE:
      'Родительский домен ещё не прошёл проверку DNS — дождитесь его активации.',
    ROUTE_PATH_RESERVED_PREFIX:
      'Адреса «/public», «/__w2a» заняты служебными каналами края — привязать на них воронку нельзя, она не откроется. Выберите другой путь.',
    REGISTRAR_NOT_CONFIGURED:
      'Регистратор доменов не настроен — операция недоступна. Списания не было.',
    CUSTOM_DOMAINS_NOT_CONFIGURED:
      'Подключение своих доменов на сервере не настроено — операция недоступна. Напишите в поддержку.',
    // WEB-2489: перезапуск подключения своего домена.
    PUBLIC_RESOLVE_HOST_REQUIRED:
      'Не передан адрес сайта (параметр host) — без него нельзя понять, какую воронку показать.',
    // WEB-3673: подтверждение показа прогретой страницы с негодным телом.
    PUBLIC_SHOW_CONFIRM_INVALID:
      'Подтверждение показа не принято: нужны номер проекта (UUID) и номер загрузки страницы (от 16 до 64 латинских букв, цифр, «-» или «_»).',
    // WEB-3286: сохранение версии ценового плана организации (админка).
    PRICING_PLAN_WRITE_FORBIDDEN:
      'Менять ценовые планы может только администратор с правом «pricing_plan.write».',
    PRICING_PLAN_ORG_NOT_FOUND:
      'Организация не найдена — версия ценового плана не сохранена.',
    PRICING_PLAN_FIELD_INVALID:
      'Версия ценового плана не сохранена: неверное значение поля «{field}».',
    PRICING_PLAN_EFFECTIVE_FROM_IN_PAST:
      'Дата начала действия версии уже прошла — прошлые события не пересчитываются. Укажите текущий момент или позже.',
    PRICING_PLAN_TARIFF_CHANGE_TOO_SOON:
      'Цену тарифа можно изменить не раньше {earliest} (через 30 дней). Сразу — только снижение (отметка «это снижение») или для организации, которая ещё ни разу не платила.',
    PRICING_PLAN_AI_MARKUP_BELOW_FLOOR:
      'Итоговая наценка ИИ для класса «{modelClass}» ниже минимальной ×{floor} — такую версию плана сохранить нельзя.',
    // WEB-3848: ручки админки плана и цена тарифа в Stripe.
    PRICING_PLAN_READ_ORG_NOT_FOUND:
      'Организация не найдена — ценовой план не показан.',
    PRICING_PLAN_CALCULATOR_INPUT_INVALID:
      'Расчёт не выполнен: неверное значение «{field}».',
    PRICING_PLAN_STRIPE_PRODUCT_NOT_CONFIGURED:
      'Новую цену тарифа сохранить нельзя: товар платформы в Stripe не настроен (PLATFORM_STRIPE_PRODUCT_ID). Ставки и прочие поля плана сохраняются без смены цены тарифа.',
    PRICING_PLAN_STRIPE_PRICE_CONFLICT:
      'Цену тарифа сохранить нельзя: в Stripe ключ «{lookupKey}» занят другой ценой. Разберитесь с этой ценой в кабинете Stripe и повторите.',
    // WEB-3292: разовый счёт допродажи Enterprise (админка).
    ENTERPRISE_ADDON_NO_STRIPE_CUSTOMER:
      'У организации нет клиента Stripe — счёт допродажи не выставлен. Клиент Stripe появляется после первой оплаты тарифа.',
    ENTERPRISE_ADDON_AMOUNT_INVALID:
      'Сумма счёта допродажи — целое число центов от 1 до {max}. Счёт не выставлен.',
    ENTERPRISE_ADDON_STRIPE_REFUSED:
      'Stripe отказал на шаге «{step}» — клиенту ничего не списано. Повторите с тем же ключом запроса: повтор продолжит с того же места.',
    ENTERPRISE_ADDON_IDEMPOTENCY_CONFLICT:
      'Этот ключ запроса уже использован для другого счёта допродажи (другая сумма или описание). Откройте форму заново и повторите.',
    // WEB-2503: оформление тарифа по периоду (вторая оплата, цена не настроена).
    CHECKOUT_SUBSCRIPTION_EXISTS:
      'У организации уже есть подписка — вторая оплата не нужна. Сменить карту, период или отменить подписку можно в управлении подпиской.',
    CHECKOUT_TARIFF_PRICE_UNAVAILABLE:
      'Оплата тарифа сейчас недоступна: цена тарифа не настроена. Деньги не списаны — напишите в поддержку.',
    // WEB-3892: второе оформление тарифа, пока открыто первое.
    CHECKOUT_IN_PROGRESS:
      'Оплата тарифа уже открыта в другой вкладке или окне — завершите её там или повторите через 35 минут. Второй раз ничего не списано.',
    // WEB-4198: повтор с ключом уже закрытого резерва.
    BALANCE_HOLD_KEY_CLOSED:
      'Этот запрос уже завершён, и повторить его с тем же номером нельзя. Начните действие заново.',
    CUSTOM_HOSTNAME_NOT_FOUND:
      'Cloudflare больше не знает подключение этого домена — перезапустите подключение.',
    DOMAIN_CONNECTION_RESTART_NOT_NEEDED:
      'Перезапускать подключение этого домена не нужно: оно не остановлено или уже запущено заново.',
    DOMAIN_CONNECTION_RESTART_FROZEN:
      'Домен приостановлен из-за оплаты — перезапуск подключения его не вернёт. Сначала восстановите оплату или тариф.',
    DOMAIN_CONNECTION_RESTART_PROVIDER_FAILED:
      'Подключение запущено заново, но Cloudflare сейчас не принял домен. Мы повторим регистрацию сами в течение нескольких минут — домен будет в статусе «Ожидает».',
    // WEB-2132 (атом 1): подтверждение владения доменом через TXT.
    OWNERSHIP_TXT_MISSING:
      'TXT-запись подтверждения владения не найдена или её значение не совпадает. Проверьте запись у регистратора домена и повторите проверку.',
    // WEB-2255 (п.4): три исхода проверки TXT вместо одного — у каждого своя
    // подсказка «когда имеет смысл нажать в следующий раз».
    OWNERSHIP_TXT_NOT_FOUND:
      'TXT-записи подтверждения владения в зоне домена нет. Добавьте её у регистратора и повторите проверку через 5-30 минут — столько обычно расходится новая запись.',
    OWNERSHIP_TXT_VALUE_MISMATCH:
      'TXT-запись на месте, но её значение не то, которое мы ждём. Исправьте значение у регистратора и повторите проверку сразу после сохранения — ждать расхождения не нужно.',
    OWNERSHIP_DOMAIN_NOT_RESOLVING:
      'Домен пока не отвечает службе имён, поэтому про TXT-запись сказать нечего. Так бывает у только что купленного домена и сразу после смены серверов имён: расхождение занимает до нескольких часов. Повторите проверку позже.',
    OWNERSHIP_CHECK_FAILED:
      'Проверить TXT-запись не удалось — временный сбой на нашей стороне или у сервиса DNS. Повторите попытку.',
    OWNERSHIP_CLAIMED_BY_ANOTHER_ACCOUNT:
      'Владение этим доменом уже подтвердил другой аккаунт.',
    OWNERSHIP_ALREADY_VERIFIED:
      'Владение этим доменом уже подтверждено — повторная проверка не требуется.',
    OWNERSHIP_VERIFICATION_NOT_APPLICABLE:
      'Для этого домена проверка владения через TXT не применяется.',
    OWNERSHIP_PENDING_IN_ANOTHER_PROJECT:
      'Этот домен уже ждёт подтверждения TXT-записи в другом проекте вашей организации.',
    LEGAL_URLS_REQUIRED:
      'Нельзя опубликовать: в блоке условий не заполнены ссылки на условия использования и политику конфиденциальности.',
    PAYMENTS_NOT_CONNECTED:
      'Нельзя опубликовать: в воронке есть блок оплаты, а приём платежей к проекту не подключён.',
    PAYWALL_PRICE_MISSING:
      'Нельзя опубликовать: у блоков оплаты не указана цена.',
    PAYWALL_PRICE_STALE:
      'Нельзя опубликовать: цены больше нет в подключённом аккаунте Stripe — выберите цены заново.',
    PAYWALL_PLAN_SELECTOR_POST_PURCHASE:
      'Нельзя опубликовать: «Выбор тарифа» стоит только на экране после оплаты «{screen}» — до оплаты покупатель его не видит, и у кнопки оплаты нет цены. Перенесите «Выбор тарифа» на обычный экран или удалите его и задайте цену кнопке оплаты.',
    PAYWALL_PRICE_CONFLICT:
      'Нельзя опубликовать: на экране неоднозначный источник цены.',
    APP_BRIDGE_NOT_CONFIGURED:
      'Нельзя опубликовать: на экране есть кнопка перехода в приложение, а мост в приложение у проекта не настроен — задайте его в настройках проекта → Подключение приложения.',
    SENSITIVE_CONSENT_REQUIRED:
      'Нельзя опубликовать: перед первым чувствительным вопросом на каждом пути посетителя обязан стоять видимый блок явного согласия — проверьте ветвления и правила показа блока.',
    EMPTY_QUIZ:
      'Добавьте хотя бы один экран с контентом перед публикацией или отправкой на проверку.',
    EMPTY_PAYWALL:
      'Пустой пейволл нельзя опубликовать или отправить на проверку: добавьте хотя бы один экран с блоками — например, экран с ценами. Уже опубликованный пейволл продолжает работать как есть — отказ касается только новой публикации, отправки на проверку и отката на пустую версию.',
    STALE_DRAFT:
      'Страницу изменили в другом окне или другим участником — обновите редактор.',
    DRAFT_BASE_REQUIRED:
      'Не удалось понять, какую версию черновика вы видите, — обновите редактор и повторите.',
    GRAPH_LAYOUT_INVALID:
      'Расположение узлов графа не сохранено: данные пришли в неверном виде. Обновите страницу и повторите.',
    GRAPH_LAYOUT_TOO_LARGE:
      'Расположение узлов графа не сохранено: в нём больше 1000 экранов. Удалите лишние экраны или нажмите «Авторасставить».',
    OVERLAY_FRAME_FORBIDDEN:
      'Эта страница запрещает показ внутри чужих страниц, поэтому окном поверх квиза её не открыть — пейволл откроется переходом. Остальные изменения сохранены.',
    OVERLAY_CHECK_FAILED:
      'Не удалось проверить, можно ли показать эту страницу окном поверх квиза (она не ответила вовремя или ответила ошибкой) — пока пейволл откроется переходом. Сохраните ещё раз позже, чтобы проверить снова. Остальные изменения сохранены.',
    OVERLAY_NOT_HTTPS:
      'Окном поверх квиза открываются только адреса, начинающиеся с https:// — пейволл откроется переходом. Остальные изменения сохранены.',
    OVERLAY_TOO_MANY:
      'За одну запись проверяется не больше 10 разных адресов окон поверх квиза — у этого окна адрес сверх предела, поэтому пейволл откроется переходом. Уберите лишние окна или откройте часть пейволлов переходом. Остальные изменения сохранены.',
    LOGIC_PAYWALL_JUMP_IN_PAYWALL:
      'Правило «Перейти к пейволлу» работает только в квизе — в пейволле его сохранить нельзя. Уберите это правило или выберите другое действие.',
    // WEB-3929: воронка «с нуля» — выбор частей «новый / из библиотеки / без».
    VALIDATION_FUNNEL_PIECE_SOURCE:
      'Для квиза и пейволла можно выбрать только «новый», «из библиотеки» или «без».',
    VALIDATION_FUNNEL_PIECE_ID_REQUIRED:
      'Выбран вариант «из библиотеки» — выберите в библиотеке, какой именно квиз или пейволл взять.',
    VALIDATION_FUNNEL_PIECE_ID_UNEXPECTED:
      'Квиз или пейволл из библиотеки передаётся только вместе с вариантом «из библиотеки» для этой части.',
    VALIDATION_FUNNEL_PIECE_ID_FORMAT:
      'Не удалось распознать выбранный квиз или пейволл — обновите страницу и выберите его в библиотеке ещё раз.',
    FUNNEL_NEEDS_PIECE: 'Воронке нужен хотя бы квиз или пейволл.',
    FUNNEL_LIBRARY_PIECE_DRAFT:
      'Черновик в воронку не взять — сначала опубликуйте его.',
    FUNNEL_LIBRARY_QUIZ_NOT_FOUND:
      'Выбранный квиз не найден в этом проекте — возможно, его удалили. Выберите другой.',
    FUNNEL_LIBRARY_PAYWALL_NOT_FOUND:
      'Выбранный пейволл не найден в этом проекте — возможно, его удалили. Выберите другой.',
    FUNNEL_NOT_FOUND:
      'Воронка не найдена в этом проекте — возможно, её удалили.',
    // WEB-3985 п.1: без привязки к воронке.
    QUIZ_NOT_FOUND: 'Квиз не найден — возможно, его удалили.',
    PAYWALL_NOT_FOUND: 'Пейволл не найден — возможно, его удалили.',
    // WEB-3399: свои стили и палитры организации.
    ORG_STYLE_LIMIT_REACHED:
      'В организации уже 50 своих стилей. Удалите ненужный, чтобы сохранить новый.',
    ORG_PALETTE_LIMIT_REACHED:
      'В организации уже 50 своих палитр. Удалите ненужную, чтобы сохранить новую.',
    ORG_DESIGN_INVALID:
      'Не сохранено: данные стиля или палитры не прошли проверку. Обновите страницу и попробуйте ещё раз.',
    ORG_DESIGN_MANAGE_FORBIDDEN:
      'Переименовать или удалить может только автор, владелец или администратор организации.',
    ORG_DESIGN_SAVE_FORBIDDEN:
      'Сохранять стили может только тот, кто может править воронки',
    ORG_DESIGN_NOT_FOUND:
      'Стиль или палитра не найдены — возможно, их уже удалили.',
    CUSTOM_STYLE_PACK_INVALID:
      'Свой стиль воронки не сохранён: данные не прошли проверку. Обновите страницу и попробуйте ещё раз.',
    DRAFT_CHANGED_DURING_PUBLISH:
      'Страницу изменили, пока шла публикация, — правки сохранены, нажмите «Опубликовать» ещё раз',
    DRAFT_CONTENT_NOT_ACCEPTED:
      'Сохранение принимает поле content — именно оно пишется в черновик.',
    // WEB-2557: общая фраза кода (особая «не список экранов» — funnelContentCorrupted).
    INVALID_FUNNEL_CONTENT:
      'Сервер не принял содержимое воронки — проверьте экраны и блоки',
    PIECE_IN_USE: 'Этот кусок используется другими воронками.',
    PIECE_SHARED: 'Некоторые куски общие с другими воронками этого проекта.',
    SOURCE_LANGUAGE_HAS_TRANSLATION:
      'На этот язык уже заказан перевод — сделать его исходным нельзя.',
    TRANSLATION_UNAVAILABLE:
      'Перевод сейчас недоступен: сервис ИИ не настроен на сервере.',
    PRICE_LEVEL_MAP_INCOMPLETE:
      'Не у всех цен пейвола указан уровень — заполните их перед публикацией.',
    RETURN_URL_INVALID: 'Недопустимый адрес возврата.',
    PRICE_NOT_ON_PAYWALL: 'Эта цена недоступна для оплаты на этой странице.',
    PRICE_GONE:
      'Эта цена больше недоступна. Обновите страницу или свяжитесь с продавцом.',
    CHECKOUT_START_FAILED:
      'Не удалось начать оплату. Попробуйте ещё раз или свяжитесь с продавцом.',
    INSTRUCTION_TOO_LONG:
      'Задание для ИИ слишком длинное. Максимум — 8000 символов. Сократите текст или разбейте правку на несколько ходов.',
    REFERENCE_REJECTED: 'Приложенный референс не принят.',
    // WEB-2461 — пара к английским фразам выше.
    REFERENCE_REJECTED_SERVER_BUSY:
      'Сервер сейчас занят разбором других файлов — «{fileName}» не удалось взять в работу. Повторите отправку через минуту.',
    REFERENCE_REJECTED_FILE_TOO_LARGE:
      'Файл «{fileName}» больше {maxMb} МБ — приложите файл поменьше или разбейте PDF на части.',
    REFERENCE_REJECTED_IMAGE_PROCESSING_UNAVAILABLE:
      'Обработка изображений на сервере недоступна — сообщите в поддержку.',
    REFERENCE_REJECTED_IMAGE_UNREADABLE:
      'Не удалось прочитать «{fileName}» как изображение — файл повреждён?',
    REFERENCE_REJECTED_PDF_UNREADABLE:
      'Не удалось прочитать «{fileName}» как PDF — файл повреждён?',
    REFERENCE_REJECTED_TIMEOUT:
      'Не удалось обработать «{fileName}» за отведённое время (страница {page}) — файл слишком тяжёлый. Пришлите нужные страницы отдельным файлом или экраны картинками.',
    REFERENCE_REJECTED_NO_PAGES: 'В «{fileName}» не нашлось ни одной страницы.',
    REFERENCE_REJECTED_TOO_MANY_PAGES:
      'В «{fileName}» {pages} страниц — потолок {maxPages}. Приложите нужные страницы отдельным файлом.',
    REFERENCE_REJECTED_PAGE_RENDER_FAILED:
      'Не удалось отрисовать страницу {page} файла «{fileName}» — пересохраните PDF или пришлите экраны картинками.',
    REFERENCE_REJECTED_NO_SCREEN_FRAMES:
      'На доске в «{fileName}» не нашлось рамок экранов — пришлите скриншоты экранов картинками.',
    REFERENCE_REJECTED_UNSUPPORTED_FORMAT:
      'Формат «{mimeType}» не поддерживается — приложите PNG, JPG, WebP или PDF.',
    REFERENCE_REJECTED_SLICE_BUDGET_EXHAUSTED:
      'Вырезок уже {count} при потолке {maxSlices} — файл «{fileName}» не поместится, уберите его или пришлите меньшую доску.',
    REFERENCE_REJECTED_TOO_MANY_SLICES:
      'С приложенных файлов получилось {count} вырезок при потолке {maxSlices} на ход — уберите один из файлов.',
    REFERENCE_REJECTED_TOO_MANY_PIXELS:
      'Картинка «{fileName}» слишком большая: больше {maxMegapixels} мегапикселей. Уменьшите её или разрежьте на части и приложите снова.',
    REFERENCE_REJECTED_IMAGE_TOO_LONG:
      'Картинка «{fileName}» слишком длинная: частей высотой в экран получилось бы {count} при потолке {maxSlices} на ход. Обрежьте её или разделите на несколько ходов.',
    REFERENCE_SLICING_FAILED:
      'Не удалось разобрать приложенный образец — попробуйте ещё раз или пришлите другой файл.',
    TURN_IN_FLIGHT:
      'По этому чату уже выполняется ход — дождитесь его завершения.',
    // WEB-3744: ход в другом разговоре той же части воронки.
    FUNNEL_TURN_IN_FLIGHT:
      'В другом разговоре этой воронки уже выполняется ход — дождитесь его завершения.',
    // WEB-3744, юнит «предложения»: «Принять всё», «Отменить» и откат
    // хода-предложения.
    PROPOSAL_STALE:
      'Черновик изменился после этого предложения — попросите ИИ ещё раз.',
    PROPOSAL_NOT_PENDING: 'Это предложение уже принято или отменено.',
    PROPOSAL_NOT_APPLIED: 'Это предложение не применялось — отменять нечего.',
    // Возврат 1 (ответ 52): ход-предложение без номера команды кабинета.
    PROPOSAL_REQUEST_ID_REQUIRED:
      'Предложение не отправлено: у запроса нет номера команды. Обновите страницу и попробуйте ещё раз.',
    // WEB-3744, юнит «поэкранно»: «принять» с выбором экранов.
    PROPOSAL_SCREEN_UNKNOWN: 'В этом предложении нет такого экрана.',
    PROPOSAL_NOTHING_SELECTED:
      'Не выбрано ни одного экрана — принимать нечего.',
    PROPOSAL_LANGUAGE_WHOLE_ONLY:
      'Это предложение меняет язык воронки — его можно принять только целиком.',
    TURN_FAILED_INTERNAL: 'Внутренняя ошибка сервера при выполнении хода',
    TURN_QUEUE_UNAVAILABLE:
      'Очередь ходов ИИ недоступна — попробуйте ещё раз через минуту.',
    TURN_STREAM_INTERRUPTED:
      'Связь с ходом прервалась — сам ход продолжается. Обновите страницу, чтобы увидеть результат.',
    STREAM_TICKET_UNAVAILABLE:
      'Живая связь прервалась, генерация продолжается на сервере — результат появится здесь сам.',
    TURN_PROCESS_DIED:
      'Ход не удалось выполнить — сервер перезапустился. Черновик не изменился, плата за сам ход не взята.',
    TURN_PROCESS_DIED_IMAGE_REFUNDED:
      'Ход не удалось выполнить — сервер перезапустился. Плата за картинку этого хода возвращена на счёт.',
    TURN_PROCESS_DIED_IMAGE_REFUND_PENDING:
      'Ход не удалось выполнить — сервер перезапустился. Плату за картинку этого хода вернуть автоматически не вышло, мы уже разбираемся.',
    REVERT_NO_CONTENT_SNAPSHOT:
      'Не удалось отменить этот ход: в сохранённых состояниях треда нет снимка содержимого.',
    REVERT_NO_LOGIC_SNAPSHOT:
      'Не удалось отменить этот ход: в сохранённых состояниях треда нет снимка логики.',
    PREVIEW_UNPARSEABLE:
      'Не удалось разобрать страницу приложения по этой ссылке.',
    REPORT_TOO_SLOW:
      'Отчёт за такой период считается слишком долго — сузьте период и попробуйте снова.',
    // WEB-2636: см. английскую половину — без слова «период».
    READ_REQUEST_TOO_SLOW:
      'Сервер сейчас не успел ответить — попробуйте ещё раз.',
    DATABASE_QUERY_TOO_SLOW:
      'База данных не успела выполнить действие — попробуйте ещё раз через минуту.',
    // WEB-2374 п.13 / WEB-2380 Д1: см. английскую половину — брошенная
    // перерисовка появляется в медиа-хабе подарком.
    IMAGE_GENERATION_TIMEOUT:
      'Не успели сгенерировать картинку — время ожидания истекло. Попробуйте ещё раз: если картинка всё-таки дорисуется, она появится в медиа-хабе.',
    IMAGE_GENERATION_FAILED:
      'Не удалось сгенерировать картинку — сервис картинок не вернул результат. Кредиты за картинку не списаны, попробуйте ещё раз.',
    IMAGE_GENERATION_UNAVAILABLE:
      'Не удалось начать перерисовку — сервис временно недоступен. Попробуйте ещё раз.',
    FILE_NOT_PROVIDED: 'Файл не передан',
    FILE_EMPTY: 'Файл пустой',
    FILE_TOO_LARGE: 'Файл слишком большой',
    IMAGE_FORMAT_UNSUPPORTED:
      'Неподдерживаемый формат изображения. Допустимы JPEG, PNG, WebP, GIF, AVIF',
    FONT_FORMAT_UNSUPPORTED:
      'Это не файл шрифта. Допустимы WOFF2, WOFF, TTF, OTF',
    IMAGE_PROCESSING_BUSY:
      'Сервер сейчас обрабатывает другие большие картинки. Повторите через минуту',
    IMAGE_TOO_MANY_PIXELS:
      'Картинка слишком большая: больше 268 мегапикселей. Уменьшите её и загрузите снова',
    IMAGE_HIGH_BIT_DEPTH_TOO_LARGE:
      'Картинка с глубиной цвета больше 8 бит слишком большая: больше 100 мегапикселей. Сохраните её в 8 бит на канал (обычный PNG или JPEG) или уменьшите и загрузите снова',
    IMAGE_ALPHA_TOO_LARGE:
      'Картинка с прозрачностью слишком большая: больше 100 мегапикселей. Уменьшите её или сохраните без прозрачности (например, в JPEG) и загрузите снова',
    SKELETON_DESCRIPTIONS_DISABLED:
      'Описания контента эталонов выключены на сервере.',
    CAPI_ACCESS_TOKEN_REQUIRED:
      'Для первого подключения сети нужен токен доступа: отправлять события без него нечем.',
    CAPI_TEST_EVENT_NO_CONNECTION:
      'Сеть не подключена — проверочное событие отправлять некуда.',
    CAPI_PIXEL_ID_INVALID_META:
      'Похоже, это не ID пикселя Meta: в Events Manager → «Источники данных» скопируйте ID набора данных — он состоит только из цифр.',
    CAPI_PIXEL_ID_INVALID_TIKTOK:
      'Похоже, это не ID пикселя TikTok: в Events Manager скопируйте ID пикселя — латинские буквы и цифры, без пробелов и знаков.',
    CAPI_EVENT_MAPPING_UNKNOWN_EVENT:
      'В сопоставлении есть событие, которого сервис не знает. Выберите наше событие из списка.',
    CAPI_EVENT_NAME_PURCHASE_FORBIDDEN:
      'Имя покупки (Purchase, CompletePayment) можно дать только событию с оплатой. Иначе сеть посчитает покупку, которой не было, а настройка «Только первую оплату человека» погасит настоящую.',
    CAPI_EVENT_NAME_SHARED_WITH_PURCHASE:
      'Это имя уже стоит у события с оплатой — событие без оплаты под тем же именем сеть посчитает покупкой, и учёт покупок в рекламном кабинете станет ложным. Дайте событиям разные имена.',
    CAPI_EVENT_NAME_CUSTOM_INVALID:
      'Своё имя события — до 50 знаков: латинские буквы, цифры, «_» и «-» (у Meta можно и пробел). У TikTok имя начинается с буквы.',
    SEQUENCE_TRIGGER_UNSUPPORTED:
      'Этот повод пока не поддерживается сервером — цепочка никогда не запустится. Выберите другой повод.',
    SEQUENCE_HAS_NO_STEPS:
      'В цепочке нет ни одного шага — добавьте шаг, прежде чем отправлять тестовое письмо.',
    SEQUENCE_TEST_SEND_SUPPRESSED:
      'Этот адрес отписался от рассылки — тестовое письмо на него не уйдёт, как не ушло бы и настоящее. Укажите другой адрес.',
    UNSUBSCRIBE_FAILED:
      'Не удалось обработать отписку — попробуйте позже или напишите в поддержку.',
    SEQUENCE_LOG_NOT_FOUND:
      'Запись в журнале писем не найдена — возможно, её уже убрали по сроку хранения.',
    SEQUENCE_RESEND_SUPPRESSED:
      'Этот адрес отписался от рассылки — отправить письмо повторно нельзя.',
    SEQUENCE_NOT_FOUND:
      'Цепочка не найдена — возможно, её удалили. Обновите страницу.',
    SEQUENCE_STEP_NOT_FOUND:
      'Шаг не найден — возможно, его удалили. Обновите страницу.',
    SEQUENCE_WRONG_PROJECT: 'Эта цепочка не принадлежит вашему проекту',
    ANALYTICS_EVENT_FILTER_REQUIRED:
      'Подписка на события аналитики требует непустого списка типов — иначе она не будет работать никогда.',
    FUNNEL_SLUG_RESERVED:
      'Этот адрес занят страницей сервиса — воронка с таким адресом не открылась бы. Переименуйте воронку.',
    DUPLICATE_NAME: 'Сущность с таким именем уже существует в этом проекте.',
    QUIZ_WRAPPER_FUNNEL_FAILED:
      'Не удалось завести служебную воронку для этого квиза. Попробуйте переименовать квиз и сохранить ещё раз.',
    SMART_LINK_QUIZ_ONLY_SHAPE:
      'У направления «Квиз» должен быть выбран ровно один квиз — без пейволла и без воронки.',
    SMART_LINK_QUIZ_NOT_IN_PROJECT:
      'Этот квиз не принадлежит проекту — выберите квиз из списка проекта.',
    SMART_LINK_TOO_MANY_DIRECTIONS:
      'В A/B-тесте может быть не больше 8 направлений. Уберите лишние и сохраните ещё раз.',
    // WEB-3742: «Отдать весь трафик победителю»
    SMART_LINK_FINISH_DRAFT:
      'Черновик нельзя завершить: тест ещё не делил трафик. Опубликуйте его или удалите.',
    SMART_LINK_WINNER_NOT_IN_TEST:
      'Это направление не из этого A/B-теста. Обновите страницу и выберите победителя ещё раз.',
    SMART_LINK_ALREADY_FINISHED:
      'Этот A/B-тест уже завершён с другим победителем.',
    // WEB-3742: запреты завершённому тесту
    SMART_LINK_FINISHED_DIRECTIONS_LOCKED:
      'Тест завершён: направления и веса больше не меняются. Чтобы проверить другое деление, запустите тест заново („Повторить“).',
    SMART_LINK_FINISHED_MOVE_FORBIDDEN:
      'Завершённый тест нельзя перенести в другой проект: весь трафик уже идёт на победителя.',
    ORG_ROLE_REQUIRED: 'У вас нет прав создавать проекты в этой организации',
    ORG_LIMIT_REACHED:
      'Достигнут предел числа организаций на одного человека. Чтобы создать новую, закройте ненужную — в её настройках, раздел «Опасная зона».',
    ORG_LIMIT_REACHED_ON_TRANSFER:
      'Передать нельзя: у получателя уже предельное число организаций. Передача пройдёт, когда он закроет ненужную.',
    AI_PROVIDER_UNAVAILABLE:
      'Сервис ИИ временно недоступен — попробуйте ещё раз через несколько минут.',
    AI_REQUEST_REJECTED: 'Сервис ИИ отклонил запрос.',
    AI_REQUEST_REFUSED:
      'Модель отказалась выполнять запрос по правилам поставщика. Повтор не поможет — переформулируйте тему или бриф.',
    AI_REQUEST_TOO_LARGE:
      'Запрос слишком большой для ИИ-генерации. Пришлите текст покороче.',
    AI_GENERATION_BASE_NOT_FOUND:
      'Основа для генерации не найдена в этом проекте. Возможно, её удалили — выберите другую.',
    AI_GENERATION_BASE_KIND_MISMATCH:
      'Эта основа не подходит для выбранного вида генерации: квиз строится от квиза или воронки, пейволл — от пейволла или воронки с пейволлом.',
    // WEB-4229: у проекта со своими платежами студия создаёт только квиз.
    AI_GENERATION_OWN_PAYMENTS_QUIZ_ONLY:
      'Проект принимает оплату сам, у себя, поэтому ИИ-студия создаёт только квиз. Выберите «Квиз» и запустите генерацию снова.',
    AI_GENERATION_BASE_FUNNEL_NO_QUIZ:
      'В этой воронке нет квиза — строить не от чего. Добавьте квиз в воронку или выберите другую основу.',
    AI_GENERATION_BASE_FUNNEL_NO_PAYWALL:
      'В этой воронке нет пейволла — строить не от чего. Добавьте пейволл в воронку или выберите другую основу.',
    AI_GENERATION_BASE_UNSUPPORTED:
      'Каркас по ссылке ещё не готов или разобрать его не удалось — выберите готовый каркас.',
    AI_GENERATION_BASE_AMBIGUOUS:
      'В запросе указаны две разные основы. Выберите одну.',
    AI_GENERATION_BASE_OWN_NOT_SUPPORTED:
      'Генерация от своего квиза, пейволла или воронки пока недоступна — выберите шаблон.',
    AI_GENERATION_BASE_TOO_LARGE:
      'Основа слишком большая: до 40 экранов квиза, до 15 экранов пейволла и до 60 000 знаков текста. Выберите основу поменьше.',
    AI_GENERATION_BASE_CHANGED:
      'Основа изменилась, пока генерация ждала очереди — запустите заново.',
    // WEB-2401 — пара к английским фразам выше.
    EDIT_REFERENCE_NOT_FOUND:
      'Образец не найден в этом проекте. Возможно, его удалили — выберите другой образец.',
    EDIT_REFERENCE_BLUEPRINT_NOT_READY:
      'Каркас по ссылке ещё не готов или разобрать его не удалось — выберите образцом готовый каркас.',
    EDIT_REFERENCE_NO_QUIZ:
      'В этом образце нет квиза — взять его образцом нельзя. Выберите другой образец.',
    EDIT_REFERENCE_TOO_LONG:
      'Образец слишком большой для чата: больше {max} знаков. Выберите образец поменьше.',
    EDIT_REFERENCE_INVALID: 'Образец указан неверно: нужны его вид и номер.',
    // WEB-2397: пачка вариантов генерации.
    AI_GENERATION_BATCH_EMPTY:
      'В пачке нет ни одного варианта. Добавьте хотя бы один.',
    AI_GENERATION_BATCH_TOO_MANY_VARIANTS:
      'Столько вариантов за один запуск нельзя. Уменьшите число вариантов в пачке.',
    AI_GENERATION_BATCH_THREADS_OUT_OF_RANGE:
      'Столько одновременных потоков выбрать нельзя: доступно от одного до потолка вашей организации.',
    AI_GENERATION_BATCH_REQUEST_ID_TAKEN:
      'Этот запуск уже записан за другим участником проекта. Нажмите «Сгенерировать» ещё раз.',
    AI_GENERATION_BATCH_LAUNCH_FAILED:
      'Запуск этой пачки вариантов не удался. Нажмите «Сгенерировать» ещё раз.',
    AI_GENERATION_BATCH_NOT_FOUND: 'Пачка генераций не найдена в этом проекте.',
    AI_GENERATION_BATCH_VARIANT_WITHDRAWN:
      'Вариант снят: пачку остановили до его начала. Кредиты за него не списаны.',
    AI_SPEND_LIMIT_DAILY_EXCEEDED:
      'Запуск выведет траты организации на ИИ за суточный лимит. Кредиты не списаны. Дождитесь следующих суток или попросите владельца организации поднять лимит.',
    AI_SPEND_LIMIT_MONTHLY_EXCEEDED:
      'Запуск выведет траты организации на ИИ за месячный лимит. Кредиты не списаны. Дождитесь следующего месяца или попросите владельца организации поднять лимит.',
    AI_LARGE_LAUNCH_CONFIRMATION_REQUIRED:
      'Это крупный запуск — подтвердите сумму перед запуском. Кредиты не списаны.',
    AI_SPEND_LIMIT_BILLING_ACCESS_REQUIRED:
      'Менять лимит трат на ИИ может только тот, у кого есть доступ к деньгам организации. Доступ выдаёт владелец организации.',
    AI_AUTHOR_QUESTION_NOT_FOUND: 'Вопрос генерации не найден.',
    AI_AUTHOR_QUESTION_CLOSED:
      'На этот вопрос уже нельзя ответить: время вышло или ответ уже дан. Генерация продолжается с выбранным вариантом.',
    AI_AUTHOR_QUESTION_GENERATION_STOPPED:
      'На этот вопрос уже нельзя ответить: генерацию остановили.',
    AI_AUTHOR_QUESTION_UNKNOWN_OPTION:
      'У этого вопроса нет такого варианта ответа. Выберите один из предложенных.',
    AI_MODEL_UNAVAILABLE:
      'Эта модель ИИ сейчас недоступна. Выберите другую модель.',
    AI_MODEL_NOT_ALLOWED_FOR_ACTION:
      'Эта модель ИИ не подходит для этого действия. Выберите другую модель.',
    TRANSLATION_CONTENT_CHANGED:
      'Перевод изменился, пока окно правки было открыто: система догнала недостающие строки. Обновите окно, чтобы не потерять свежий перевод, — ваши правки при этом сохранятся.',
    LINK_PARSE_LINK_INVALID:
      'Это не ссылка на страницу в интернете. Вставьте адрес, который начинается с http:// или https://. Кредиты не списаны.',
    LINK_PARSE_LINK_UNAVAILABLE:
      'Не удалось открыть страницу по ссылке: она не отвечает, закрыта или ведёт не на воронку. Проверьте ссылку в браузере. Кредиты не списаны.',
    LINK_PARSE_BOT_PROTECTION:
      'Страница по ссылке защищена от роботов (например, капчей) — наш разборщик не может её пройти. Попробуйте другую ссылку на эту воронку. Кредиты не списаны.',
    LINK_PARSE_LOGIN_REQUIRED:
      'Воронка по ссылке просит войти в аккаунт — наш разборщик не может пройти вход. Дайте ссылку на часть воронки, которая открывается без входа. Кредиты не списаны.',
    LINK_PARSE_EMAIL_CODE_TIMEOUT:
      'Воронка по ссылке попросила код из письма, а письмо с кодом не пришло вовремя. Попробуйте ещё раз позже или дайте другую ссылку на эту воронку. Кредиты не списаны.',
    LINK_PARSE_UNKNOWN_PLATFORM:
      'Воронки этой платформы мы пока не умеем разбирать. Кредиты не списаны.',
    LINK_PARSE_PARSE_FAILED:
      'Не удалось разобрать воронку на этой странице. Кредиты не списаны.',
    LINK_PARSE_PAGE_TOO_LARGE:
      'Страница по ссылке слишком большая для разбора. Кредиты не списаны.',
    LINK_PARSE_UNAVAILABLE:
      'Разбор по ссылке временно недоступен. Попробуйте через несколько минут. Кредиты не списаны.',
    LINK_PARSE_PAID_ACCESS_REQUIRED:
      'Разбор по ссылке доступен на платном тарифе и в пробном периоде.',
    LINK_PARSE_TOO_FREQUENT:
      'Эту площадку сейчас разбирают слишком часто. Попробуйте через минуту.',
    LINK_PARSE_NOT_FOUND: 'Разбор не найден в этом проекте.',
    PARSED_FUNNEL_NOT_READY:
      'Разбор ещё не закончен или не удался — воронку из него не создать. Дождитесь окончания разбора или разберите ссылку заново.',
    PARSED_FUNNEL_OLD_PATH:
      'Этот разбор сделан прежним способом: готовой воронки в нём нет. Разберите ссылку заново, чтобы получить готовую воронку.',
    PARSED_FUNNEL_FORM_UNSUPPORTED:
      'Готовая воронка в этом разборе сохранена в устаревшем виде. Разберите ссылку заново.',
    AI_ASK_IN_FLIGHT:
      'Ответ на этот вопрос уже готовится — подождите несколько секунд.',
    AI_ASK_TIMEOUT:
      'ИИ не успел ответить. Кредиты не списаны — спросите ещё раз или короче.',
    AI_ASK_NO_ANSWER:
      'ИИ не смог ответить по данным. Кредиты не списаны — переформулируйте вопрос.',
    AI_ASK_REQUEST_CLOSED:
      'Этот запрос уже завершён. Задайте вопрос ещё раз — он уйдёт новым запросом.',
    // WEB-3820 юнит 2: план «Спросить ИИ» (без сумм).
    AI_ASK_NO_EDIT_RIGHT:
      'Нет права менять эту воронку. Участник проекта меняет свои воронки, владелец и администратор — все. Вопросы о данных доступны всем.',
    AI_ASK_PLAN_DAILY_CAP:
      'На сегодня исчерпан лимит бесплатных планов ИИ для организации ({limit} в сутки). Попробуйте завтра — сутки считаются по UTC.',
    AI_ASK_PLAN_UNAVAILABLE:
      'План ИИ временно недоступен. Попробуйте через минуту.',
    AI_ASK_PLAN_FAILED: 'ИИ не смог составить план. Переформулируйте задачу.',
    AI_ASK_TARGET_NOT_FOUND: 'Воронка не найдена в этом проекте.',
    FUNNEL_IMAGES_NOTHING_TO_DO:
      'Рисовать нечего — ни на одном экране нет места для картинки с описанием.',
    FUNNEL_IMAGES_RUN_NOT_FOUND: 'Этот ход картинок не найден в воронке.',
    FUNNEL_IMAGES_SLOT_FAILED:
      'Картинку не удалось нарисовать — за неё ничего не списано. Нарисуйте её заново на экране.',
    FUNNEL_IMAGES_MODEL_GONE:
      'Модель картинок, на которой запущен ход, больше недоступна. Это место не нарисовано, деньги за него не списаны — запустите картинки заново.',
    FUNNEL_IMAGES_RUN_STARTING:
      'Картинки для этой воронки уже запускаются. Подождите несколько секунд и обновите экран.',
    TRANSLATION_NOTHING_TO_TRANSLATE:
      'Переводить нечего — пока нет экранов с текстом. Добавьте экраны с текстом и закажите перевод снова.',
    TRANSLATION_BATCH_INSUFFICIENT_BALANCE:
      'Кредитов не хватает на перевод всех выбранных языков. Пополните кошелёк или выберите меньше языков — ничего не запущено и не отложено.',
    TRANSLATION_BATCH_TOO_MANY_LANGUAGES:
      'Выбрано больше языков, чем есть в списке. Уберите лишние и отправьте снова.',
    TRANSLATION_BATCH_INVALID_LANGUAGES:
      'Список языков не принят: языки повторяются, среди них язык оригинала или язык, которого нет в списке. Исправьте выбор и отправьте снова.',
    TRANSLATION_BATCH_FAILED:
      'Перевод на этот язык не удался. Кредиты за него не списаны — попробуйте ещё раз.',
    TRANSLATION_BATCH_TIMED_OUT:
      'Перевод на этот язык слишком долго ждал очереди и отменён. Кредиты за него не списаны — попробуйте ещё раз.',
    TRANSLATION_BATCH_LANGUAGE_DELETED:
      'Перевод на этот язык отменён: язык удалили, пока шёл перевод. Кредиты за него не списаны.',
    TRANSLATION_LANGUAGE_DELETED:
      'Перевод отменён: язык удалили, пока шёл перевод. Кредиты за него не списаны.',
    REGISTRAR_UNAVAILABLE:
      'Регистратор доменов сейчас недоступен. Деньги не списаны, попробуйте позже.',
    DOMAIN_AVAILABILITY_UNAVAILABLE:
      'Проверка доменов временно недоступна, попробуйте позже.',
    DOMAIN_PURCHASE_FAILED: 'Не удалось купить домен, деньги не списаны.',
    DOMAIN_RENEWAL_FAILED: 'Не удалось продлить домен, деньги не списаны.',
    DOMAIN_RENEW_DUPLICATE:
      'Продление этого домена уже выполняется по такому же запросу — повторное списание не создано.',
    DOMAIN_RENEW_RETRY_LIMIT:
      'Слишком много попыток продления этого домена подряд — повторите позже или обратитесь в поддержку.',
    DOMAIN_RENEW_FORCE_TOO_SOON:
      'Предыдущая попытка продления сделана только что и может быть ещё в работе. Повторное списание сейчас может оплатить второй год, который не вернуть, — подождите около 15 минут и повторите.',
    DOMAIN_RENEWED_NOT_PERSISTED:
      'Домен продлён у регистратора, требуется ручная сверка.',
    DOMAIN_RENEW_OUTCOME_UNKNOWN:
      'Регистратор не ответил вовремя, и пока неизвестно, продлён ли домен. Мы проверим это сами — повторно платить не нужно: если продление не прошло, деньги вернутся автоматически.',
    // WEB-4070: массовое продление — остатка организации меньше суммы пачки.
    DOMAIN_RENEW_INSUFFICIENT_BALANCE:
      'Кредитов на кошельке не хватает на продление выбранных доменов — ничего не списано. Пополните кошелёк и повторите.',
    stripe_connect_mode_key_mismatch:
      'Ключи не из той среды Stripe, что выбрана в строке подключения. Возьмите ключи из нужной среды либо подключайте их в другой строке.',
    STRIPE_LIVE_KEYS_NOT_CONNECTED:
      'Нельзя включить боевой режим: боевые ключи Stripe не подключены к проекту.',
    STRIPE_TEST_KEYS_NOT_CONNECTED:
      'Нельзя включить тестовый режим: тестовые ключи Stripe не подключены к проекту.',
    STRIPE_LIVE_MODE_WITHOUT_KEYS:
      'Проект переключён в боевой режим Stripe, а боевые ключи не подключены.',
    STRIPE_TEST_MODE_WITHOUT_KEYS:
      'Проект переключён в тестовый режим Stripe, а тестовые ключи не подключены.',
    STRIPE_SUBSCRIPTION_MODE_KEYS_MISSING:
      'У подписки своя среда Stripe, а её ключи к проекту не подключены — отменить или изменить подписку нечем.',
    STRIPE_SUBSCRIPTION_MODE_UNKNOWN:
      'У этой подписки не записана среда Stripe (боевая или тестовая) — отменить или изменить её нечем, пока это не разберут вручную.',
    STRIPE_ACCOUNT_SHARED_WARNING:
      'Этот аккаунт Stripe уже подключён к другому проекту. Подписки, письма и выручка покупателей могут перепутаться между проектами.',
    STRIPE_ACCOUNT_NOT_READ_LABEL_DROPPED:
      'Аккаунт Stripe по новым ключам не прочитан (ключ ограничен или Stripe не ответил), поэтому прежний аккаунт за проектом мы не оставили: иначе каталог цен скрыл бы цены нового аккаунта. Каталог будет пересинхронизирован.',
    STRIPE_KEY_SET_NOT_CONNECTED:
      'Этот набор ключей Stripe к проекту не подключён — отвязывать нечего.',
    STRIPE_KEY_SET_WEBHOOK_REMOVE_FAILED:
      'Не удалось снять вебхук отвязываемой среды в Stripe — ключи оставлены на месте, попробуйте ещё раз.',
    PAYMENTS_UNAVAILABLE:
      'Оплата сейчас недоступна. Попробуйте позже или свяжитесь с продавцом.',
    VISITOR_ID_REQUIRED:
      'Не удалось начать оплату. Платёж не списан — обратитесь в поддержку приложения.',
    VISITOR_ID_FOREIGN_PROJECT:
      'Не удалось начать оплату. Деньги не списаны — обратитесь в поддержку приложения.',
    consent_denied:
      'Посетитель отказался от отслеживания — свойство не сохранено.',
    reserved_property:
      'Это свойство зарезервировано платформой и задаётся только ею.',
    no_payment_to_refund:
      'По указанному платежу не найдено успешное списание для возврата.',
    not_found: 'Ресурс не найден.',
    QA_EVENT_STATUS_REQUIRED:
      'Поле status обязательно для события customer.subscription.updated',
    VALIDATION_ADJUSTMENT_AMOUNT_INT:
      'Сумма корректировки должна быть целым числом центов',
    VALIDATION_ADJUSTMENT_AMOUNT_NOT_ZERO:
      'Сумма корректировки не может быть нулевой',
    VALIDATION_ADJUSTMENT_AMOUNT_MIN:
      'Списание за одну операцию превышает допустимый потолок',
    VALIDATION_ADJUSTMENT_AMOUNT_MAX:
      'Зачисление за одну операцию превышает допустимый потолок',
    VALIDATION_UNFREEZE_REASON_LENGTH:
      'Причина должна быть от 10 до 500 символов — её читает владелец организации',
    VALIDATION_UNFREEZE_WRITE_OFF_DECISION:
      'Отметка о списании — write_off_done (списание сделано) или write_off_not_required (списание не требуется)',
    VALIDATION_FORCE_RENEW_CONFIRM_REQUIRED:
      'Осознанный повтор продления требует явного подтверждения: confirmNewCharge должно быть true',
    VALIDATION_BUNDLE_ID_FORMAT:
      'bundleId: буквы/цифры/точка/дефис/подчёркивание, например com.my-app.ios',
    VALIDATION_RETURN_TARGET_KIND:
      'Цель возврата: допустимы только «приложение или магазин», «произвольный адрес» и «оставить на последнем экране»',
    VALIDATION_RETURN_TARGET_URL_REQUIRED:
      'Выбран произвольный адрес возврата — впишите сам адрес или выберите другую цель',
    VALIDATION_RETURN_TARGET_URL_SCHEME:
      'Адрес возврата должен начинаться с http:// или https:// — другие схемы (javascript:, data:, file:) и относительные пути запрещены',
    VALIDATION_RETURN_TARGET_URL_MACRO:
      'Подстановка меток вида {utm_source} в адресе возврата не поддерживается — впишите обычный адрес',
    VALIDATION_RETURN_TARGET_URL_LENGTH: 'Адрес возврата длиннее 1000 символов',
    VALIDATION_STORE_URL_SCHEME:
      'Ссылка на магазин должна начинаться с http:// или https:// — другие схемы (javascript:, data:, file:) запрещены',
    VALIDATION_MMP_BASE_URL_SCHEME:
      'Ссылка трекера должна начинаться с http:// или https:// — другие схемы (javascript:, data:, file:) запрещены',
    VALIDATION_IOS_APP_ID_FORMAT:
      'iOS App ID: формат TeamID.BundleID (например ABCDE12345.com.example.app), без пробелов и "://"',
    VALIDATION_ANDROID_PACKAGE_FORMAT:
      'Android package: reverse-DNS (например com.example.app)',
    VALIDATION_SHA256_FINGERPRINT_FORMAT:
      'SHA-256: 32 hex-байта через двоеточие (AA:BB:…) или 64 hex-символа',
    VALIDATION_EVENT_SESSION_ID_LENGTH: 'sessionId: от 8 до 64 символов',
    VALIDATION_EVENT_SCREEN_ID_LENGTH: 'screenId: не длиннее 128 символов',
    VALIDATION_MMP_BRANCH_UNSUPPORTED:
      'Branch пока не поддерживается — выберите AppsFlyer или Adjust',
    VALIDATION_MMP_PAIR_INCOMPLETE:
      'Заполните вторую половину пары: у ссылки MMP не выбран трекер — install-ссылка не соберётся',
    VALIDATION_MMP_BASE_URL_HOST_MISMATCH:
      'Ссылка не соответствует выбранному трекеру',
    VALIDATION_PUBLIC_GUID_LENGTH:
      'Идентификатор посетителя (guid) должен быть длиной от 8 до 64 символов. Приложение передало значение другой длины — тот же идентификатор нужно использовать и при оплате, и при связке покупателя с профилем платформы.',
    REDIRECT_ENDING_NO_STORE_URL:
      'Концовка «редирект» ведёт в магазин приложений, а в настройках проекта не заполнена ни одна ссылка — ни App Store, ни Google Play. Заполните хотя бы одну в настройках проекта и запустите генерацию снова.',
    // WEB-1992 — отказы ручки приёма замеров скорости `POST /public/vitals`.
    VALIDATION_VITALS_METRIC_UNKNOWN:
      'Неизвестный показатель скорости: принимаются LCP, INP, CLS, TTFB и FCP',
    VALIDATION_VITALS_BATCH_TOO_LARGE:
      'В одном пакете замеров слишком много показателей',
    // WEB-2114 — ссылки предпросмотра (кабинет).
    PREVIEW_LINKS_DISABLED:
      'Ссылки предпросмотра пока выключены. Попробуйте позже или напишите в поддержку.',
    PREVIEW_TTL_OUT_OF_RANGE:
      'Срок ссылки — от 1 до 48 часов. Укажите значение в этих пределах.',
    PREVIEW_TARGET_NOT_FOUND:
      'Воронка, квиз или пейволл для предпросмотра не найдены в этом проекте.',
    FUNNEL_BROKEN:
      'Воронка сломана: её квиз или пейволл удалён либо недоступен. Почините воронку в редакторе и попробуйте снова.',
    EMPTY_FUNNEL:
      'В воронке нечего показывать: и квиз, и пейволл пусты. Добавьте хотя бы один экран с контентом.',
    PREVIEW_LINK_NOT_FOUND: 'Ссылка предпросмотра не найдена.',
    PREVIEW_PAYWALL_NOT_REVIEWED:
      'Сначала отправьте на проверку экран оплаты (пейволл) этой воронки — его может отправить владелец или тот, у кого есть право на этот пейволл.',
    PREVIEW_QUIZ_NOT_REVIEWED:
      'Сначала отправьте на проверку квиз этой воронки — его может отправить владелец или тот, у кого есть право на этот квиз.',
    // WEB-2791: кусок заперт понижением тарифа — совет про тариф.
    PREVIEW_PAYWALL_READONLY_NOT_REVIEWED:
      'Экран оплаты (пейволл) этой воронки доступен только для чтения после понижения тарифа и ещё ни разу не был на проверке. Отправить его на проверку можно будет после повышения тарифа.',
    PREVIEW_QUIZ_READONLY_NOT_REVIEWED:
      'Квиз этой воронки доступен только для чтения после понижения тарифа и ещё ни разу не был на проверке. Отправить его на проверку можно будет после повышения тарифа.',
    // WEB-2114 — публичная дверь предпросмотра (получатель ссылки).
    PREVIEW_UNAVAILABLE: 'Ссылка недоступна.',
    PREVIEW_CODE_INVALID: 'Код не подошёл.',
    PREVIEW_ATTEMPTS_EXHAUSTED:
      'Попытки ввода кода закончились. Попросите автора прислать новую ссылку.',
    // WEB-2119 — внешняя аналитика клиента (Amplitude и её будущие соседи).
    EXTERNAL_ANALYTICS_CONNECTION_NOT_FOUND:
      'Подключение внешней аналитики не найдено.',
    EXTERNAL_ANALYTICS_API_KEY_REQUIRED:
      'Вставьте ключ проекта — без него подключить аналитику нельзя.',
    EXTERNAL_ANALYTICS_KEY_OR_REGION_INVALID:
      'Аналитика не приняла ключ. Проверьте два поля сразу: сам ключ проекта и регион (США или Европа) — по ответу различить эти два случая нельзя, ответ одинаковый.',
    EXTERNAL_ANALYTICS_PROVIDER_PAUSED:
      'Доставка в эту аналитику сейчас приостановлена нами у всех проектов. Проверочное событие не отправлено: чинить на вашей стороне нечего. Живые события копятся до суток и уйдут, когда доставку возобновят.',
    EXTERNAL_ANALYTICS_EVENT_RENAME_FORBIDDEN:
      'Имена событий закрепляются при подключении и больше не меняются: переименование разорвёт вашу историю и сохранённые графики в аналитике. Новые события добавлять можно.',
    OUTBOUND_URL_MACRO_FORBIDDEN:
      'В адресе есть метка с личными данными — почта, телефон, имя, дата рождения, адрес или свободный текст ответа наружу не передаются. Разрешены метки кампании ({utm_source}, {utm_medium}, {utm_campaign}, {utm_content}, {utm_term}), метка рекламного клика ({click_id}), идентификатор посетителя ({visitor_id}) и ответ вопроса ({answer.<идентификатор вопроса>}) — последний только у вопросов с включённой галочкой «передавать ответ в исходящий адрес».',
    BLOCK_MIN_ABOVE_MAX:
      'В блоке минимальное значение больше максимального — посетитель не сможет ответить на такой вопрос. Поменяйте числа местами или исправьте одно из них.',
    FUNNEL_TOO_MANY_SCREENS:
      'В воронке больше 150 экранов — столько сохранить нельзя. Удалите лишние экраны или разделите воронку на две.',
    SMARTLINK_EXTERNAL_URL_OWN_HOST:
      'Внешний адрес направления не может вести на наш собственный домен — получилась бы цепочка переадресаций, за которую рекламные площадки наказывают домен. Укажите адрес своей страницы.',
    HANDOFF_BASE_DOMAIN_NOT_ALLOWED:
      'Такая база возврата в приложение недопустима: подойдёт только основная база зоны передачи или одна из прежних — на других сервер не отдаёт файлы Universal Links / App Links, и ссылка не откроет приложение.',
    VITALS_PAYLOAD_TOO_LARGE: 'Пакет замеров скорости слишком большой',
    REQUEST_BODY_TOO_LARGE: 'Тело запроса больше допустимого для этого адреса',
    ORIGIN_NOT_VERIFIED:
      'Запрос пришёл не через защищённый вход сервиса. Используйте адрес вашего проекта или кабинета',
    ANALYTICS_TIMEZONE_INVALID:
      'Неизвестный часовой пояс отчёта — нужен пояс из списка IANA, например Europe/Moscow',
    ANALYTICS_ANSWER_FILTER_INVALID:
      'Фильтр по ответу задан неверно: нужен вид «вопрос:вариант».',
    ANALYTICS_HOURLY_RANGE_INVALID:
      'По часам можно показать только одни сутки — выберите период не длиннее суток',
    ANALYTICS_TOO_FEW_VISITORS:
      'Слишком мало посетителей в выборке, чтобы показать числа: нужно хотя бы 5. Уберите часть фильтров или возьмите период длиннее.',
    ANALYTICS_UTM_VALUES_TOO_MANY:
      'Слишком много значений UTM в одном фильтре: можно выбрать не больше 50.',
  },
);

/** Все коды реестра — множество для сторожа и для точного поиска. */
export const ERROR_CODE_LIST = Object.keys(
  errorCodeStrings.en,
) as readonly string[];

/**
 * Фраза по машинному коду отказа на заданном языке, ТОЧНЫМ регистром.
 * `undefined` — код не из реестра (или служебное имя прототипа: поиск идёт
 * через hasOwnProperty, иначе `constructor`/`toString` вернули бы функцию).
 */
export function isServerErrorCode(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(errorCodeStrings.en, code);
}

export function errorPhraseByCode(
  code: string,
  locale: UiLocale = getUiLocale(),
): string | undefined {
  if (!isServerErrorCode(code)) return undefined;
  return (pickStrings(errorCodeStrings, locale) as Record<string, string>)[
    code
  ];
}

/**
 * Регулярка по английскому тексту сервера → ключ фразы.
 * ⚠ ПОРЯДОК ЗНАЧИМ: выигрывает первое совпадение.
 */
export const ERROR_PATTERNS: ReadonlyArray<readonly [RegExp, ErrorStringKey]> =
  [
    [/too many requests/i, 'tooManyRequests'],
    [/rate ?limit/i, 'tooManyRequests'],
    // WEB-1444: код 503 EMAIL_DELIVERY_FAILED — письмо не удалось отправить
    // (повтор приглашения, коды подтверждения и т.п.).
    [/email_delivery_failed/i, 'emailDeliveryFailed'],
    // WEB-1352: «латинские» — сервер (validator.js isStrongPassword) считает
    // буквами только латиницу; текст обязан говорить правду.
    [
      /password must be at least|weak password|password is too weak/i,
      'weakPassword',
    ],
    [
      /invalid credentials|invalid email or password|wrong password|unauthorized/i,
      'invalidCredentials',
    ],
    // Ревизия фикс-волны 23.08 (п.1/п.4): правило про домен обязано стоять ДО
    // правила про email ниже — «Domain "x" is already registered» матчится его
    // хвостом `already (registered|in use)` и переводился бы как «Этот email уже
    // зарегистрирован» (грабля класса WEB-916: первое совпадение выигрывает).
    // Ревизия пачки 15 (F2): у той же грабли был второй конец — «Domain already
    // in use» / «is already taken» под доменное правило не попадали и уходили
    // в почтовое. Правило закрывает все формы занятости домена.
    [
      /domain\b.*(already (registered|in use|taken|connected)|is taken)/i,
      'domainAlreadyRegistered',
    ],
    [/invalid domain name/i, 'invalidDomainName'],
    [
      /(email|user).*(already|taken|exists)|already (registered|in use)/i,
      'emailAlreadyRegistered',
    ],
    // WEB-665 — коды 2FA приходят в `error.message` сырой строкой, поэтому
    // стоят ДО общего правила про неверный/истёкший код ниже.
    [/2fa_already_enabled/i, 'twoFactorAlreadyEnabled'],
    [/no_pending_enrollment/i, 'twoFactorEnrollmentNotStarted'],
    // WEB-1864: текстовый близнец кода RESOURCE_READONLY (тело без кода).
    [
      /read-only because your subscription has been downgraded/i,
      'resourceReadonly',
    ],
    [/invalid_2fa_code/i, 'invalidTwoFactorCode'],
    [/code_expired/i, 'codeExpired'],
    [/too_many_attempts/i, 'tooManyAttempts'],
    [/code_or_recovery_code_required/i, 'codeOrRecoveryCodeRequired'],
    [/2fa_not_enabled/i, 'twoFactorNotEnabled'],
    [/send_code_not_applicable/i, 'sendCodeNotApplicable'],
    [/resend_cooldown/i, 'resendCooldown'],
    [/password_required/i, 'passwordRequired'],
    [/invalid_password/i, 'invalidPassword'],
    // WEB-1091 — подтверждение личности у аккаунтов «только Google».
    [/google_reauth_required/i, 'googleReauthRequired'],
    [/google_reauth_mismatch/i, 'googleReauthMismatch'],
    [/invalid_recovery_code/i, 'invalidRecoveryCode'],
    [/email_delivery_failed/i, 'emailDeliveryFailed'],
    [
      /invalid.*(code|token)|(code|token).*(invalid|expired)|expired/i,
      'invalidOrExpiredCode',
    ],
    // Специфичные «not found» ДО общего правила ниже — иначе оно их затеняет
    // («Member not found» содержит «not found»). WEB-916 п.4: «Paywall not
    // found» через общее правило дало бы «Аккаунт не найден» — просто враньё.
    [/member not found/i, 'memberNotFound'],
    [/paywall not found/i, 'paywallNotFound'],
    [/quiz not found/i, 'quizNotFound'],
    [/funnel not found/i, 'funnelNotFound'],
    [/project not found/i, 'projectNotFound'],
    // WEB-1228 (вторая половина п.5) + WEB-1532: сервер НАЗЫВАЕТ виновника (тип
    // блока, id экрана, перебранный потолок списка) — выбрасывать это при
    // переводе значит вернуть человека к «непонятно, что чинить». Тип блока
    // показывается ровно так, как его называет сервер (`cancel_button`): второй
    // словарь «тип → имя» здесь заводить нельзя, он разъехался бы с палитрой
    // редактора. Порядок внутри группы — от подробного к общему.
    [
      /funnel content failed schema validation:\s*block "([^"]+)" on screen "([^"]+)":\s*field "([^"]+)" has (\d+) items, maximum is (\d+)/i,
      'funnelContentListTooLong',
    ],
    [
      /funnel content failed schema validation:\s*block "([^"]+)" on screen "([^"]+)"/i,
      'funnelContentBlockRejected',
    ],
    [
      /funnel content failed schema validation:\s*screen "([^"]+)"/i,
      'funnelContentScreenRejected',
    ],
    [/funnel content failed schema validation/i, 'funnelContentRejected'],
    [/funnel content must be an array of screens/i, 'funnelContentCorrupted'],
    // WEB-1894 п.1: запрет копировать НЕопубликованный пейволл в другой проект
    // (paywalls.service.ts:2374). Сам запрет не ослабляется — переводится текст.
    [/only published paywalls can be copied/i, 'onlyPublishedPaywallCopy'],
    // WEB-1894 п.2: битое правило логики (logic-validation.util.ts:26).
    // Подробная форма (с путём) ДО общей.
    [/invalid logic rules at "([^"]+)"/i, 'logicRulesInvalidAt'],
    // WEB-2408 п.11: текст отказа сервера несёт имена опечаток — фраза их
    // называет (функция бьёт общую формулировку кода, см. localizeError).
    [
      /unknown macro\(s\) in postback url template: (.+)$/i,
      'postbackUnknownMacro',
    ],
    [/invalid logic rules/i, 'logicRulesInvalid'],
    // Ревизия волны 23.08: отказы автопродления и продления домена. Уточнение
    // «or not eligible» стоит ПЕРЕД коротким /domain not found/ — иначе человек
    // из массового продления не понял бы, что домен нашёлся, но продлить нельзя.
    [/domain not found or not eligible/i, 'domainNotFoundOrNotEligible'],
    [/domain not found/i, 'domainNotFound'],
    [/custom domains cannot be renewed/i, 'customDomainCannotRenew'],
    [/renewal price unavailable/i, 'renewalPriceUnavailable'],
    [
      /only an organization owner or admin can remove a domain/i,
      'onlyOwnerOrAdminRemovesDomain',
    ],
    // Ревизия фикс-волны 23.08 (п.3): текстовые близнецы денежных кодов выше.
    [/insufficient balance/i, 'insufficientBalance'],
    [/top-?up is not available on the free plan/i, 'topupRequiresPro'],
    // Ревизия пачки 16 (F2): общее правило ниже ловило «orgId not found» и
    // «projectId not found» как «Аккаунт не найден» — уводило дежурного не туда.
    // Узкие правила стоят ДО общего (первое совпадение выигрывает, класс WEB-916).
    [/(organi[sz]ation|\borg(id)?\b).*not found/i, 'organizationNotFound'],
    [/\bproject(id)?\b.*not found/i, 'projectNotFound'],
    [/not found|no such (user|account)/i, 'accountNotFound'],
    [/forbidden|access denied/i, 'accessDenied'],
    [/network|failed to fetch|timeout|timed out|connection/i, 'networkError'],
    [/internal server error|server error|500/i, 'serverError'],
    [/service unavailable|temporarily unavailable|503/i, 'serviceUnavailable'],

    // WEB-615: сообщения бэкенда, протекавшие в интерфейс по-английски.
    // Порядок важен: «уже участник» проверяется РАНЬШЕ «не участник», иначе
    // подстрока `a member` увела бы оба сообщения в один перевод.
    [
      /you are already a member of this project|^already a member$/i,
      'alreadyProjectMember',
    ],
    [
      /you are not a member of this project|not a member of the target project|^not a member$/i,
      'notProjectMember',
    ],
    [/user is not a member of this organization/i, 'userNotOrgMember'],
    [/no user with that email/i, 'noUserWithEmail'],
    [/owner cannot leave/i, 'ownerCannotLeaveOrg'],
    [/cannot demote the owner/i, 'cannotDemoteOwner'],
    [/cannot remove the owner/i, 'cannotRemoveOwner'],
    [/cannot remove yourself/i, 'cannotRemoveYourself'],
    [/not the current owner/i, 'onlyCurrentOwnerAction'],
    [/this invite has been revoked/i, 'inviteRevoked'],
    [/this invite has reached maximum uses/i, 'inviteMaxUsesReached'],
    [/email mismatch/i, 'inviteEmailMismatch'],
    [/cannot create invite for owner role/i, 'cannotInviteAsOwner'],
    // WEB-1366 остаток 2, п.6: сервер отвечает по-русски (общая константа
    // team-role-messages.const.ts). Прежнее английское сообщение оставлено в
    // словаре намеренно: запрос может уйти из старой вкладки или своего клиента.
    [/admin cannot invite another admin/i, 'adminCannotInviteAdmin'],
    // WEB-1620: верхняя граница списка адресатов (`@ArrayMaxSize`). Формулировка
    // «no more than» — текущая у class-validator, «not more than» — прежняя:
    // покрыты обе, чтобы перевод не отвалился при обновлении пакета.
    [
      /recipients must contain (?:no|not) more than \d+ elements/i,
      'tooManyInviteRecipients',
    ],
    [/insufficient permissions/i, 'insufficientPermissions'],
    [/user not authenticated/i, 'authenticationRequired'],
    [
      /parent domain does not belong to this project|domain does not belong to this project/i,
      'domainBelongsToAnotherProject',
    ],
    [/parent domain is not active/i, 'parentDomainNotActive'],
    [
      /custom domains do not support auto-renewal/i,
      'customDomainNoAutoRenewal',
    ],
    [/failed to toggle auto-renewal/i, 'autoRenewalToggleFailed'],
    [/invalid subdomain slug/i, 'invalidSubdomainSlug'],
    [
      /stripe not configured for this project|stripe not connected/i,
      'stripeNotConnected',
    ],
    [/failed to create stripe checkout session/i, 'stripeCheckoutStartFailed'],
    [/current password is incorrect/i, 'currentPasswordIncorrect'],
    [/google account email is not verified/i, 'googleEmailNotVerified'],

    // org/team (WEB-665)
    [
      /only admin or owner can (create|view|revoke|delete) invites/i,
      'onlyAdminOrOwnerInvites',
    ],
    [/org roles:\s*OWNER,\s*ADMIN/i, 'orgOwnerOrAdminOnly'],
    [/org roles:\s*OWNER(?!,)/i, 'orgOwnerOnly'],
    [/requires one of the following roles/i, 'projectOwnerOrAdminOnly'],
    [/you are not a member of this organization/i, 'notOrgMember'],
    [/no user found with that email/i, 'noUserWithEmail'],
    [
      /only the current organization owner can transfer ownership/i,
      'onlyOrgOwnerTransfers',
    ],
    [
      /target user must already be a member of the organization/i,
      'targetMustBeOrgMember',
    ],
    [/user is already the organization owner/i, 'alreadyOrgOwner'],
    [
      /cannot change owner role|cannot assign owner role/i,
      'ownerRoleImmutable',
    ],

    // Дефект 4 (ревизия «исправлений»): PATCH /bridge-config отвергает ссылку на
    // неопубликованный (или удалённый) пейволл-портал — без этой строки вкладка
    // «Подключение приложения» просто переставала сохраняться без объяснения.
    [
      /portalPaywallId must reference a published paywall/i,
      'portalPaywallNotPublished',
    ],

    // WEB-1294: BridgeTierGuard отдаёт PATCH /bridge-config сырым английским
    // текстом при тарифе FREE.
    [/pro plan is required to configure the app bridge/i, 'bridgeRequiresPro'],

    // Ревизия пачки 10 (WEB-1286): гейт публикации на бесплатном тарифе. Фраза
    // ЖИВАЯ — её и сегодня отдаёт tier-enforcement.service.ts без кода отказа.
    [
      /publishing is not available on the free plan/i,
      'publishRequiresPaidPlan',
    ],
    //
    // Ревизия пачки 16 (R1-7): правило по фразе «…button_to_app_store block but
    // the project has no bridge configuration…» СНЯТО вместе с ключом
    // publishNeedsBridgeConfig. Оно было мёртвым: обе точки отказа (публикация
    // пейволла и публикация воронки) с WEB-1278/WEB-1452 бросают
    // humanError(…, 'APP_BRIDGE_NOT_CONFIGURED', …) — код есть в реестре и
    // зеркалится здесь же кодом APP_BRIDGE_NOT_CONFIGURED, и разбор ловит его
    // ступенью выше. Английской фразы с несуществующим типом блока сервер не
    // произносит больше нигде (проверено обходом backend/src), а пока детектор
    // был мёртв, её вообще никто не видел — «на старые сборки» держать нечего.
  ];
